/**
 * The maps: where every piece of scenery stands, and where each team's base is.
 *
 * Split out of `props.ts` so that file owns *what a prop is* and the collision
 * maths, and this one owns *where they are*. The import runs one way only —
 * maps read the catalogue, the catalogue knows nothing of maps — because a cycle
 * here would make module-initialisation order decide whether a map is built
 * before the definitions it reads, which is a bug that shows up in one bundler
 * and not the other.
 *
 * Shared, and pure, for the same reason the simulation is: the client predicts
 * its own movement, so it has to resolve a collision against the same wall, in
 * the same place, to the same millimetre. No `Date.now()`, no `Math.random()`,
 * nothing Node-only — the browser loads this through the `@shared` alias.
 *
 * **A map never crosses the wire.** It is identified by id at join and echoed
 * back in `joined`; both sides then look the definition up here. That is what
 * keeps two hundred buildings at zero bytes per snapshot, and it is why an arena
 * resolves its map once, at construction, and never changes it mid-match.
 *
 * ## How these were laid out
 *
 * Every map is built from the same three questions, and the answers are what
 * make them play differently rather than merely look different:
 *
 * - **Where can you see?** A sight line is a gun that reaches. Long open lanes
 *   reward the slow accurate builds; broken ground rewards the fast ones.
 * - **How many ways are there round?** A single route is a queue. Three routes
 *   into the same place is a fight; three routes to three different places is
 *   three separate duels, which at 3-a-side is a different game.
 * - **Is the middle worth having?** On one of these it is the whole match
 *   (Kestrel Depot); on another it is a place you die crossing (Harrow Flats).
 *   Both are deliberate, and they produce completely different openings.
 *
 * ## Fairness is by construction, not by eye
 *
 * Where a layout is symmetric it is *built* symmetric — `mirrorZ`, `mirrorX` and
 * `mirrorPoint` generate the second half from the first. A map balanced by hand
 * is a map that is subtly unbalanced, and nobody ever finds the eight metres of
 * extra cover one side got until it has been played for a month.
 */
import type { ArenaHalfExtent, MissionKind, TeamId } from './protocol';
import { placementBlocking, propDefinition, type PlacedProp, type PropBody, type PropId } from './props';
import { clipMasked, MASK_SCALE } from './clip-masks';
import type { PlacedDecal } from './decals';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * One team's fortified start position.
 *
 * A base is scenery like any other — there is no "base" entity, no capture, no
 * ownership at runtime. What this record adds is the part the arena cannot
 * infer from a pile of boxes: which points inside the walls a tank may appear
 * on, and which way it should be looking when it does.
 */
export interface MapBase {
  team: TeamId;
  /** Centre of the compound. The last-resort spawn, and always kept clear. */
  x: number;
  z: number;
  /**
   * Hull yaw a tank takes on spawn — the direction the base's gate opens.
   *
   * Forward is `(sin yaw, cos yaw)`, matching `stepTank`. Spawning a tank facing
   * its own back wall would cost the player a three-point turn every death.
   */
  facing: number;
  /**
   * Spawn points inside the walls, in preference order.
   *
   * **Five slots for a team of three**, deliberately — five thirds, and that
   * ratio is the thing to scale rather than the count. `pickSpawn` skips a slot
   * with a living tank on it, and with exactly as many slots as tanks a respawn
   * during a lull — everybody alive, everybody sitting in the base — would find
   * nothing free and fall through to the compound centre, putting two tanks in
   * the same place. The spares make that arithmetically impossible; a
   * fifteen-a-side base therefore carries twenty-six of them, not fifteen.
   */
  spawns: readonly { x: number; z: number }[];
  /**
   * Radius around the centre that counts as "in the base", in metres.
   *
   * Read by `Arena.pickDropSpot`, which refuses to drop a crate inside one: a
   * crate behind a team's walls pays them for staying home, which is the one
   * thing a base is designed not to reward.
   *
   * A radius rather than the fort's real outline, and therefore a property of
   * the fort rather than a constant — the standard bastion is 62 m wide and
   * 26 m deep, so 35 m covers it with a little to spare, while the citadel a
   * fifteen-tank side spawns in is 122 m wide and needs far more. Absent means
   * `DEFAULT_BASE_KEEP_OUT`.
   */
  keepOut?: number;
}

/**
 * How close a crate may fall to a base whose fort does not say.
 *
 * Sized for the standard bastion. See `MapBase.keepOut`.
 */
export const DEFAULT_BASE_KEEP_OUT = 35;

/** How close a crate may fall to this base. */
export function keepOutFor(base: MapBase): number {
  return base.keepOut ?? DEFAULT_BASE_KEEP_OUT;
}

/**
 * Tanks per side on a map that does not state its own.
 *
 * It lives here rather than in `protocol.ts` — which is where the name every
 * caller uses, `TEAM_SIZE`, is exported from — for one structural reason: this
 * file may not value-import `protocol.ts`. The import runs one way, `protocol`
 * → `maps`, and a second edge back would make module-initialisation order decide
 * whether a map is built before the constants it reads. So the number is
 * defined beside the map property it is the default for, and `protocol.ts`
 * re-exports it under its established name. There is exactly one 3.
 */
export const DEFAULT_TEAM_SIZE = 3;

/** Tanks per side on a given map. Unstated means `DEFAULT_TEAM_SIZE`. */
export function teamSizeFor(map: Pick<MapDefinition, 'teamSize'>): number {
  return map.teamSize ?? DEFAULT_TEAM_SIZE;
}

/**
 * A point a mission is laid out around: a waypoint, a spawn gate, the core's
 * post, the uplink.
 *
 * `yaw` is a *facing* and is meaningful only where something is placed rather
 * than merely driven to — a tank spawning at a gate faces it, a waypoint has
 * nothing to face. Optional rather than defaulted to 0, so that "no opinion"
 * and "pointing at +Z" stay different statements.
 */
export interface MissionPoint {
  x: number;
  z: number;
  yaw?: number;
}

/**
 * Where a mission happens on its map.
 *
 * One shape covering all three kinds rather than three, because every field is
 * inert at its absent value and a mission reads only what it needs — the same
 * argument `WeaponProfile` makes for a new gun being numbers rather than a new
 * branch. `maps.test.ts` asserts each kind carries the fields it actually
 * needs, so an escort map with no route fails rather than ships.
 */
export interface MissionLayout {
  kind: MissionKind;
  /**
   * Escort: the convoy's road, in order. The first entry is the start pad and
   * the last is the terminus that wins the mission.
   *
   * The route is driven by **local avoidance, not pathfinding** — the convoy
   * swerves around what it meets and reverses when it has covered nothing —
   * so consecutive points must be joined by a corridor a tank can hold without
   * solving a corner. The test asserts a 14 m clear channel at every 2 m
   * sample; that figure is above the 10 m working floor on purpose, because a
   * driver at the floor is a driver that will one day stop.
   */
  route?: readonly MissionPoint[];
  /**
   * Where enemies come from. Wave defence rotates through them; escort's
   * ambushes index into them.
   */
  gates?: readonly MissionPoint[];
  /** CTF: where the signal core stands when it is home. */
  flagPost?: MissionPoint;
  /** CTF: the friendly uplink the core has to reach. */
  captureRing?: MissionPoint;
}

export interface MapDefinition {
  id: string;
  name: string;
  /** One line for the map picker. What it *plays* like, not what it looks like. */
  blurb: string;
  /**
   * How far the playable rectangle reaches from the origin, per world axis, in
   * metres. **This is the arena bound**, not a hint about one.
   *
   * It was a description of `ARENA_HALF_SIZE` until a map wanted to be a
   * different size; it is now the figure the simulation actually clamps to, the
   * distance `distanceToWall` measures, the extent of the client's ground plane
   * and the radius its fog reaches. An arena resolves it once, in its
   * constructor, and the client resolves the same pair out of the same map on
   * the `joined` payload — beside the buildings, before the first reconcile,
   * because a client predicting against a wall in a different place is corrected
   * on every tick it spends near one.
   *
   * **A pair rather than one number, and the axes are named rather than a
   * width and a length.** The simulation clamps in world axes; a name that has
   * to be resolved against a map's orientation is a name that will be resolved
   * wrongly once. It is a pair rather than a scalar with an optional second
   * component because that second form would compile at every call site while
   * letting half of them read one axis for both — see `ArenaHalfExtent`.
   *
   * **Every map is square today**, at `{ x: 100, z: 100 }` bar Karsten Basin's
   * 220 — which is what the whole game used to be, so the pair changed the type
   * and no map's size. A rectangle is a later step, deliberately: with the
   * geometry held still, any divergence the probe reports is the per-axis bound
   * and nothing else.
   */
  halfExtent: ArenaHalfExtent;
  /**
   * Tanks per side, humans and bots together. Absent means `TEAM_SIZE`.
   *
   * A property of the map for the same reason the size is: a fifteen-a-side
   * battle needs a battlefield built for thirty tanks and a base with somewhere
   * for fifteen of them to appear, so the roster and the ground cannot be chosen
   * independently. Resolve it with `teamSizeFor`, and the arena's human capacity
   * with `maxHumansFor`.
   */
  teamSize?: number;
  /**
   * A gunnery range: the AI holds its fire, and only the side with no humans is
   * filled.
   *
   * One flag rather than two toggles, because these are not independent choices
   * — they are what the word *range* means. A target that shoots back is not a
   * target, and a team-mate on a range is a tank wandering through your line of
   * sight for no reason. Anything driving on a range map is there to be shot at.
   *
   * **The AI is not modified for it.** `stepBot` still aims, tracks and holds a
   * range exactly as it always does; `Arena.driveBots` zeroes the `fire` flag on
   * the input it produces, at the one place a bot's command is queued. That
   * keeps the rule where the bots-are-an-input-source design says it belongs — a
   * bot cannot do anything a player could not, and a range is a player choosing
   * not to pull the trigger. `bot-ai.ts` never learns that ranges exist.
   *
   * It is a property of the **map**, so it is fixed at construction like the map
   * itself and cannot change under a running arena.
   */
  gunneryRange?: boolean;
  /**
   * The geometry a mission is fought over: its route, its spawn gates, where
   * the core stands and where it has to be carried.
   *
   * Shared, and on the map rather than on the wire, for exactly the reason the
   * props are: the client draws the route line, the gate markers and the
   * capture ring, and it resolves all of them from `mapId` at join. **No
   * mission geometry crosses the wire per snapshot** — what the snapshot
   * carries is which leg, which wave and where the core is *now*.
   *
   * Absent on every PvP map, and a mission map without one is a mission that
   * cannot be laid out — `maps.test.ts` asserts the pairing both ways, because
   * the failure is otherwise a convoy with nowhere to drive.
   */
  mission?: MissionLayout;
  props: PlacedProp[];
  /**
   * Soft-shadow blobs — cosmetic ground paint, never a collision body. See
   * `decals.ts`. Absent means none; nearly every map today has none, so this
   * costs nothing to leave unstated.
   */
  decals?: PlacedDecal[];
  /**
   * A fortified base per team, or absent.
   *
   * Optional because two maps genuinely have none: the empty proving ground and
   * Ashfield, which predates team bases and is kept exactly as it played. A map
   * with no bases falls back to the arena's original spawn ring, so "no bases"
   * is a supported layout rather than a missing feature.
   */
  bases?: Readonly<Record<TeamId, MapBase>>;
}

/**
 * Resolves a map's placements into collision bodies.
 *
 * Placements naming an unknown prop are dropped rather than throwing: a map is
 * data, and a typo in it should cost one building, not the match.
 */
export function buildBodies(map: MapDefinition): PropBody[] {
  const bodies: PropBody[] = [];
  for (const placed of map.props) {
    const def = propDefinition(placed.propId);
    if (!def) continue;
    // `clipSize` is only ever trusted for an invisible clip volume — see its
    // own doc on `PlacedProp`. A stray override on an ordinary, meshed prop
    // would desync the collision box from the art nobody resized to match,
    // so it is read here and nowhere else in the resolution.
    const size = def.invisible ? placed.clipSize : undefined;
    const halfX = size?.halfX ?? def.halfX;
    const halfZ = size?.halfZ ?? def.halfZ;
    bodies.push({
      propId: def.id,
      x: placed.x,
      z: placed.z,
      yaw: placed.yaw,
      halfX,
      halfZ,
      height: size?.height ?? def.height,
      // Per-placement clip overrides, resolved in exactly one place so the
      // simulation, the shell path and the bots cannot disagree about what a
      // given placement stops. See `placementBlocking`.
      ...placementBlocking(placed, def),
      // Drawing only — nothing in the simulation reads it. Carried on the body
      // so a renderer never has to match one back to its placement; see
      // `PlacedProp.liftY` for the stacked-pillar bug that matching caused.
      ...(placed.liftY ? { liftY: placed.liftY } : {}),
      boundingRadius: Math.hypot(halfX, halfZ),
    });
  }
  return bodies;
}

// ─── Placement helpers ───────────────────────────────────────────────────────

/**
 * Repeats a barrier section end to end, from one point along a bearing.
 *
 * Sections meet exactly because each is placed `2 * halfX` further along — the
 * footprint is the spacing, so a run has no gaps for a shell to slip through and
 * no overlaps for a tank to catch on.
 */
export function run(propId: PropId, x: number, z: number, yaw: number, count: number): PlacedProp[] {
  const def = propDefinition(propId);
  if (!def) return [];
  const step = def.halfX * 2;
  // Along the section's **own long axis**, which the placement yaw has turned:
  // local +X lands on (cos, -sin), not on (sin, cos). Stepping the other way
  // lays the sections side-on like railings, spaced a section-length apart —
  // and a gap of one section minus its thickness is narrower than a tank, so
  // the resolver bounces it between two of them and never settles. It read as
  // a wall in a screenshot and as a hang in the collision tests.
  const dx = Math.cos(yaw);
  const dz = -Math.sin(yaw);
  const out: PlacedProp[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push({ propId, x: x + dx * step * i, z: z + dz * step * i, yaw });
  }
  return out;
}

/**
 * The same run, *centred* on the point rather than starting at it.
 *
 * Worth having as its own function because almost every wall in these maps is
 * described by where its middle is and how wide the gate beside it needs to be.
 * Computing the start by hand is where the arithmetic errors live: a run whose
 * end lands two metres past a side wall reads as a mistake, and one whose end
 * lands two metres short is a hole a tank drives through into the spawn.
 */
export function span(propId: PropId, x: number, z: number, yaw: number, count: number): PlacedProp[] {
  const def = propDefinition(propId);
  if (!def) return [];
  const step = def.halfX * 2;
  const back = (step * (count - 1)) / 2;
  return run(propId, x - Math.cos(yaw) * back, z + Math.sin(yaw) * back, yaw, count);
}

/**
 * Rotates a placement list about the origin and moves it.
 *
 * The rotation matches the game's own convention — forward is
 * `(sin yaw, cos yaw)`, and a box's long axis lies along `(cos yaw, -sin yaw)` —
 * so adding `yaw` to a placement and putting its position through this
 * transform turns the whole assembly rigidly. Getting the sign wrong here is not
 * subtle: the fort's walls end up mirrored against its own gate.
 */
function place(props: PlacedProp[], yaw: number, dx: number, dz: number): PlacedProp[] {
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return props.map((p) => ({
    // Spread first, so a placement carrying its own override (a barrier's
    // `blocksShells: true`, a hand-authored mask's `clipSize`) survives the
    // transform — only position and yaw are rewritten.
    ...p,
    x: p.x * cos + p.z * sin + dx,
    z: -p.x * sin + p.z * cos + dz,
    yaw: p.yaw + yaw,
  }));
}

/**
 * Marks a `span()`/`run()` run of Hesco barriers or chain-link fence as
 * already handled for movement, so `clipMasked` skips its per-prop pass over
 * them — see `SHELL_BLOCKING_MASK` in `clip-masks.ts`. The object keeps
 * blocking shells; `wallMask` below is what carries the movement collision.
 */
function wallOf(props: PlacedProp[]): PlacedProp[] {
  return props.map((p) => ({ ...p, blocksTanks: false, blocksShells: true }));
}

/**
 * One `clip_wall_move` covering a whole barrier run, in place of one mask per
 * section.
 *
 * `x`/`z`/`yaw` must be the same triple the `span()` call for that run used —
 * `span` centres its run on that point, so the mask lands there too. `count`
 * is that same run's barrier count; the mask's long axis is sized to the
 * run's own half-length (`propHalfX * count`) rather than shrunk by
 * `MASK_SCALE`, or a merged 60 m+ wall would open a gap at each end wide
 * enough for a hull to cross. Only the thickness and height — the axes a
 * single barrier's own mask already shrinks — keep the 90% scale.
 */
function wallMask(propId: PropId, x: number, z: number, yaw: number, count: number): PlacedProp {
  const def = propDefinition(propId)!;
  const round2 = (v: number) => Math.round(v * 100) / 100;
  return {
    propId: 'clip_wall_move',
    x,
    z,
    yaw,
    clipSize: {
      halfX: round2(def.halfX * count),
      halfZ: round2(def.halfZ * MASK_SCALE),
      height: round2(def.height * MASK_SCALE),
    },
  };
}

/** Reflects a layout across the x axis — north half to south half. */
function mirrorZ(props: PlacedProp[]): PlacedProp[] {
  return props.map((p) => ({ propId: p.propId, x: p.x, z: -p.z, yaw: -p.yaw }));
}

/** Reflects a layout across the z axis — east half to west half. */
function mirrorX(props: PlacedProp[]): PlacedProp[] {
  return props.map((p) => ({ propId: p.propId, x: -p.x, z: p.z, yaw: Math.PI - p.yaw }));
}

/** Turns a layout half a revolution about the origin — for diagonal maps. */
function mirrorPoint(props: PlacedProp[]): PlacedProp[] {
  return props.map((p) => ({ propId: p.propId, x: -p.x, z: -p.z, yaw: p.yaw + Math.PI }));
}

// ─── The base ────────────────────────────────────────────────────────────────

/**
 * A fortified base, built facing +Z at the origin and then placed.
 *
 * All five maps use the same fort. That is a decision, not laziness: these are
 * one army's field fortifications, so they are made of the same kit, and a
 * player who learns where the gate is on one map knows where it is on all of
 * them. What differs is where the fort stands and what it overlooks — which is
 * the part that actually changes how a match plays.
 *
 * The shape, in local metres, with the enemy toward +Z:
 *
 *            teeth ───┐  gate  ┌─── teeth          z = +11
 *      ┌ gap ─┤                        ├─ gap ─┐
 *      │                                       │
 *   side wall            spawns            side wall   x = ±30
 *      │             command post              │
 *      └───────────── back wall ───────────────┘        z = -13
 *
 * Three things about it are load-bearing:
 *
 * - **It has three ways in, not one.** A twelve-metre gate in the middle and a
 *   ten-metre gap at each front corner. A team of three can stand on three
 *   doors, but then it is three tanks fighting alone, which is worse than three
 *   tanks fighting together somewhere else. A single-entrance fort is a fort
 *   worth sitting in, and a match where both sides sit in one is not a match.
 * - **It gives cover but no view.** The teeth and the barriers stop shells in
 *   both directions. A defender inside can only shoot through the openings, so
 *   the base is a place to survive a bad engagement and rejoin, not a firing
 *   position that covers the map.
 * - **Nothing in it is worth anything.** Airdrops fall inside 70 m of the centre
 *   and every base sits outside that, so time spent in the base is time not
 *   spent on the only thing on the map that pays.
 */
function bastion(team: TeamId, cx: number, cz: number, facing: number): Fort {
  const local: PlacedProp[] = [
    // Back wall, x -30 .. 30. Meets both side walls at the corner. One mask
    // for the whole run, not one per barrier — see `wallOf`/`wallMask` above.
    ...wallOf(span('hesco_barrier', 0, -13, 0, 12)),
    // Side walls, z -12.5 .. 12.5. Their inner faces are at x = ±29.1.
    ...wallOf(span('hesco_barrier', 30, 0, Math.PI / 2, 5)),
    ...wallOf(span('hesco_barrier', -30, 0, Math.PI / 2, 5)),
    wallMask('hesco_barrier', 0, -13, 0, 12),
    wallMask('hesco_barrier', 30, 0, Math.PI / 2, 5),
    wallMask('hesco_barrier', -30, 0, Math.PI / 2, 5),
    // Front line. Teeth reach x = ±18.8, leaving a 10.3 m corner gap each side
    // before the side wall, and the pylons stand as the gate posts at ±6.0 —
    // so the gate itself is 12 m, comfortably two tanks abreast.
    ...span('dragon_teeth', -14, 11, 0, 2),
    ...span('dragon_teeth', 14, 11, 0, 2),
    { propId: 'gate_pylon', x: -7.6, z: 11, yaw: 0 },
    { propId: 'gate_pylon', x: 7.6, z: 11, yaw: 0 },
    // The compound's one building, at the back where it cannot be shot from
    // outside the walls.
    { propId: 'command_post', x: 0, z: -9, yaw: 0 },
    // Sandbag lines, **tucked behind the teeth rather than across the compound**.
    //
    // They used to be three runs at z = 6 — two flanking and one on the centre
    // line — and the comment justifying that said "sandbags do not stop a tank,
    // so these can never trap a spawn". That premise is gone: sandbags are solid
    // now, and the centre run was a 10 m wall five metres inside a 12 m gate.
    // `maps.test.ts` caught both halves on every bastion map at once — "red gate
    // blocked 3 m out" and "red slot 3 is inside something", the slot being the
    // forward pair at (±8, 5) the flanking runs reached.
    //
    // So they moved to the one band that is free of both: x = ±15 is inside the
    // teeth's own shadow (they run |x| = 9.2 .. 18.8), and z = 8.5 is behind the
    // front line and clear of the forward slots, whose hulls reach z = 8. What
    // that leaves open is a 23 m gate lane and a 10.7 m lane at each corner gap —
    // both wider than the openings they serve, so the fort's three ways in are
    // still three ways in.
    //
    // **The centre run is not replaced**, and there is nowhere to put it: the
    // gate is 12 m and the forward spawn pair sits 6 m inside it, so anything
    // covering that lane either blocks it or stands on a slot. The fort's cover
    // is its walls and its teeth; these add depth on the flanks.
    ...span('sandbag_wall', -15, 8.5, 0, 2),
    ...span('sandbag_wall', 15, 8.5, 0, 2),
  ];

  // Seven slots, no two within 10 m of each other — which is the distance
  // `pickSpawn` treats as occupied, so a full base still has a free point.
  //
  // **Seven for four, not five**, and the ratio is the reason rather than the
  // spare pair: `maps.test.ts` asserts `slots / teamSize >= 5/3`, the bastion's own
  // five thirds, and 5 / 4 is 1.25. One edit here is inherited identically by every
  // bastion map, which is the property the shared fort exists for.
  //
  // The two additions are the **rear corners**, tucked either side of the command
  // post. The obvious alternative was the front corners at (±24, 6), which the
  // separation and the walls both allow — and which puts a spawning tank in the
  // 10.3 m corner gap, i.e. standing in one of the fort's three doorways. A base
  // must not spawn its own team in its own entrance; behind the command post is
  // the deepest ground in the compound and the furthest from anything that can be
  // shot through an opening.
  //
  // **The walls turned out to bind after all**, which the plan said they would
  // not. (±24, -9) is 10.63 m from its neighbour and reads as obviously inside the
  // compound — and it fails, by 5 cm: the back wall's inner face is at z = -11.95
  // and a 3 m hull at z = -9 reaches -12. (±25, -8) clears the back wall and the
  // side wall by 0.95 m each and is still 10.82 m from (∓16, -2). Two constraints,
  // both tight, in a compound that looks roomy.
  const localSpawns = [
    { x: -16, z: -2 },
    { x: 0, z: -2 },
    { x: 16, z: -2 },
    { x: -8, z: 5 },
    { x: 8, z: 5 },
    { x: -25, z: -8 },
    { x: 25, z: -8 },
  ];

  return fortify(team, cx, cz, facing, local, localSpawns);
}

/**
 * A fifteen-a-side fortification: the bastion, at the size that roster needs.
 *
 * Built to the same three rules, because they are what make a base a base
 * rather than a bunker somebody sits in for the whole match — and every one of
 * them had to be *re-derived* at this size rather than scaled, because two of
 * the three are about numbers of tanks.
 *
 * **Three ways in, and they are wider.** A 26 m gate on the centre line and an
 * 18.7 m gap at each front corner. The bastion's 12 m gate held against three
 * tanks precisely because three tanks standing on three doors is three tanks
 * fighting alone; fifteen tanks *can* afford to post five on each of three
 * narrow doors, so the doors are wide enough that standing in one is standing in
 * the open. The count stays at three: a fourth entrance would make the fort
 * scenery rather than a place to rally.
 *
 * **Cover but no view**, unchanged in kind — dragon's teeth across the front and
 * three sandbag lines inside, all of which stop shells in *both* directions, so
 * a defender can only shoot through the openings.
 *
 * **Twenty-six spawn slots for fifteen tanks**, which is the bastion's five
 * thirds carried across and rounded up, not a number picked to look generous.
 * Three rows at z = -12, 0 and +11, offset by 6 m so that no two slots are
 * within the 10 m `pickSpawn` treats as occupied — the closest pair is 11.7 m
 * apart. With fifteen slots a respawn during a lull would find none free and put
 * two tanks in one place, and at this roster size a lull is most of the time
 * between engagements.
 *
 * The shape, in local metres, with the enemy toward +Z:
 *
 *          teeth ──┐    26 m gate    ┌── teeth           z = +22
 *     ┌ 18.7 m ────┤                 ├──── 18.7 m ┐
 *     │                  sandbags                 │
 *  side wall           26 spawn slots          side wall    x = ±60
 *     │               command post                │
 *     └──────────────── back wall ────────────────┘          z = -26
 */
function citadel(team: TeamId, cx: number, cz: number, facing: number): Fort {
  const local: PlacedProp[] = [
    // Back wall, x -60 .. 60. Meets both side walls at the corners. One mask
    // for the whole run, not one per barrier — see `wallOf`/`wallMask` above.
    ...wallOf(span('hesco_barrier', 0, -26, 0, 24)),
    // Side walls, z -27 .. 23. Their inner faces are at x = ±58.95.
    ...wallOf(span('hesco_barrier', 60, -2, Math.PI / 2, 10)),
    ...wallOf(span('hesco_barrier', -60, -2, Math.PI / 2, 10)),
    wallMask('hesco_barrier', 0, -26, 0, 24),
    wallMask('hesco_barrier', 60, -2, Math.PI / 2, 10),
    wallMask('hesco_barrier', -60, -2, Math.PI / 2, 10),
    // Front line. Teeth run |x| = 16.2 .. 40.2, leaving an 18.75 m corner gap
    // each side before the wall, and the pylons stand as gate posts with their
    // inner faces at ±13.0 — so the gate is 26 m, four tanks abreast.
    ...span('dragon_teeth', -28.2, 22, 0, 5),
    ...span('dragon_teeth', 28.2, 22, 0, 5),
    { propId: 'gate_pylon', x: -14.6, z: 22, yaw: 0 },
    { propId: 'gate_pylon', x: 14.6, z: 22, yaw: 0 },
    // The compound's one building, at the back where it cannot be shot from
    // outside the walls and clear of the rearmost spawn row.
    { propId: 'command_post', x: 0, z: -21, yaw: 0 },
    // Sandbag lines, behind the teeth — the bastion's repair at this size, and
    // for the same reason: they are solid now, so the centre run was a 24 m wall
    // six metres inside a 26 m gate. Centred on the teeth's own band
    // (|x| = 16.2 .. 40.2) and set at z = 18, which is behind the front line and
    // well clear of the forward spawn row at z = 11, whose hulls reach z = 14.
    // That leaves a 39 m gate lane and a 22 m lane at each corner gap.
    ...span('sandbag_wall', -28.2, 18, 0, 5),
    ...span('sandbag_wall', 28.2, 18, 0, 5),
  ];

  // Twenty-six slots in three offset rows. Rows are 12 m and 11 m apart and the
  // middle row is stepped 6 m sideways, so the nearest pair is hypot(6, 11) =
  // 12.5 m — comfortably past the 10 m `pickSpawn` reads as occupied.
  const localSpawns: { x: number; z: number }[] = [];
  for (let i = -4; i <= 4; i += 1) localSpawns.push({ x: i * 12, z: -12 });
  for (let i = -3.5; i <= 3.5; i += 1) localSpawns.push({ x: i * 12, z: 0 });
  for (let i = -4; i <= 4; i += 1) localSpawns.push({ x: i * 12, z: 11 });

  // The fort is 122 m across and 50 m deep, so the bastion's 35 m would leave
  // most of it inside the crate scatter. 80 m covers the compound and the ground
  // immediately outside its gate, which is the ground a team must not be paid to
  // sit on.
  return fortify(team, cx, cz, facing, local, localSpawns, 80);
}

/** A fort's props and the base record that goes with it. */
interface Fort {
  base: MapBase;
  props: PlacedProp[];
}

/**
 * Turns a fort drawn at the origin facing +Z into one standing somewhere.
 *
 * Shared by every fort so that the transform is written once: the props go
 * through `place`, the spawn slots through the same rotation by hand, and a
 * mistake in either would show up as tanks appearing outside their own walls.
 */
function fortify(
  team: TeamId,
  cx: number,
  cz: number,
  facing: number,
  local: PlacedProp[],
  localSpawns: readonly { x: number; z: number }[],
  keepOut?: number,
): Fort {
  const cos = Math.cos(facing);
  const sin = Math.sin(facing);

  return {
    base: {
      team,
      x: cx,
      z: cz,
      facing,
      spawns: localSpawns.map((s) => ({
        x: s.x * cos + s.z * sin + cx,
        z: -s.x * sin + s.z * cos + cz,
      })),
      ...(keepOut === undefined ? {} : { keepOut }),
    },
    props: place(local, facing, cx, cz),
  };
}

/**
 * Assembles both bases and hands back the props and the lookup together.
 *
 * `fort` is a parameter rather than a hard-coded `bastion` because one map's
 * roster is five times the others'. It defaults to the bastion, so the five maps
 * that share one army's field fortifications go on sharing them by saying
 * nothing.
 */
function bases(
  red: { x: number; z: number; facing: number },
  blue: { x: number; z: number; facing: number },
  fort: (team: TeamId, x: number, z: number, facing: number) => Fort = bastion,
): { bases: Record<TeamId, MapBase>; props: PlacedProp[] } {
  const r = fort('red', red.x, red.z, red.facing);
  const b = fort('blue', blue.x, blue.z, blue.facing);
  return { bases: { red: r.base, blue: b.base }, props: [...r.props, ...b.props] };
}

/** Facings, named. Forward is `(sin yaw, cos yaw)`. */
const NORTH = 0;
const SOUTH = Math.PI;
const EAST = Math.PI / 2;
const NORTH_WEST = -Math.PI / 4;

// ─── Maps with no bases ──────────────────────────────────────────────────────

/**
 * The empty map — the arena with nothing in it.
 *
 * Kept as a real entry rather than a null case so that "no scenery" is a map
 * like any other, and every code path that handles props is exercised by the
 * default configuration rather than skipped. It is also the honest place to test
 * a gun against a moving target with nothing in the way.
 */
export const MAP_EMPTY: MapDefinition = {
  id: 'empty',
  name: 'Proving ground',
  blurb: 'Bare ground, no cover, no bases. Gunnery practice against live tanks.',
  // **The first rectangle in the game**, and deliberately the map with nothing on
  // it: 280 m across by 420 m long, 2.94× the old square. It went first because
  // it has no props, so anything that looks wrong here is the per-axis bound, the
  // walls, the fog, the spawn ring or the crate scatter — never a layout. It also
  // has a standing reason to be long: the Railspike reaches 260 m and that does
  // not fit in a 200 m proving ground.
  //
  // The long axis is z, matching every enlarged battlefield (`docs/maps-design.md`
  // §2.0 puts the bases on it at (0, ±188)), so a gunnery lane driven here is the
  // same lane as the approach on a real map.
  halfExtent: { x: 140, z: 210 },
  // Four tanks a side, like every map the resize enlarges. `MAP_TOWN.teamSize`
  // carries the reasoning for all six, including what it costs to raise the roster
  // before the other five have been resized.
  teamSize: 4,
  props: [],
};

/**
 * The drill yard — where the tutorial is fought.
 *
 * Bare ground like the proving ground, and **empty until the mission puts
 * something on it**. That emptiness is the whole reason this exists as its own
 * map rather than the tutorial opening a room on `empty`: the drills are
 * sequential, and the first three of them — look, drive, shoot — happen with
 * nothing on the field at all. `teamSize: 1` used to be what bought that (it was
 * the smallest roster `ensureBots` could be asked for, and it still delivered
 * the first lesson under fire from one tank); a mission arena skips `ensureBots`
 * entirely, so the yard is now genuinely bare and every tank on it arrives
 * because a stage called for it.
 *
 * The number is kept anyway, and it is doing different work now: `capacity` is
 * derived from it, so `teamSize: 1` is what makes a tutorial room seat two
 * people rather than eight. The room is private per player in any case.
 *
 * **Deliberately not in `MAP_SUMMARIES`.** It is a real map and it plays like
 * one, but it is not a battlefield to choose beside Ironback Ridge: bare ground
 * offered in the picker is a lesson dressed as a match. See the note on
 * `MAP_SUMMARIES`.
 */
export const MAP_DRILL: MapDefinition = {
  id: 'drill',
  name: 'Drill Yard',
  blurb: 'Bare ground, and nothing on it until the drill calls for it.',
  // The same square as everywhere else, and it has to be at least this big: the
  // gates sit at the far west of a yard the player spawns on the west half of,
  // and both the crate the `supply` drill lands (55 m from the player, inward)
  // and the group's drive have to be a drive rather than a turn of the head.
  halfExtent: { x: 100, z: 100 },
  teamSize: 1,
  /**
   * The tutorial's own points: where the final group forms up, and nothing else.
   *
   * There used to be a `rally` point at the east end too — the ground the player
   * was sent to and told to hold — and it went with the two commanding drills
   * (see `TutorialStageId`). The drill that replaced them puts its crate at a
   * distance from the *player* rather than at a point on the map, for the reason
   * the duelling enemy already did: the yard has no bases, so a tank can start
   * or finish a fight anywhere on it and a fixed point is a different drill on
   * every run.
   *
   * The gates are west and clear of the ring the arena spawns blue on (0.75 of
   * each half-extent, on the west half of the ellipse), so the group's drive is a
   * real one and it never arrives on top of anybody.
   *
   * Gate yaw is `π/2` — heading is `(sin, cos)`, so that is due +x, facing back
   * across the yard.
   */
  mission: {
    kind: 'tutorial',
    gates: [
      { x: -72, z: -18, yaw: Math.PI / 2 },
      { x: -72, z: 0, yaw: Math.PI / 2 },
      { x: -72, z: 18, yaw: Math.PI / 2 },
    ],
  },
  props: [],
};

/** The map the tutorial is fought on. See `MAP_DRILL`. */
export const TUTORIAL_MAP_ID = MAP_DRILL.id;

/**
 * A small town in the middle of the proving ground.
 *
 * Laid out to give the arena what an empty plane cannot: cover to break line of
 * sight, corners worth holding, and lanes between buildings that make position
 * mean something. Everything sits inside a radius of about 71 m, which leaves
 * the spawn ring at 75 m clear — a tank must never appear inside a wall — and
 * the exact centre is empty because that is where `pickSpawn` falls back to when
 * every ring slot is taken.
 *
 * **Deliberately has no bases.** It predates them, both sides spawn around the
 * whole ring, and the *shape* is kept exactly as it played — four quarters
 * round a fought-over crossroads — which also means it is the one map that
 * exercises the ring-spawn fallback in a real match rather than only in a unit
 * test.
 *
 * ## Why this one is hand-laid, and the only one that is
 *
 * Every other map generates its second half from its first, because a map with
 * a front line has to be fair to the side that starts on the wrong end of it.
 * Ashfield has no front line: both sides spawn all round the same ring, so
 * there is no "your half", and `maps.test.ts` asserts symmetry on every map
 * except this one and the two bare ones. A town that was symmetric about
 * anything would stop being a town. So the quarters differ from each other on
 * purpose, and the fairness they need is that **every quarter is somebody's
 * back yard and nobody's own** — which is a property of the ring spawn, not of
 * the layout.
 *
 * ## The four quarters, and what makes each one a place
 *
 * - **North-west, the works.** One big volume (the industrial unit), a
 *   warehouse turned across it, a fuel silo and the water tower. Long sight
 *   lines broken by things too big to shoot past rather than by clutter.
 * - **North-east, the town proper.** Two terraces standing shoulder to
 *   shoulder along the high street with a 3 m ginnel between them, their back
 *   gardens walled in drystone, outbuildings *behind* the houses where
 *   outbuildings go, and the civic hall facing the crossroads. This is the
 *   piece the whole re-dress is really about: houses front a street, and the
 *   street is the thing you fight along.
 * - **South-east, the farm.** Farmhouse, barn and grain store round a walled
 *   yard, a hedge along the field boundary and an orchard in the corner. The
 *   orchard is planted on a 12 m by 10 m grid, which is a real orchard spacing
 *   and is also wide enough that a tank drives between the rows rather than
 *   into them.
 * - **South-west, the ruins.** The quarter that was shelled: a gutted
 *   apartment block, a roofless hall, a burnt-out bus and a bank of rubble.
 *   Ruins stand **where buildings would have stood** — along the same street
 *   lines as the north-east terraces — rather than scattered, because scattered
 *   ruins read as debris and a ruined street reads as a town somebody lost.
 *
 * ## Two lines that must stay clear, and both are load-bearing
 *
 * - **The origin**, because `pickSpawn` and `pickDropSpot` both fall back to it.
 * - **x = 55, from z = 0 to z = 40.** `arena.test.ts` lays its friendly-fire
 *   firing range down that lane on this map, and a shell that stops on masonry
 *   measures masonry rather than the target filter. Nothing solid is placed
 *   within 8 m of it in that band. The lane used to be x = 0 and moved here when
 *   scenery started stopping rounds; moving it again is a test change, not a
 *   map change, so it is cheaper to keep the lane than to keep re-finding one.
 *
 * ## Enclosure is drystone and hedge, never brick
 *
 * `wall_brick_garden` blocks tanks and `wall_drystone` does not, and a town's
 * worth of garden walls is exactly where that distinction earns its keep: a
 * 3.5 m alley between a terrace's back wall and its garden wall is narrower
 * than a tank, and a bot that drives into one has nowhere to go. Every
 * enclosure here is drystone, hedge or wooden fence, so the yards read as yards
 * and none of them is a trap. The rule generalises to every map in this file.
 */
export const MAP_TOWN: MapDefinition = {
  id: 'town',
  name: 'Ashfield',
  blurb: 'A town, no front line. Both sides spawn all round it — a free-for-all.',
  halfExtent: { x: 100, z: 100 },
  /**
   * Four a side, on every map the resize enlarges.
   *
   * Stated on each of the six rather than by moving `DEFAULT_TEAM_SIZE`, because
   * `foundry` keeps its 200 m square *and* its three (§2.0), and the drill yard
   * keeps its one. The roster and the ground are one decision — eight tanks on
   * 117,600 m² is 14,700 m² each, 2.2× the room a tank has on a 200 m map — which
   * is also the honest caveat here: **five of the six still are 200 m squares.**
   * `teamSize` leads the resize by design (`docs/maps-design.md` §18 step 5 comes
   * before step 6) so that the fort gains its spawn slots once, in one place,
   * rather than map by map as each one is rebuilt; until a map's own resize lands
   * it is playing a four-a-side roster on three-a-side ground.
   */
  teamSize: 4,
  props: [
    // Collision lives on invisible masks at 90% of each prop's own box,
    // not on the props themselves — see `clipMasked` in `clip-masks.ts`.
    ...clipMasked([
    // ── North-west: the works ──
    { propId: 'industrial_unit', x: -44, z: 40, yaw: 0 },
    { propId: 'warehouse_brick', x: -16, z: 56, yaw: Math.PI / 2 },
    { propId: 'workshop_garage', x: -52, z: 24, yaw: 0.3 },
    { propId: 'silo_battery', x: -42, z: 20, yaw: 0 },
    { propId: 'water_tower', x: -58, z: 8, yaw: 0 },
    { propId: 'fuel_silo', x: -14, z: 34, yaw: 0 },
    // Back against the works, not out in the yard. This pair stood at (-28, 20)
    // and (-28, 26) for one revision, which put a shipping container on the
    // exact ground `arena.test.ts` has its commander stand on to test "hold
    // this position" — and a hold order anchored inside a solid prop is a test
    // whose premise is false. The precedent in `CLAUDE.md` is the firing range
    // that moved off x = 0 when the guard tower started stopping shells; the
    // difference here is that the building was the new thing, so the building
    // moved. Ashfield's centre-west is open ground and stays that way.
    { propId: 'container_stack', x: -40, z: 26, yaw: 0.2 },
    { propId: 'container_stack', x: -40, z: 30, yaw: 0.2 },
    // The works' own boundary, **with a gate in it**. Chain-link used to stop
    // nothing, which was what made it safe to draw a yard round buildings that
    // are already tight; solid, an unbroken 35 m run is the south wall of a box.
    // The break at x = -48.5 .. -31.5 is the yard entrance, seventeen metres of
    // it, and it is what the works are entered through rather than driven into.
    // Ten was the first attempt and is exactly the navigator's working floor —
    // `steerAround` threading a gap at the floor is a bot that will one day
    // stop, and a squad ordered to hold ground inside the yard did: mean 44 m
    // from a point they were told to gather on.
    //
    // The concrete run along the north side is kept as it was — `maps.test.ts`
    // drives the collision resolver out from inside a `wall_concrete` section
    // and this is the map it does it on.
    ...run('fence_chainlink', -56, 12, 0, 2),
    ...run('fence_chainlink', -29, 12, 0, 2),
    ...run('wall_concrete', -56, 16, 0, 3),

    // ── North-east: the high street ──
    // Two terraces facing the crossroads with a ginnel between them, gardens
    // and outbuildings behind. The street runs east–west at z ≈ 34.
    { propId: 'terrace_row', x: 22, z: 44, yaw: 0 },
    { propId: 'terrace_row', x: 40, z: 44, yaw: 0 },
    { propId: 'brick_outbuilding', x: 20, z: 58, yaw: 0.2 },
    { propId: 'brick_outbuilding', x: 34, z: 58, yaw: -0.3 },
    ...run('wall_drystone', 14, 53, 0, 7),
    { propId: 'civic_hall', x: 30, z: 26, yaw: 0 },
    { propId: 'chapel_stone', x: 6, z: 60, yaw: 0 },
    ...span('wall_drystone', 6, 52, 0, 5),
    { propId: 'house_two_storey', x: 44, z: 30, yaw: Math.PI / 2 },
    { propId: 'tree_oak', x: 16, z: 30, yaw: 0.4 },

    // ── South-east: the farm ──
    { propId: 'farmhouse_long', x: 34, z: -32, yaw: Math.PI / 2 },
    { propId: 'barn_wooden', x: 52, z: -32, yaw: Math.PI / 2 },
    { propId: 'grain_store', x: 16, z: -40, yaw: 0 },
    { propId: 'brick_outbuilding', x: 42, z: -17, yaw: 0 },
    ...run('wall_drystone', 20, -22, 0, 6),
    ...span('hedgerow', 37, -10, 0, 4),
    // The orchard, on a 12 by 10 grid — real spacing, and wide enough to drive.
    { propId: 'tree_orchard', x: 10, z: -58, yaw: 0 },
    { propId: 'tree_orchard', x: 22, z: -58, yaw: 0 },
    { propId: 'tree_orchard', x: 34, z: -58, yaw: 0 },
    { propId: 'tree_orchard', x: 10, z: -48, yaw: 0 },
    { propId: 'tree_orchard', x: 22, z: -48, yaw: 0 },
    { propId: 'tree_orchard', x: 34, z: -48, yaw: 0 },

    // ── South-west: the quarter that was shelled ──
    { propId: 'ruin_apartment', x: -32, z: -24, yaw: 0.3 },
    { propId: 'ruin_warehouse', x: -46, z: -38, yaw: 0.4 },
    { propId: 'ruin_roofless', x: -20, z: -42, yaw: -0.2 },
    { propId: 'ruin_corner', x: -34, z: -50, yaw: 0.9 },
    { propId: 'ruin_church', x: -10, z: -58, yaw: 0.2 },
    { propId: 'ruin_gable', x: -56, z: -20, yaw: 0.6 },
    { propId: 'rubble_bank', x: -38, z: -14, yaw: -0.4 },
    { propId: 'wreck_bus', x: -44, z: -12, yaw: 1.0 },
    { propId: 'wreck_car', x: -26, z: -16, yaw: 0.5 },

    // ── The crossroads ──
    // Hard cover either side of the middle, with sandbags to fight from.
    // Nothing sits on the origin itself.
    //
    // **The sandbag runs are two sections each, not five.** They are positions to
    // fight *from*, which is what they were when a hull crossed them; solid, two
    // five-section runs at x = -8 were a 27 m wall down the west side of the
    // crossroads, and a squad ordered to hold ground in the works quarter piled
    // up against it 44 m short. Two apiece leaves a 13 m gap on the axis, which
    // is the crossing the crossroads is named for.
    { propId: 'bunker_concrete', x: -14, z: 0, yaw: 0 },
    { propId: 'bunker_concrete', x: 14, z: 0, yaw: Math.PI },
    { propId: 'guard_tower', x: 0, z: 22, yaw: 0 },
    { propId: 'guard_tower', x: 0, z: -22, yaw: Math.PI },
    ...run('sandbag_wall', -8, 10, Math.PI / 2, 2),
    ...run('sandbag_wall', -8, -10, Math.PI / 2, 2),
    { propId: 'bus_shelter', x: 9, z: 9, yaw: 0.4 },
    { propId: 'wreck_truck', x: -5, z: 17, yaw: 1.2 },
    { propId: 'wreck_car', x: 7, z: -13, yaw: -0.6 },
    { propId: 'utility_pole', x: -6, z: 30, yaw: 0 },
    { propId: 'utility_pole', x: -6, z: -30, yaw: 0 },
    { propId: 'utility_pole', x: 34, z: 4, yaw: 0 },
    { propId: 'utility_pole', x: -34, z: 4, yaw: 0 },
      ]),
  ],
};

// ─── 1. Ironback Ridge ───────────────────────────────────────────────────────

/**
 * A rock spine across the middle, with three ways through it.
 *
 * **The map about committing to a lane.** Bases face each other north to south
 * across a continuous east–west ridge, and there are exactly three crossings:
 * a fourteen-metre defile on the centre line, and a nineteen-metre pass on each
 * flank at |x| = 29–48. Rotating from one flank pass to the other is a 90 m
 * drive behind your own half of the ridge — five to seven seconds at speed, and
 * that number is the whole design. It is long enough that a three-tank team
 * cannot cover two passes at once, and short enough that being wrong is not
 * fatal.
 *
 * **The ridge has no gaps by accident.** The rock runs are placed with `run` at
 * a yaw of zero so each outcrop meets the next exactly. A stray metre between
 * two of them would be narrower than a tank and the collision resolver would
 * bounce a hull between them forever — the failure the comment on `run` is
 * about, and the reason nothing on this line is given a decorative rotation.
 *
 * **The approach is long and it is meant to be.** From a base gate to the ridge
 * is about 55 m of half-open ground with rubble, bunkers and loose rock to move
 * between. Long-barrelled guns own that ground; everything else has to cross it
 * in bounds. Whoever holds the centre defile can shoot into both approaches,
 * which is why the defile is the narrowest crossing and the one with the least
 * cover on either side.
 *
 * ## What is on the ground, and why it is where it is
 *
 * The spine runs through a farmed upland, and the hamlet that stood at the
 * defile is the reason anybody is fighting over it. Reading outward from the
 * centre line:
 *
 * - **The church, on the centre line at z = 36, inside its own walled yard.**
 *   A church has ground around it, so it has ground around it — a drystone
 *   rectangle that stops shells and not tanks, which makes the yard a place to
 *   fight *in* rather than an obstacle to go round. It stands where a water
 *   tower used to, and does the same job: it is the one thing on the straight
 *   line from a base gate to the defile, so the centre approach is a lane and
 *   not a firing range.
 * - **The hamlet, ruined, on both shoulders of the defile.** Three ruins on the
 *   same short street, not scattered: a roofless shell, a gable end and a
 *   corner, with a burnt-out lorry between them. Ruins belong where buildings
 *   would have been.
 * - **The steading, behind each ridge arm at |x| ≈ 66.** Farmhouse fronting its
 *   yard, barn and grain store behind it, an outbuilding at the gate, a
 *   drystone yard wall with a farm gate in it and a poplar windbreak along the
 *   arena wall. This is the ground a team rotating between the flank passes
 *   drives through, so it is cover on the long way round rather than on the
 *   crossings themselves.
 * - **Scree, everywhere the spine sheds it.** Loose `rock_outcrop` on the
 *   approaches — the same prop as the ridge, which costs nothing to download and
 *   is the only rock this landscape would actually produce.
 *
 * **This layout was authored as a mirrored quadrant and is now flat literals.**
 * It was generated for the north-west quadrant and reflected twice — `mirrorX`
 * east, `mirrorZ` south — so neither side and neither flank had a metre more
 * cover than the other. A BAKE from the map editor replaced that composition
 * with the placements it produced, and the editor is where the layout is edited
 * now. The description above still reads true of what is on the ground; what is
 * no longer true is that the two halves are guaranteed identical by
 * construction. `maps.test.ts`'s fairness ledger is what holds them comparable
 * instead, and it is a ledger of outcomes rather than of symmetry.
 */
export const MAP_RIDGE: MapDefinition = {
  id: 'ridge',
  name: 'Ironback Ridge',
  blurb: 'A rock spine with three crossings. Pick a flank and live with it.',
  halfExtent: { x: 100, z: 100 },
  // Four a side, like every map the resize enlarges — see `MAP_TOWN.teamSize`.
  teamSize: 4,
  bases: {
      blue: {
      team: 'blue',
      x: 0,
      z: -78,
      facing: 0,
      spawns: [
        { x: -16, z: -80 },
        { x: 0, z: -77 },
        { x: 16, z: -80 },
        { x: -25, z: -67 },
        { x: 25, z: -67 },
        { x: -25, z: -86 },
        { x: 25, z: -86 },
      ],
    },
      red: {
      team: 'red',
      x: 0,
      z: 78,
      facing: Math.PI,
      spawns: [
        { x: 16, z: 80 },
        { x: 0, z: 77 },
        { x: -16, z: 80 },
        { x: 25, z: 67 },
        { x: -25, z: 67 },
        { x: 25, z: 86 },
        { x: -25, z: 86 },
      ],
    },
    },
  props: [
    { propId: 'hesco_barrier', x: 27.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: 22.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: 17.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: 12.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: -12.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: -17.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: -22.5, z: 91, yaw: Math.PI },
    { propId: 'hesco_barrier', x: -27.5, z: 91, yaw: Math.PI },
    { propId: 'dragon_teeth', x: -11.33, z: 65.02, yaw: -2.962, blocksShells: false },
    { propId: 'dragon_teeth', x: -16, z: 65.5, yaw: Math.PI, blocksShells: false },
    { propId: 'gate_pylon', x: 7.5, z: 65.5, yaw: Math.PI },
    { propId: 'gate_pylon', x: -7.5, z: 65.5, yaw: Math.PI },
    { propId: 'hesco_barrier', x: -27.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: -22.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: -17.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: -12.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: -7.5, z: -89.5, yaw: -0.524 },
    { propId: 'hesco_barrier', x: 7.51, z: -89.55, yaw: 0.524 },
    { propId: 'hesco_barrier', x: 12.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: 17.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: 22.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: 27.5, z: -91, yaw: 0 },
    { propId: 'hesco_barrier', x: 30.5, z: -68, yaw: 1.669 },
    { propId: 'hesco_barrier', x: 30, z: -73, yaw: 1.627 },
    { propId: 'hesco_barrier', x: 30, z: -78, yaw: 1.475 },
    { propId: 'hesco_barrier', x: 30, z: -83, yaw: 1.517 },
    { propId: 'hesco_barrier', x: 30, z: -88, yaw: 1.621 },
    { propId: 'hesco_barrier', x: -30, z: -67.5, yaw: Math.PI / 2 },
    { propId: 'hesco_barrier', x: -30, z: -72.5, yaw: 1.419 },
    { propId: 'hesco_barrier', x: -30, z: -77.5, yaw: 1.54 },
    { propId: 'hesco_barrier', x: -30, z: -82.5, yaw: 1.46 },
    { propId: 'hesco_barrier', x: -29.5, z: -87.5, yaw: Math.PI / 2 },
    { propId: 'command_post', x: 0.01, z: -86.05, yaw: 0 },
    { propId: 'sandbag_wall', x: -16.72, z: -69.5, yaw: 0, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: -69.5, yaw: 0, blocksShells: false },
    { propId: 'rock_outcrop', x: -86.5, z: 0, yaw: 0 },
    { propId: 'rock_outcrop', x: -75.5, z: 0, yaw: -2.928 },
    { propId: 'rock_outcrop', x: -64, z: -0.5, yaw: -0.237 },
    { propId: 'rock_outcrop', x: -54, z: 0, yaw: 0 },
    { propId: 'rock_outcrop', x: -22, z: -0.5, yaw: -0.262 },
    { propId: 'rock_outcrop', x: -12.5, z: 0, yaw: -2.88 },
    { propId: 'rock_outcrop', x: 12.5, z: 0, yaw: 0 },
    { propId: 'rock_outcrop', x: 22, z: 0, yaw: -2.88 },
    { propId: 'rock_outcrop', x: 55, z: 0, yaw: -0.262 },
    { propId: 'rock_outcrop', x: 64.5, z: 0, yaw: 2.88 },
    { propId: 'rock_outcrop', x: 75, z: 1.5, yaw: 0 },
    { propId: 'rock_outcrop', x: 79.5, z: 1.5, yaw: 0.262 },
    { propId: 'wall_drystone', x: -11.5, z: 40, yaw: Math.PI / 2 },
    { propId: 'wall_drystone', x: -11.5, z: 31, yaw: Math.PI / 2 },
    { propId: 'ruin_roofless', x: -20, z: 22, yaw: 0.25 },
    { propId: 'wreck_truck', x: -20.95, z: 32.89, yaw: 1.1 },
    { propId: 'farmhouse_long', x: -66, z: 46, yaw: -0.014 },
    { propId: 'wall_drystone', x: -82, z: 40, yaw: 0 },
    { propId: 'wall_drystone', x: -77.2, z: 40, yaw: 0 },
    { propId: 'wall_drystone', x: -72.4, z: 40, yaw: 0 },
    { propId: 'wall_drystone', x: -67.6, z: 40, yaw: 0 },
    { propId: 'wall_drystone', x: -60, z: 40, yaw: 0 },
    { propId: 'wall_drystone', x: -55.2, z: 40, yaw: 0 },
    { propId: 'gate_farm', x: -63.8, z: 40, yaw: 0 },
    { propId: 'tree_oak', x: -52.5, z: 71, yaw: 0.4 },
    { propId: 'tree_dead', x: -42, z: 58, yaw: -0.747 },
    { propId: 'log_fallen', x: -22, z: 40, yaw: 0.9, blocksTanks: false },
    { propId: 'tree_poplar', x: -88, z: 44, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -88, z: 30, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -88, z: 16, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -45.5, z: 59, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -34, z: 60, yaw: 0.7, blocksShells: false },
    { propId: 'bush_round', x: -31, z: 10, yaw: 0, blocksShells: false },
    { propId: 'wall_drystone', x: 11.5, z: 40, yaw: Math.PI / 2 },
    { propId: 'wall_drystone', x: 11.5, z: 35.5, yaw: Math.PI / 2 },
    { propId: 'wall_drystone', x: 11.5, z: 31, yaw: Math.PI / 2 },
    { propId: 'ruin_roofless', x: 20, z: 22, yaw: 2.892 },
    { propId: 'wreck_truck', x: 21, z: 33, yaw: 2.042 },
    { propId: 'rock_outcrop', x: 62.5, z: 22.5, yaw: -1.435 },
    { propId: 'rock_outcrop', x: 54, z: 18, yaw: -2.642 },
    { propId: 'rock_outcrop', x: 72, z: 20, yaw: 2.842 },
    { propId: 'farmhouse_long', x: 66, z: 46, yaw: Math.PI },
    { propId: 'wall_drystone', x: 82, z: 40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 77.2, z: 40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 72.4, z: 40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 67.6, z: 40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 60, z: 40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 55.2, z: 40, yaw: Math.PI },
    { propId: 'gate_farm', x: 63.8, z: 40, yaw: Math.PI },
    { propId: 'tree_oak', x: 52, z: 70, yaw: 2.742 },
    { propId: 'tree_dead', x: 42, z: 58, yaw: 2.842 },
    { propId: 'tree_oak_ancient', x: 94, z: 93, yaw: Math.PI },
    { propId: 'log_fallen', x: 22, z: 36.5, yaw: -2.994, blocksTanks: false },
    { propId: 'tree_poplar', x: 88, z: 44, yaw: Math.PI, blocksShells: false },
    { propId: 'tree_poplar', x: 88, z: 30, yaw: Math.PI, blocksShells: false },
    { propId: 'tree_poplar', x: 88, z: 16, yaw: Math.PI, blocksShells: false },
    { propId: 'bush_round', x: 46, z: 54, yaw: Math.PI, blocksShells: false },
    { propId: 'bush_round', x: 31, z: 10, yaw: Math.PI, blocksShells: false },
    { propId: 'chapel_stone', x: 0, z: 36, yaw: 1.36 },
    { propId: 'wall_drystone', x: -9.6, z: 29, yaw: 0 },
    { propId: 'wall_drystone', x: -4.8, z: 29, yaw: 0 },
    { propId: 'wall_drystone', x: 0, z: 29, yaw: 0 },
    { propId: 'wall_drystone', x: 4.8, z: 29, yaw: 0 },
    { propId: 'wall_drystone', x: 9.6, z: 29, yaw: 0 },
    { propId: 'wall_drystone', x: -9.6, z: 42, yaw: 0 },
    { propId: 'wall_drystone', x: -4.8, z: 42, yaw: 0 },
    { propId: 'wall_drystone', x: 0, z: 42, yaw: 0 },
    { propId: 'wall_drystone', x: 4.8, z: 42, yaw: 0 },
    { propId: 'wall_drystone', x: 9.6, z: 42, yaw: 0 },
    { propId: 'wall_drystone', x: -10.5, z: -40, yaw: -Math.PI / 2 },
    { propId: 'wall_drystone', x: -10.5, z: -35.5, yaw: -Math.PI / 2 },
    { propId: 'wall_drystone', x: -11, z: -31, yaw: -1.833 },
    { propId: 'ruin_roofless', x: -20, z: -22, yaw: -0.25 },
    { propId: 'rock_outcrop', x: -63, z: -24.5, yaw: -0.65 },
    { propId: 'rock_outcrop', x: -57.5, z: -15.5, yaw: 0.5 },
    { propId: 'rock_outcrop', x: -71, z: -21.5, yaw: -0.3 },
    { propId: 'tree_oak', x: -52, z: -70, yaw: -0.4 },
    { propId: 'tree_dead', x: -42, z: -58, yaw: -0.3 },
    { propId: 'tree_oak_ancient', x: -92.5, z: -92, yaw: 0 },
    { propId: 'tree_poplar', x: -88, z: -30, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -88, z: -16, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -46, z: -54, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -34, z: -60, yaw: -0.7, blocksShells: false },
    { propId: 'bush_round', x: -31, z: -10, yaw: 0, blocksShells: false },
    { propId: 'wall_drystone', x: 10, z: -36, yaw: -1.029 },
    { propId: 'wall_drystone', x: 11, z: -32, yaw: -Math.PI / 2 },
    { propId: 'ruin_roofless', x: 20, z: -22, yaw: -2.892 },
    { propId: 'wreck_truck', x: 21, z: -33, yaw: -2.042 },
    { propId: 'rock_outcrop', x: 60.5, z: -24, yaw: -2.492 },
    { propId: 'rock_outcrop', x: 54, z: -18, yaw: 2.642 },
    { propId: 'rock_outcrop', x: 71, z: -22.5, yaw: -2.842 },
    { propId: 'farmhouse_long', x: 66, z: -46, yaw: Math.PI },
    { propId: 'wall_drystone', x: 82, z: -40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 77.2, z: -40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 72.4, z: -40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 67.6, z: -40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 60, z: -40, yaw: Math.PI },
    { propId: 'wall_drystone', x: 55.2, z: -40, yaw: Math.PI },
    { propId: 'gate_farm', x: 63.8, z: -40, yaw: Math.PI },
    { propId: 'tree_oak', x: 52, z: -70, yaw: -2.742 },
    { propId: 'tree_dead', x: 42, z: -58, yaw: -2.842 },
    { propId: 'log_fallen', x: 22, z: -40, yaw: -2.242 },
    { propId: 'tree_poplar', x: 88, z: -44, yaw: Math.PI, blocksShells: false },
    { propId: 'tree_poplar', x: 88, z: -30, yaw: Math.PI, blocksShells: false },
    { propId: 'tree_poplar', x: 88, z: -16, yaw: Math.PI, blocksShells: false },
    { propId: 'bush_round', x: 45.98, z: -54.04, yaw: Math.PI, blocksShells: false },
    { propId: 'bush_round', x: 34, z: -60, yaw: -2.442, blocksShells: false },
    { propId: 'bush_round', x: 31, z: -10, yaw: Math.PI, blocksShells: false },
    { propId: 'sandbag_wall', x: -12.04, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -8.6, z: -14, yaw: -0.262, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -5.16, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 5.16, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 8.6, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 12.04, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -9.6, z: -29, yaw: 0 },
    { propId: 'wall_drystone', x: -4.8, z: -29, yaw: 0 },
    { propId: 'wall_drystone', x: 0, z: -29, yaw: 0 },
    { propId: 'wall_drystone', x: 4.8, z: -29, yaw: 0 },
    { propId: 'wall_drystone', x: 9, z: -29.5, yaw: 0.262 },
    { propId: 'wall_drystone', x: -8.5, z: -42, yaw: 0 },
    { propId: 'wall_drystone', x: -4.8, z: -42, yaw: 0 },
    { propId: 'wall_drystone', x: 0, z: -42, yaw: 0 },
    { propId: 'wall_drystone', x: 4.5, z: -41.5, yaw: -0.262 },
    { propId: 'bush_round', x: 34, z: 60, yaw: 2.442, blocksShells: false },
    { propId: 'house_shelled', x: -3, z: -34.5, yaw: 1.581 },
    { propId: 'tree_willow', x: 6, z: -35, yaw: 0 },
    { propId: 'wreck_car', x: 7.5, z: -38.5, yaw: -1.844 },
    { propId: 'tree_oak_ancient', x: 78.5, z: 83.5, yaw: Math.PI / 4 },
    { propId: 'rock_outcrop', x: -73.5, z: -2, yaw: -2.928 },
    { propId: 'rock_outcrop', x: 86, z: 2.5, yaw: 2.88 },
    { propId: 'rock_outcrop', x: -61, z: -26.5, yaw: -0.65 },
    { propId: 'rock_outcrop', x: 64, z: 16.5, yaw: 2.842 },
    { propId: 'sandbag_wall', x: 68.5, z: 24.5, yaw: -2.618 },
    { propId: 'sandbag_wall', x: 64, z: -17, yaw: -2.618 },
    { propId: 'sandbag_wall', x: 66.5, z: -19, yaw: -2.618 },
    { propId: 'wreck_truck', x: 63, z: -19.5, yaw: Math.PI / 4 },
    { propId: 'bush_round', x: -45.5, z: 56, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -46, z: 52.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -58, z: 43.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -58, z: 45.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -58, z: 49, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -74, z: 43, yaw: 0, blocksShells: false },
    { propId: 'car_parked', x: -21.5, z: -32.5, yaw: 1.833 },
    { propId: 'hesco_barrier', x: -7.5, z: 89.5, yaw: 0.524 },
    { propId: 'hesco_barrier', x: 7.51, z: 89.55, yaw: -0.524 },
    { propId: 'command_post', x: 0.01, z: 86.05, yaw: 0 },
    { propId: 'wall_drystone', x: -53.74, z: 42.73, yaw: -1.833 },
    { propId: 'wall_drystone', x: -55.19, z: 47.23, yaw: -1.833 },
    { propId: 'bush_round', x: -56, z: 43.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -52.5, z: 40, yaw: 2.094, blocksShells: false },
    { propId: 'bush_bramble', x: -74.5, z: 46, yaw: 0 },
    { propId: 'hesco_barrier', x: -30, z: 67.5, yaw: -Math.PI / 2 },
    { propId: 'hesco_barrier', x: -30, z: 72.5, yaw: -1.419 },
    { propId: 'hesco_barrier', x: -30, z: 77.5, yaw: -1.54 },
    { propId: 'hesco_barrier', x: -30, z: 82.5, yaw: -1.46 },
    { propId: 'hesco_barrier', x: -29.5, z: 87.5, yaw: -Math.PI / 2 },
    { propId: 'hesco_barrier', x: 30.5, z: 68, yaw: -1.669 },
    { propId: 'hesco_barrier', x: 30, z: 73, yaw: -1.627 },
    { propId: 'hesco_barrier', x: 30, z: 78, yaw: -1.475 },
    { propId: 'hesco_barrier', x: 30.5, z: 83, yaw: -1.517 },
    { propId: 'hesco_barrier', x: 31, z: 88, yaw: -1.621 },
    { propId: 'car_parked', x: -66.67, z: 49.83, yaw: 0 },
    { propId: 'farmhouse_long', x: -66, z: -46, yaw: 0.014 },
    { propId: 'wall_drystone', x: -79, z: -42, yaw: -1.309 },
    { propId: 'wall_drystone', x: -77, z: -40, yaw: 0 },
    { propId: 'wall_drystone', x: -72.4, z: -40, yaw: 0 },
    { propId: 'wall_drystone', x: -67.6, z: -40, yaw: 0 },
    { propId: 'wall_drystone', x: -60, z: -40, yaw: 0 },
    { propId: 'wall_drystone', x: -55.2, z: -40, yaw: 0 },
    { propId: 'gate_farm', x: -63.8, z: -40, yaw: 0 },
    { propId: 'bush_round', x: -58, z: -43.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -58, z: -45.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -58, z: -49, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -75, z: -43, yaw: 0, blocksShells: false },
    { propId: 'wall_drystone', x: -53.74, z: -42.73, yaw: 1.833 },
    { propId: 'wall_drystone', x: -55.19, z: -47.23, yaw: 1.833 },
    { propId: 'bush_round', x: -56, z: -42.5, yaw: 0, blocksShells: false },
    { propId: 'bush_round', x: -52.5, z: -40, yaw: -2.094, blocksShells: false },
    { propId: 'bush_bramble', x: -74.5, z: -46, yaw: 0 },
    { propId: 'car_parked', x: -66.67, z: -49.83, yaw: 0 },
    { propId: 'bush_bramble', x: -72.52, z: -47.96, yaw: 0 },
    { propId: 'bush_round', x: 33, z: -9.5, yaw: -2.88, blocksShells: false },
    { propId: 'bush_round', x: 30, z: -11, yaw: -2.094, blocksShells: false },
    { propId: 'bush_round', x: 31, z: -7.5, yaw: -Math.PI / 2, blocksShells: false },
    { propId: 'bush_round', x: 29, z: -9.5, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'bush_bramble', x: 30.53, z: -9.55, yaw: 0 },
    { propId: 'bush_round', x: -33, z: 9.5, yaw: 0.262, blocksShells: false },
    { propId: 'bush_round', x: -30, z: 11, yaw: 1.047, blocksShells: false },
    { propId: 'bush_round', x: -31, z: 7.5, yaw: Math.PI / 2, blocksShells: false },
    { propId: 'bush_round', x: -29, z: 9.5, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'bush_bramble', x: -30.5, z: 9.5, yaw: Math.PI },
    { propId: 'rock_outcrop', x: -60.5, z: 24, yaw: 0.65 },
    { propId: 'rock_outcrop', x: -54, z: 18, yaw: -0.5 },
    { propId: 'rock_outcrop', x: -71, z: 22.5, yaw: 0.3 },
    { propId: 'sandbag_wall', x: -66, z: 17, yaw: Math.PI / 4 },
    { propId: 'water_cistern', x: -63.5, z: 19, yaw: 0 },
    { propId: 'sandbag_wall', x: -63, z: 16, yaw: -0.262 },
    { propId: 'tree_burnt', x: 20.5, z: -18, yaw: 0 },
    { propId: 'concrete_barrier', x: -12, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: -6, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: 4.5, z: 13.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: 6.5, z: 13.5, yaw: 1.309, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: 8.5, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: 18.5, z: 2, yaw: Math.PI / 2 },
    { propId: 'concrete_barrier', x: -8, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'concrete_barrier', x: 10.5, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'tree_oak_ancient', x: 94, z: 67.5, yaw: Math.PI },
    { propId: 'tree_oak', x: 86, z: 71, yaw: 2.742 },
    { propId: 'tree_birch_clump', x: 91, z: 58, yaw: 0 },
    { propId: 'tree_oak', x: 96.5, z: 83.5, yaw: 0.524 },
    { propId: 'stump_cut', x: 87.5, z: 75.5, yaw: 0 },
    { propId: 'tree_willow', x: 87.5, z: 88.5, yaw: 1.309 },
    { propId: 'tree_orchard', x: 76, z: 43.5, yaw: 0, blocksTanks: true },
    { propId: 'tree_orchard', x: -74.5, z: 50, yaw: 0 },
    { propId: 'tree_orchard', x: 30, z: 43, yaw: 0, blocksTanks: true },
    { propId: 'tree_orchard', x: -76.5, z: -50, yaw: Math.PI / 4, blocksTanks: true },
    { propId: 'tree_willow', x: 89, z: 82.5, yaw: -1.047 },
    { propId: 'tree_birch_clump', x: 86.5, z: 63.5, yaw: 1.047 },
    { propId: 'stump_cut', x: 91, z: 74.5, yaw: -0.262 },
    { propId: 'stump_cut', x: 95, z: 77, yaw: -0.262 },
    { propId: 'tree_oak', x: 78.5, z: 93.5, yaw: 2.742 },
    { propId: 'tree_oak_ancient', x: -94, z: 93, yaw: 0 },
    { propId: 'tree_oak_ancient', x: -94, z: 67.5, yaw: 0 },
    { propId: 'tree_oak', x: -86, z: 71, yaw: 0.4 },
    { propId: 'tree_birch_clump', x: -91, z: 58, yaw: Math.PI },
    { propId: 'tree_oak', x: -96.5, z: 83.5, yaw: 2.618 },
    { propId: 'stump_cut', x: -87.5, z: 75.5, yaw: Math.PI },
    { propId: 'tree_willow', x: -87.5, z: 88.5, yaw: 1.833 },
    { propId: 'tree_willow', x: -89, z: 82.5, yaw: -2.094 },
    { propId: 'tree_birch_clump', x: -86.5, z: 63.5, yaw: 2.094 },
    { propId: 'stump_cut', x: -91, z: 74.5, yaw: -2.88 },
    { propId: 'stump_cut', x: -95, z: 77, yaw: -2.88 },
    { propId: 'tree_oak', x: -78.5, z: 93.5, yaw: 0.4 },
    { propId: 'tree_oak_ancient', x: 94, z: -93, yaw: Math.PI },
    { propId: 'tree_oak_ancient', x: 68, z: -96, yaw: Math.PI },
    { propId: 'tree_oak', x: 86, z: -71, yaw: -2.742 },
    { propId: 'tree_birch_clump', x: -81.5, z: -93.5, yaw: 0 },
    { propId: 'tree_oak', x: 96.5, z: -83.5, yaw: -0.524 },
    { propId: 'stump_cut', x: 87.5, z: -75.5, yaw: 0 },
    { propId: 'tree_willow', x: 87.5, z: -88.5, yaw: -1.309 },
    { propId: 'tree_willow', x: 89, z: -82.5, yaw: 1.047 },
    { propId: 'tree_birch_clump', x: 86.5, z: -63.5, yaw: -1.047, blocksShells: false },
    { propId: 'stump_cut', x: 91, z: -74.5, yaw: 0.262 },
    { propId: 'stump_cut', x: 95, z: -77, yaw: 0.262 },
    { propId: 'tree_oak', x: 95.5, z: -71.5, yaw: -2.742 },
    { propId: 'tree_oak_ancient', x: 68.5, z: 95, yaw: Math.PI },
    { propId: 'tree_oak', x: -68.5, z: 96.5, yaw: 1.709 },
    { propId: 'tree_oak', x: 95.5, z: 71.5, yaw: 2.742 },
    { propId: 'tree_oak_ancient', x: -68, z: -96, yaw: 0 },
    { propId: 'tree_oak', x: -86, z: -71, yaw: -0.4 },
    { propId: 'tree_oak', x: -96.5, z: -83.5, yaw: -2.618 },
    { propId: 'tree_oak', x: -95.5, z: -71.5, yaw: -0.4 },
    { propId: 'tree_oak', x: -50.5, z: 69, yaw: 0.4 },
    { propId: 'gate_farm', x: -11.5, z: 35.5, yaw: Math.PI / 2 },
    { propId: 'tree_orchard', x: -9, z: 31.5, yaw: 0 },
    { propId: 'tree_oak', x: 8, z: 39, yaw: 0 },
    { propId: 'tree_willow', x: -9, z: 39, yaw: 0 },
    { propId: 'clip_wall_move', x: 60.5, z: -16, yaw: 0.148 },
    { propId: 'clip_wall_move', x: 59.5, z: -43, yaw: -1.878 },
    { propId: 'clip_wall_move', x: 72.5, z: -43, yaw: 1.565 },
    { propId: 'clip_wall_move', x: -58.5, z: 48, yaw: 2.834 },
    { propId: 'clip_wall_move', x: 59.5, z: 43, yaw: 1.878 },
    { propId: 'clip_wall_move', x: 72.5, z: 43, yaw: -1.565 },
    { propId: 'clip_wall_move', x: -59.5, z: -43, yaw: -1.263 },
    { propId: 'clip_wall_move', x: -72.5, z: -43, yaw: 1.576 },
    { propId: 'clip_wall_move', x: -60.5, z: 16, yaw: -2.994 },
    { propId: 'sandbag_wall', x: 16.72, z: -69.5, yaw: Math.PI, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: -69.5, yaw: Math.PI, blocksShells: false },
    { propId: 'dragon_teeth', x: 11.33, z: 65.02, yaw: -0.18, blocksShells: false },
    { propId: 'dragon_teeth', x: 16, z: 65.5, yaw: 0, blocksShells: false },
    { propId: 'sandbag_wall', x: -16.72, z: 69.5, yaw: 0, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: 69.5, yaw: 0, blocksShells: false },
    { propId: 'sandbag_wall', x: 16.72, z: 69.5, yaw: Math.PI, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: 69.5, yaw: Math.PI, blocksShells: false },
    { propId: 'dragon_teeth', x: -11.33, z: -65.02, yaw: 2.962, blocksShells: false },
    { propId: 'dragon_teeth', x: -16, z: -65.5, yaw: Math.PI, blocksShells: false },
    { propId: 'gate_pylon', x: 7.5, z: -65.5, yaw: Math.PI },
    { propId: 'gate_pylon', x: -7.5, z: -65.5, yaw: Math.PI },
    { propId: 'dragon_teeth', x: 11.33, z: -65.02, yaw: 0.18, blocksShells: false },
    { propId: 'dragon_teeth', x: 16, z: -65.5, yaw: 0, blocksShells: false },
    { propId: 'concrete_barrier', x: -10, z: 14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall_move', x: -9, z: 13.9, yaw: 0, clipSize: { halfX: 4, halfZ: 0.25, height: 3 } },
    { propId: 'clip_wall_move', x: 9.46, z: 14.08, yaw: 0, clipSize: { halfX: 1.97, halfZ: 0.2, height: 3 } },
    { propId: 'clip_wall_move', x: 6.57, z: 13.65, yaw: -0.257, clipSize: { halfX: 0.96, halfZ: 0.2, height: 3 } },
    { propId: 'clip_wall_move', x: 4.58, z: 13.66, yaw: 0, clipSize: { halfX: 0.79, halfZ: 0.2, height: 3 } },
    { propId: 'clip_wall_move', x: 22.02, z: 36.58, yaw: 0.088, clipSize: { halfX: 1.24, halfZ: 0.3, height: 3 } },
    { propId: 'clip_wall_move', x: -21.79, z: 40.02, yaw: 0.91, clipSize: { halfX: 1.17, halfZ: 0.3, height: 3 } },
    { propId: 'clip_wall_move', x: 21.98, z: -40.06, yaw: 0.959, clipSize: { halfX: 1.04, halfZ: 0.3, height: 3 } },
    { propId: 'clip_wall_move', x: 8.48, z: -14.09, yaw: 0, clipSize: { halfX: 5.18, halfZ: 0.5, height: 3 } },
    { propId: 'clip_wall_move', x: -8.52, z: -14.09, yaw: 0, clipSize: { halfX: 5.18, halfZ: 0.63, height: 3 } },
    { propId: 'field_gun', x: -9, z: 85, yaw: Math.PI, blocksTanks: false },
    { propId: 'field_gun', x: 9, z: 85, yaw: Math.PI, blocksTanks: false },
    { propId: 'field_gun', x: -9, z: -85, yaw: 0 },
    { propId: 'field_gun', x: 9, z: -85, yaw: 0 },
    { propId: 'clip_wall_move', x: -9, z: 85, yaw: -0.021, clipSize: { halfX: 0.5, halfZ: 2.25, height: 3 } },
    { propId: 'clip_wall_move', x: 9, z: 85, yaw: -3.12, clipSize: { halfX: 0.5, halfZ: 2.25, height: 3 } },
    { propId: 'clip_wall_move', x: 9, z: -85, yaw: 3.12, clipSize: { halfX: 0.5, halfZ: 2.25, height: 3 } },
    { propId: 'clip_wall_move', x: -9, z: -85, yaw: 3.12, clipSize: { halfX: 0.5, halfZ: 2.25, height: 3 } },
  ],
  decals: [
    { kind: 'rect', x: -30, z: -77.5, yaw: 0, scaleX: 0.94, scaleZ: 13 },
    { kind: 'rect', x: 63, z: -19.5, yaw: Math.PI / 4, scaleX: 5.4, scaleZ: 4.4 },
    { kind: 'rect', x: 7.5, z: -38.5, yaw: -1.844, scaleX: 2.58, scaleZ: 1.35 },
    { kind: 'rect', x: 21, z: -33, yaw: -2.042, scaleX: 4.16, scaleZ: 1.95 },
    { kind: 'rect', x: -21.5, z: -32.5, yaw: 1.833, scaleX: 3.06, scaleZ: 1.42 },
    { kind: 'rect', x: 21, z: 33, yaw: 2.042, scaleX: 4.16, scaleZ: 1.95 },
    { kind: 'rect', x: -21, z: 33, yaw: 1.1, scaleX: 4.16, scaleZ: 1.95 },
    { kind: 'rect', x: -66.67, z: 49.83, yaw: 0, scaleX: 3.06, scaleZ: 1.42 },
    { kind: 'rect', x: 9, z: 85, yaw: Math.PI, scaleX: 1.8, scaleZ: 2.34, strength: 0.3 },
    { kind: 'rect', x: -9, z: 85, yaw: Math.PI, scaleX: 1.8, scaleZ: 1.97, strength: 0.33 },
    { kind: 'rect', x: -9, z: -85, yaw: 0, scaleX: 1.8, scaleZ: 2.07, strength: 0.33 },
    { kind: 'rect', x: 9, z: -85, yaw: 0, scaleX: 1.8, scaleZ: 1.87, strength: 0.33 },
    { kind: 'puddle', x: 9.5, z: -39, yaw: 0, scaleX: 0.72, scaleZ: 0.95 },
    { kind: 'puddle', x: -28.5, z: 1, yaw: 0, scaleX: 0.72, scaleZ: 0.95 },
    { kind: 'tracks_light', x: -21.52, z: 34.07, yaw: -0.482, scaleX: 1.6, scaleZ: 3.6, strength: 0.4 },
  ],
};

// ─── 2. Slag Foundry ─────────────────────────────────────────────────────────

export const MAP_FOUNDRY: MapDefinition = {
  id: 'foundry',
  name: 'Slag Foundry',
  blurb: 'Warehouse alleys and ten-metre corners. Nothing here shoots far.',
  halfExtent: { x: 100, z: 100 },
  bases: {
      blue: {
      team: 'blue',
      x: 78,
      z: 0,
      facing: -Math.PI / 2,
      spawns: [
        { x: 80, z: -16 },
        { x: 80, z: 0 },
        { x: 80, z: 16 },
        { x: 73, z: -8 },
        { x: 73, z: 8 },
        { x: 86, z: -25 },
        { x: 86, z: 25 },
      ],
    },
      red: {
      team: 'red',
      x: -78,
      z: 0,
      facing: Math.PI / 2,
      spawns: [
        { x: -80, z: 16 },
        { x: -80, z: 0 },
        { x: -80, z: -16 },
        { x: -73, z: 8 },
        { x: -73, z: -8 },
        { x: -86, z: 25 },
        { x: -86, z: -25 },
      ],
    },
    },
  props: [
    { propId: 'hesco_barrier', x: -91, z: 27.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: 22.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: 17.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: 12.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: 7.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: 2.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -2.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -7.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -12.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -17.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -22.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -91, z: -27.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -73, z: -30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -78, z: -30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -83, z: -30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -88, z: -30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -73, z: 30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -78, z: 30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -83, z: 30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -88, z: 30, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'clip_wall_move', x: -91, z: 0, yaw: Math.PI / 2, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -81, z: -30, yaw: Math.PI, clipSize: { halfX: 10.75, halfZ: 1, height: 2.03 } },
    { propId: 'clip_wall_move', x: -80.5, z: 30, yaw: Math.PI, clipSize: { halfX: 9.75, halfZ: 1, height: 2.03 } },
    { propId: 'dragon_teeth', x: -67, z: 16.4, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -67, z: 11.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -67, z: -11.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -67, z: -16.4, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -67, z: 7.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -67, z: -7.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: -87, z: 0, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -69.5, z: 16.72, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -69.5, z: 13.28, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -69.5, z: -13.28, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -69.5, z: -16.72, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'hesco_barrier', x: 91, z: -27.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: -22.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: -17.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: -12.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: -7.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: -2.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 2.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 7.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 12.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 17.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 22.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 91, z: 27.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 73, z: 30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 78, z: 30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 83, z: 30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 88, z: 30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 73, z: -30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 78, z: -30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 83, z: -30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 88, z: -30, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'clip_wall_move', x: 91, z: 0, yaw: -Math.PI / 2, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 80.5, z: 30, yaw: 0, clipSize: { halfX: 9.75, halfZ: 1, height: 2.03 } },
    { propId: 'clip_wall_move', x: 80.5, z: -30, yaw: 0, clipSize: { halfX: 9.75, halfZ: 1, height: 2.03 } },
    { propId: 'dragon_teeth', x: 67, z: -16.4, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 67, z: -11.6, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 67, z: 11.6, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 67, z: 16.4, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 67, z: -7.6, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 67, z: 7.6, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 87, z: 0, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 69.5, z: -16.72, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 69.5, z: -13.28, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 69.5, z: 13.28, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 69.5, z: 16.72, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -45, z: 19, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'boiler_house', x: -30, z: 72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -48, z: 84, yaw: 0.4, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: 45, z: 19, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: 15, z: 19, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'boiler_house', x: 30, z: 72, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 48, z: 84, yaw: 2.742, blocksTanks: false, blocksShells: false },
    { propId: 'fence_chainlink', x: 56, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 51, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 46, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 41, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 36, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 31, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 26, z: 34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'utility_pole', x: 28.68, z: 37.95, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 57.24, z: 39.08, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'industrial_unit', x: 0, z: 72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -20, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -15, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -10, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -5, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 0, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 5, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 10, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 15, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 20, z: 60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -45, z: -19, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -15, z: -19, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'boiler_house', x: -30, z: -72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -48, z: -84, yaw: -0.4, blocksTanks: false, blocksShells: false },
    { propId: 'fence_chainlink', x: -68.03, z: -35.21, yaw: 1.794, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -60.04, z: -35.36, yaw: -0.628, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -56, z: -34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -51, z: -34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -46, z: -34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -41, z: -34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -36, z: -34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -31.01, z: -34.06, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -26.03, z: -33.98, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'utility_pole', x: -28, z: -37, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -57, z: -39, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: 45, z: -19, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: 13, z: -14.5, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'boiler_house', x: 30, z: -72, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 48, z: -84, yaw: -2.742, blocksTanks: false, blocksShells: false },
    { propId: 'industrial_unit', x: 0, z: -72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -20, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -15, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -10, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: -5, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 0, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 5, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 10, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 15, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hoarding_corrugated', x: 20, z: -60, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'pipe_rack', x: -10, z: -8, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_tank_horizontal', x: -22, z: 8, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -42, z: -8, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'slab_stack', x: -44, z: 10, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'loading_ramp', x: -20.56, z: -10.67, yaw: 0, blocksTanks: false },
    { propId: 'wreck_truck', x: -57.5, z: -22.5, yaw: 0.6, blocksTanks: false, blocksShells: false },
    { propId: 'pipe_rack', x: 10, z: 8, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_tank_horizontal', x: 22, z: -8, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: 42, z: 8, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'slab_stack', x: 44, z: -10, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'loading_ramp', x: 20.78, z: 10.84, yaw: Math.PI },
    { propId: 'wreck_truck', x: 57.35, z: 22.06, yaw: -2.542, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall_move', x: -67, z: 16.4, yaw: Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -67, z: 11.6, yaw: Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -67, z: -11.6, yaw: Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -67, z: -16.4, yaw: Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: -67, z: 7.6, yaw: Math.PI / 2, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: -67, z: -7.6, yaw: Math.PI / 2, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: -87, z: 0, yaw: Math.PI / 2, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: -69.5, z: 16.72, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -69.5, z: 13.28, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -69.5, z: -13.28, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -69.5, z: -16.72, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 67, z: -16.4, yaw: -Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 67, z: -11.6, yaw: -Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 67, z: 11.6, yaw: -Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 67, z: 16.4, yaw: -Math.PI / 2, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: 67, z: -7.6, yaw: -Math.PI / 2, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 67, z: 7.6, yaw: -Math.PI / 2, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 87, z: 0, yaw: -Math.PI / 2, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: 69.5, z: -16.72, yaw: -Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 69.5, z: -13.28, yaw: -Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 69.5, z: 13.28, yaw: -Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 69.5, z: 16.72, yaw: -Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall', x: -45, z: 19, yaw: 0, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -30.67, z: 72.18, yaw: 0, clipSize: { halfX: 5.1, halfZ: 5.55, height: 14.4 } },
    { propId: 'clip_wall_move', x: -48, z: 84, yaw: 0.4, clipSize: { halfX: 2.5, halfZ: 2.75, height: 2.27 } },
    { propId: 'clip_wall', x: 45, z: 19, yaw: Math.PI, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: 15, z: 19, yaw: Math.PI, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall_move', x: 48, z: 84, yaw: 2.742, clipSize: { halfX: 2.75, halfZ: 2.5, height: 2.27 } },
    { propId: 'clip_wall', x: 0, z: 72, yaw: 0, clipSize: { halfX: 8.29, halfZ: 6.3, height: 10.13 } },
    { propId: 'clip_wall', x: 0, z: 60, yaw: 0, clipSize: { halfX: 22.08, halfZ: 0.31, height: 3.13 } },
    { propId: 'clip_wall', x: -45, z: -19, yaw: 0, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -15, z: -19, yaw: 0, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall_move', x: -48, z: -84, yaw: -0.4, clipSize: { halfX: 2.75, halfZ: 2.75, height: 2.27 } },
    { propId: 'clip_wall_move', x: -68.03, z: -35, yaw: 1.79, clipSize: { halfX: 2.25, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: -59.91, z: -35.44, yaw: -0.628, clipSize: { halfX: 2.47, halfZ: 0.38, height: 3.09 } },
    { propId: 'clip_wall_move', x: -40.77, z: -34.01, yaw: 0, clipSize: { halfX: 16.98, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall', x: -28, z: -37, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -57.23, z: -39.16, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 45, z: -19, yaw: Math.PI, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: 13, z: -14.5, yaw: -Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall_move', x: 48, z: -84, yaw: -2.742, clipSize: { halfX: 2.75, halfZ: 2.75, height: 2.27 } },
    { propId: 'clip_wall', x: 0, z: -72, yaw: 0, clipSize: { halfX: 8.29, halfZ: 6.3, height: 10.13 } },
    { propId: 'clip_wall', x: -10, z: -8, yaw: 0, clipSize: { halfX: 5.31, halfZ: 1.98, height: 3.69 } },
    { propId: 'clip_wall', x: -22, z: 8, yaw: Math.PI / 2, clipSize: { halfX: 2.84, halfZ: 1.98, height: 4.9 } },
    { propId: 'clip_wall', x: -42, z: -8, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -44, z: 10, yaw: 0, clipSize: { halfX: 1.25, halfZ: 1.25, height: 2.47 } },
    { propId: 'clip_wall', x: -57.5, z: -22.5, yaw: 0.6, clipSize: { halfX: 2.49, halfZ: 1.17, height: 2.33 } },
    { propId: 'clip_wall', x: 10, z: 8, yaw: Math.PI, clipSize: { halfX: 5.31, halfZ: 1.98, height: 3.69 } },
    { propId: 'clip_wall', x: 22, z: -8, yaw: -Math.PI / 2, clipSize: { halfX: 2.84, halfZ: 1.98, height: 4.9 } },
    { propId: 'clip_wall', x: 42, z: 8, yaw: -Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: 44, z: -10, yaw: Math.PI, clipSize: { halfX: 1.5, halfZ: 1.25, height: 2.47 } },
    { propId: 'clip_wall', x: 57.39, z: 22.06, yaw: -2.542, clipSize: { halfX: 2.49, halfZ: 1.17, height: 2.33 } },
    { propId: 'clip_wall_move', x: 20.86, z: 11.09, yaw: 0, clipSize: { halfX: 2.55, halfZ: 2.43, height: 3 } },
    { propId: 'clip_wall_move', x: -20.56, z: -10.95, yaw: 0, clipSize: { halfX: 2.55, halfZ: 2.43, height: 3 } },
    { propId: 'clip_wall_move', x: -24.65, z: 73.79, yaw: 0, clipSize: { halfX: 1.12, halfZ: 2.85, height: 3 } },
    { propId: 'clip_wall', x: 0, z: -60, yaw: 0, clipSize: { halfX: 22.08, halfZ: 0.31, height: 3.13 } },
    { propId: 'clip_wall_move', x: -24.73, z: -69.8, yaw: 0, clipSize: { halfX: 1.12, halfZ: 2.85, height: 3 } },
    { propId: 'clip_wall_move', x: 24.65, z: -73.79, yaw: Math.PI, clipSize: { halfX: 1.12, halfZ: 2.85, height: 3 } },
    { propId: 'clip_wall_move', x: 24.69, z: 69.99, yaw: Math.PI, clipSize: { halfX: 1.12, halfZ: 2.85, height: 3 } },
    { propId: 'clip_wall', x: -30.67, z: -72.18, yaw: 0, clipSize: { halfX: 5.1, halfZ: 5.55, height: 14.4 } },
    { propId: 'clip_wall', x: 30.67, z: -72.18, yaw: Math.PI, clipSize: { halfX: 5.1, halfZ: 5.55, height: 14.4 } },
    { propId: 'clip_wall', x: 30.67, z: 72.18, yaw: Math.PI, clipSize: { halfX: 5.1, halfZ: 5.55, height: 14.4 } },
    { propId: 'fence_chainlink', x: -68.03, z: 35.21, yaw: -1.794, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -60.04, z: 35.36, yaw: 0.628, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -56, z: 34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -51, z: 34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -46, z: 34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -41, z: 34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -36, z: 34, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -31.01, z: 34.06, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: -26.03, z: 33.98, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 68.03, z: -35.21, yaw: 1.348, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 60.04, z: -35.36, yaw: -2.513, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 56, z: -34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 51, z: -34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 46, z: -34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 41, z: -34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 36, z: -34, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 31.01, z: -34.06, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'fence_chainlink', x: 26.03, z: -33.98, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'clip_wall_move', x: -40.5, z: 34, yaw: 0, clipSize: { halfX: 16.98, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: 40.5, z: -34, yaw: Math.PI, clipSize: { halfX: 16.98, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: -60.08, z: 35.28, yaw: 0.628, clipSize: { halfX: 2.52, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: 60.09, z: -35.31, yaw: -2.513, clipSize: { halfX: 2.43, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: 59.99, z: 35.45, yaw: 2.513, clipSize: { halfX: 2.59, halfZ: 0.44, height: 3.09 } },
    { propId: 'fence_chainlink', x: 68.03, z: 35.21, yaw: -1.348, blocksTanks: false, blocksShells: true },
    { propId: 'clip_wall_move', x: -68, z: 35, yaw: -1.79, clipSize: { halfX: 2.25, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: 68, z: -35, yaw: 1.352, clipSize: { halfX: 2.25, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall_move', x: 68.03, z: 35, yaw: -1.352, clipSize: { halfX: 2.25, halfZ: 0.44, height: 3.09 } },
    { propId: 'clip_wall', x: -57, z: 39, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 57.23, z: -39.16, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 57.23, z: 39.16, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 28.69, z: -38.04, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 28.69, z: 38.04, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'fence_chainlink', x: 60.11, z: 35.54, yaw: 2.513, blocksTanks: false, blocksShells: true },
    { propId: 'utility_pole', x: -28, z: 39, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall', x: -28, z: 39, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'utility_pole', x: -57, z: 39, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 28.69, z: -38.01, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 57.11, z: -39.14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall_move', x: 40.77, z: 34.01, yaw: Math.PI, clipSize: { halfX: 16.98, halfZ: 0.44, height: 3.09 } },
    { propId: 'wreck_car', x: -13, z: -4.5, yaw: 1.901, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall', x: -13, z: -4.5, yaw: 1.898, clipSize: { halfX: 1.54, halfZ: 0.81, height: 1.49 } },
    { propId: 'clip_wall', x: 13, z: 4.5, yaw: -1.244, clipSize: { halfX: 1.54, halfZ: 0.81, height: 1.49 } },
    { propId: 'wreck_car', x: 13, z: 4.5, yaw: -1.241, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -12.5, z: 13.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall', x: -12.5, z: 13.5, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'brick_smokestack', x: -73.5, z: 72.5, yaw: 0.611, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall', x: -73.5, z: 72.5, yaw: 0.611, clipSize: { halfX: 3.9, halfZ: 5.02, height: 18 } },
    { propId: 'brick_smokestack', x: 73.5, z: -72.5, yaw: -2.531, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall', x: 73.5, z: -72.5, yaw: -2.531, clipSize: { halfX: 3.9, halfZ: 5.02, height: 18 } },
  ],
  decals: [
    { kind: 'rect', x: 57.35, z: 22.06, yaw: -2.542, scaleX: 4.16, scaleZ: 1.95 },
    { kind: 'rect', x: -57.5, z: -22.5, yaw: 0.6, scaleX: 4.16, scaleZ: 1.95 },
    { kind: 'rect', x: 10, z: 8, yaw: Math.PI, scaleX: 8.85, scaleZ: 3.3 },
    { kind: 'rect', x: -10, z: -8, yaw: 0, scaleX: 8.85, scaleZ: 3.3 },
    { kind: 'rect', x: 42, z: 8, yaw: -Math.PI / 2, scaleX: 4.57, scaleZ: 2.24 },
    { kind: 'rect', x: -42, z: -8, yaw: Math.PI / 2, scaleX: 4.57, scaleZ: 2.24 },
  ],
};

// ─── 3. Harrow Flats ─────────────────────────────────────────────────────────

const FLATS_BASES = bases({ x: 0, z: 188, facing: SOUTH }, { x: 0, z: -188, facing: NORTH });

/**
 * One farm plan, built twice — and the substitution table is the design.
 *
 * The east lane is the west lane's own placement list, turned half a revolution
 * and passed through this map. So the two farms are laid out identically because
 * they are the *same layout*: same ranges round the same yard, same field
 * boundaries at the same intervals, same orchard on the same grid. What differs
 * is what each thing is made of, which is exactly the claim Harrow Flats makes —
 * *one farm is being worked and the other was burnt when the fighting came down
 * the east lane.*
 *
 * Written as a table rather than as two hand-typed districts for the reason
 * `mirrorZ` exists: a parish balanced by hand is a parish that is subtly
 * unbalanced. Two properties fall out of it for free and both are asserted:
 *
 * - **Every pair preserves `blocksTanks`.** A building becomes a smaller
 *   building, a permeable thing becomes a permeable thing. So the two lanes carry
 *   the *same number* of hard-cover pieces however the table is edited, which is
 *   the row the §14 ledger holds to ±2 and the one a hand-built pair would break
 *   first.
 * - **The burnt lane conceals less at the same footprint count.** Every
 *   substitute is smaller than what it replaces — a gutted farmhouse is a shorter
 *   building than a standing one — so the east lane sees further and hides less
 *   without owning one extra thing. That is the map's asymmetry, and it is a
 *   consequence of the table rather than a decision taken twice.
 *
 * `wall_drystone` is deliberately absent: a drystone wall does not burn, so the
 * yard walls are identical on both farms and are the one thing that tells you the
 * two steadings were the same building group.
 */
const FLATS_BURNT: Partial<Record<PropId, PropId>> = {
  // The steading, range for range.
  farmhouse_long: 'terrace_shelled',
  barn_wooden: 'ruin_roofless',
  grain_store: 'ruin_corner',
  cottage_stone: 'house_shelled',
  // The hedges were not cut for two years, and shells went through them.
  hedgerow: 'hedgerow_wild',
  // A wood that burnt is still a wood: `tree_dead` is solid at the trunk
  // exactly as the living oak is (2.6 × 2.14 against 2.62 × 2.69), so the
  // burnt lane keeps the same obstacles in the same places.
  tree_oak: 'tree_dead',
  // A burnt fruit tree, though, must NOT become a `tree_dead`: the orchard
  // tree is drive-through and the dead tree is solid, so the old mapping would
  // hand the burnt lane a grove of hard cover the living lane does not have —
  // exactly the imbalance the §14 fairness ledger measures. Stumps instead:
  // still drive-over, and a felled orchard is what a shelled farm has anyway.
  tree_orchard: 'stump_cut',
  // The yard's own furniture. A haystack and a burnt-out lorry are the same
  // bargain — F/T, building-scale, drive through it and shelter behind it — so
  // the burnt yard loses none of its cover, only its harvest.
  haystack: 'wreck_truck',
  // Where the tractor stood there is a hole. `crater_small` since the big
  // square-plinth crater was cut from the catalogue (2026-08-19).
  tractor_farm: 'crater_small',
};

/**
 * The east lane: the west lane, turned about the origin and burnt.
 *
 * `mirrorPoint` rather than `mirrorX`, and that is the one place this layout
 * departs from §8 of `docs/maps-design.md` — see the note on `MAP_FLATS`.
 *
 * `swap` overrides the table for one caller: the felled half of the orchard,
 * which becomes stumps rather than standing dead trees.
 */
function burntLane(props: PlacedProp[], swap: Partial<Record<PropId, PropId>> = {}): PlacedProp[] {
  return mirrorPoint(props).map((p) => ({
    ...p,
    propId: swap[p.propId] ?? FLATS_BURNT[p.propId] ?? p.propId,
  }));
}

/**
 * Harrow Farm: the steading at the head of the lane, and its home fields.
 *
 * Six ranges round one yard, which is what a farm of this size actually has —
 * the long house facing the yard, two barns across the head of it, the grain
 * store on the track side, and the pair of farm cottages down at the lane end.
 * Every gap between two solid ranges is over ten metres, measured corner to
 * corner rather than face to face, because a bot rounding one enters the next off
 * centreline.
 */
const FLATS_STEADING: PlacedProp[] = [
  { propId: 'farmhouse_long', x: -114, z: 138, yaw: EAST },
  { propId: 'barn_wooden', x: -100, z: 160, yaw: NORTH },
  { propId: 'barn_wooden', x: -78, z: 158, yaw: NORTH },
  { propId: 'grain_store', x: -98, z: 122, yaw: NORTH },
  { propId: 'cottage_stone', x: -124, z: 116, yaw: EAST },
  { propId: 'cottage_stone', x: -124, z: 96, yaw: EAST },
  // The yard wall: F/T, so it encloses the yard for a gunner and never for a
  // driver. Two runs meeting at a corner, open to the south where the track
  // comes in.
  ...run('wall_drystone', -112, 148, NORTH, 5),
  ...run('wall_drystone', -90, 148, EAST, 4),
  // What makes a yard read as a working yard, at zero navigation cost. The
  // haystacks stop rounds; nothing here stops a tank.
  { propId: 'haystack', x: -106, z: 142, yaw: 0 },
  { propId: 'haystack', x: -98, z: 134, yaw: 0 },
  { propId: 'haystack', x: -94, z: 144, yaw: 0 },
  { propId: 'tractor_farm', x: -92, z: 130, yaw: 0.4 },
  { propId: 'tractor_farm', x: -84, z: 146, yaw: -0.8 },

  // ── The home fields: real boundaries, 28–36 m apart, each with its gateway ──
  //
  // A boundary that reaches the track would make the lane a series of rooms, so
  // every run stops short of it. The gateways are 27 m, 39 m and 21 m wide and
  // in different places, which is what a field system looks like from above and
  // is why the lane does not read as a corridor.
  ...run('hedgerow', -134, 108, NORTH, 11),
  ...run('hedgerow', -134, 80, NORTH, 9),
  ...run('hedgerow', -134, 36, NORTH, 12),

  // ── The orchard: two of its four rows, on a 12 × 10 m grid ──
  //
  // Real orchard spacing, kept because it *looks* planted rather than because a
  // tank needs the room: since `3ec7816` these stop no tank and still stop
  // rounds, so an orchard is ground you drive into and cannot be shot inside.
  // The single biggest play change the tree flag makes anywhere in the set, and
  // this is the map to spend it on.
  //
  // The other two rows are `FLATS_ORCHARD_FELLED`, which exists so the burnt
  // farm's orchard can be half stumps.
  ...[44, 54, 64, 74].flatMap((z) =>
    [-130, -106].map((x) => ({ propId: 'tree_orchard' as PropId, x, z, yaw: 0 })),
  ),

  // A copse where three field boundaries meet, which is where a copse actually
  // ends up on a farm — and it sits on the approach to the middle door, so the
  // last thirty metres before the field are cover you can drive through.
  { propId: 'tree_oak', x: -72, z: 28, yaw: 0.3 },
  { propId: 'tree_oak', x: -62, z: 22, yaw: -0.2 },
  { propId: 'tree_oak', x: -80, z: 16, yaw: 0.5 },
];

/**
 * Harrow's lower closes: the same farm's south end, unbuilt.
 *
 * This is the fast half of the lane and it is meant to be. Four boundaries, a
 * smallholding half way down, one field barn a long way from anything, and
 * otherwise hedge, stumps and hay. The
 * *point* of it is that the enemy base's own flank is like this too — the
 * layout is point-symmetric, so the closes at one end of the map face a steading
 * at the other, and both sides get one of each.
 */
const FLATS_CLOSES: PlacedProp[] = [
  ...run('hedgerow', -134, -44, NORTH, 11),
  ...run('hedgerow', -134, -82, NORTH, 8),
  ...run('hedgerow', -134, -120, NORTH, 12),
  ...run('hedgerow', -134, -158, NORTH, 6),

  // ── The smallholding: three ranges half way down the lane ──
  //
  // The lane is 96 m wide and 420 m long, and a farm at one end of it leaves
  // three hundred metres of hedge with nothing to fight over. This is the second
  // place in the lane: a long range facing the outer boundary with a pair of
  // cottages across the track from it, between two field boundaries and clear of
  // both. It is also the piece that keeps the §14 footprint row honest — its
  // burnt twin is *heavier* than it is (`terrace_shelled` is longer than
  // `farmhouse_long`), which pulls back against the steading, where every
  // substitute is lighter.
  { propId: 'farmhouse_long', x: -120, z: -63, yaw: EAST },
  { propId: 'cottage_stone', x: -102, z: -52, yaw: NORTH },
  { propId: 'cottage_stone', x: -102, z: -74, yaw: NORTH },

  // The field barn: one building, standing on its own in the middle of a close,
  // 30 m clear of anything else. On the burnt lane it is a roofless shell.
  { propId: 'barn_wooden', x: -96, z: -140, yaw: NORTH },

  { propId: 'tree_oak', x: -128, z: -38, yaw: 0.2 },
  { propId: 'tree_oak', x: -108, z: -166, yaw: -0.4 },
  { propId: 'haystack', x: -88, z: -56, yaw: 0 },
  { propId: 'haystack', x: -104, z: -108, yaw: 0 },
  { propId: 'tractor_farm', x: -82, z: -150, yaw: 1.1 },
  { propId: 'stump_cut', x: -128, z: -100, yaw: 0 },
  { propId: 'stump_cut', x: -76, z: -132, yaw: 0 },
  { propId: 'stump_cut', x: -114, z: -190, yaw: 0 },
  { propId: 'stump_cut', x: -60, z: -64, yaw: 0 },
];

/**
 * The other two rows of the orchard — the ones cut for fuel on the burnt farm.
 *
 * Identical trees on identical spacing, held in their own list for one reason:
 * `burntLane` is given an override for them, so on the east lane these eight
 * become `stump_cut` while the eight in `FLATS_STEADING` become `tree_dead`.
 * The working orchard is sixteen trees that stop rounds; the burnt one is eight
 * that do and eight that do not. **Same ground, same route, half the cover.**
 */
const FLATS_ORCHARD_FELLED: PlacedProp[] = [44, 54, 64, 74].flatMap((z) =>
  [-118, -94].map((x) => ({ propId: 'tree_orchard' as PropId, x, z, yaw: 0 })),
);

/**
 * The Flat: the empty part, and it is the map.
 *
 * **88 m across and 420 m long, and one thing in it stops a round.** Everything
 * else here is `blocksTanks: false` *and* `blocksShells: false` — craters, a
 * telegraph line — so the field looks like farmland that has been shelled and
 * plays like four hundred metres of nothing, which is the same thing it played
 * like when it was 76 m of nothing.
 *
 * **The exception is deliberate and it is what makes the resize survivable.**
 * Scaled naively, the crossing becomes eight seconds of open ground against a
 * gun that reaches 260 m: not "a place you die crossing" but a place nobody
 * crosses, and a map whose central idea is a decision loses the decision the
 * moment the answer is always *no*. So there is a copse of ten `tree_oak` on the
 * centre line. It stops no tank, costs no drivable area, and stops rounds — so
 * an 88 m dash becomes two dashes of about thirty metres with somewhere to stand
 * in between. Crossing is a risk again rather than a coin.
 *
 * §8 of `docs/maps-design.md` says both things: its district E says nothing in
 * the field stops a shell, and its own resize note asks for exactly this
 * waypoint. The note is the later of the two and is the one implemented; the
 * district paragraph was written for a 76 m field.
 */
const FLATS_FIELD: PlacedProp[] = [
  // The copse is point-symmetric about the origin — three placements and their
  // half-turn mirrors — because the oaks are solid at the trunk now and this
  // stands on the centre line: an asymmetric scatter of solid trunks gave the
  // two sides different crossing counts in the §14 fairness ledger (4 against
  // 3, measured). While trees stopped nothing the scatter was free.
  { propId: 'tree_oak', x: -16, z: 6, yaw: 0.2 },
  { propId: 'tree_oak', x: 16, z: -6, yaw: 0.2 },
  { propId: 'tree_oak', x: -6, z: -8, yaw: -0.5 },
  { propId: 'tree_oak', x: 6, z: 8, yaw: -0.5 },
  { propId: 'tree_oak', x: -4, z: 22, yaw: -0.3 },
  { propId: 'tree_oak', x: 4, z: -22, yaw: -0.3 },

  // The field's big craters are gone with `crater_large` itself — the delivered
  // mesh read as a razed building, not shelled ground, and was cut from the
  // catalogue on 2026-08-19. The field plays exactly as it did, because they
  // stopped nothing.

  // The telegraph line: two rows of poles down the length of the field. Eight
  // metres tall, stops nothing, and reads as infrastructure from the far end of
  // a 420 m map — the cheapest legibility in the game, and the only thing that
  // tells you which way the field runs.
  ...[-12, 12].flatMap((x) =>
    [-144, -120, -96, -72, -48, -24, 0, 24, 48, 72, 96, 120, 144].map((z) => ({
      propId: 'utility_pole' as PropId,
      x,
      z,
      yaw: 0,
    })),
  ),
];

/**
 * The inner hedge: the lane/field boundary, and the doors in it.
 *
 * **The gap is the contract and it is 10 m to the metre**, set by `TANK_RADIUS`
 * and unchanged by the resize. What the resize changes is how many there are:
 * one door at z = 0 made "do I cross" the only question on the map, which is a
 * question a 420 m field answers *no* to every time. Three doors per lane, 118 m
 * apart, turn it into *which door, and when* — a strictly better question and the
 * only one that survives a field this long.
 *
 * A `gate_farm` stands in every door and stops nothing, so the door is exactly
 * as wide as it was before there was a gate drawn in it.
 */
const FLATS_HEDGE_WEST: PlacedProp[] = [
  ...span('hedgerow', -44, 165, EAST, 14),
  ...span('hedgerow', -44, 59, EAST, 18),
  ...span('hedgerow', -44, -59, EAST, 18),
  ...span('hedgerow', -44, -165, EAST, 14),
  { propId: 'gate_farm', x: -44, z: 118, yaw: EAST },
  { propId: 'gate_farm', x: -44, z: 0, yaw: EAST },
  { propId: 'gate_farm', x: -44, z: -118, yaw: EAST },
];

/**
 * The same boundary on the burnt side, with two more ways through it.
 *
 * Same line, same three doors in the same places — that part is the parish's
 * field boundary and it is identical, which is what the ledger's route row
 * checks. What is different is that nobody has laid this hedge for two years and
 * shells went through it: two further gaps, 12 m and irregular because a blown
 * gap is irregular, at z = ±59.
 *
 * **So the burnt lane has five ways into the field and the working lane has
 * three.** That is the map's existing trade — the burnt side sees further and
 * hides less — extended to the thing the resize made important, which is access.
 * The extra gaps are placed one in each half, so the trade costs the ledger
 * nothing.
 */
const FLATS_HEDGE_EAST: PlacedProp[] = [
  ...span('hedgerow_wild', 44, 165, EAST, 14),
  ...span('hedgerow_wild', 44, 89, EAST, 8),
  ...span('hedgerow_wild', 44, 29, EAST, 8),
  ...span('hedgerow_wild', 44, -29, EAST, 8),
  ...span('hedgerow_wild', 44, -89, EAST, 8),
  ...span('hedgerow_wild', 44, -165, EAST, 14),
  { propId: 'gate_farm', x: 44, z: 118, yaw: EAST },
  { propId: 'gate_farm', x: 44, z: 0, yaw: EAST },
  { propId: 'gate_farm', x: 44, z: -118, yaw: EAST },
];

/**
 * Two hedged lanes, and four hundred metres of nothing between them.
 *
 * **The map where the middle is a mistake.** The field is 88 m across and runs
 * the whole 420 m length: no cover, no hard shadow, nothing to break a shot bar
 * one copse on the centre line. A tank crossing is visible from both flanks for
 * the whole crossing, so holding the middle means standing in the open, nobody
 * does, and the match becomes two flank fights with a very expensive way to
 * reinforce between them. This is the deliberate inverse of Kestrel Depot, and it
 * is the cleanest single idea in the set.
 *
 * **Hedgerows are the reason the flanks work.** `hedgerow` stops a shell and does
 * not stop a tank — the sandbag rule turned round — so a lane is a place you can
 * be shot along but can break out of sideways at any point, which makes pushing
 * one survivable and camping one unreliable. Almost the entire enclosure system
 * here is permeable: of 496 placements, 358 stop no tank. That is what buys a map
 * this dense-looking at 1.4% solid footprint.
 *
 * ## The parish: one farm plan, built twice
 *
 * **This is the owner's sentence, literally: houses on one side, destroyed houses
 * and wrecked vehicles on the other.** Two farms laid out the same way, because
 * farms in a parish are laid out the same way. One is being worked; one was burnt
 * when the fighting came down the east lane. The field between them was never
 * fought over, because nobody crossed it — which is why it is empty, and the
 * emptiness is now a fact about the place rather than a gap in the decoration.
 *
 * The east lane is not a second layout. It is `FLATS_STEADING` and
 * `FLATS_CLOSES` put through `burntLane`, so the two farms are the same
 * placements with different materials — see `FLATS_BURNT`, which is where the
 * asymmetry is stated once and derived everywhere.
 *
 * ## Where this departs from §8, and why
 *
 * **§8 puts both steadings at the north end of their lanes. This puts the
 * working one north and the burnt one south, and it has to.** §14's fairness
 * ledger splits a map on the perpendicular bisector of the two base centres,
 * which here is z = 0: north against south. Two steadings in the north would put
 * every building in the game on one side of that line — the north base would
 * spawn behind two farms and the south base behind two empty closes, at four
 * times the solid footprint. That is not a tolerance failure, it is the thing
 * mirroring used to prevent.
 *
 * A half turn fixes it and costs the design nothing, because the thing §8 is
 * protecting is not *where* each steading stands but that the **lanes** differ:
 * the whole west lane is worked and the whole east lane is burnt, which is what a
 * player sees. §14's own note that the two halves are congruent by *rotation*
 * rather than by reflection is the licence for it. So each side has a steading on
 * one flank and open closes on the other, one side's steading intact and the
 * other's gutted, and both sides fight both lanes.
 *
 * Three smaller departures, all budget: no `bridge_stone`/`culvert_headwall`
 * ditch crossing and no `tree_willow` (three distinct props for one piece of
 * decoration, against a hard cap of 26), no `oil_drums`/`tyre_stack`, and the
 * telegraph poles are at 24 m centres rather than §8's 14–16 m — which is nearer
 * a real telegraph line anyway, and 14 m centres would have cost 60 placements
 * for a row of poles.
 */
export const MAP_FLATS: MapDefinition = {
  id: 'flats',
  name: 'Harrow Flats',
  blurb: 'Two hedged lanes and a bare field between them. Crossing costs.',
  // 280 m across by 420 m long. The **width is the number that held** and the
  // length is the number that was spent: the field is 88 m against the 76 m it
  // was, because a wider field is a field nobody crosses, while the lanes went
  // from 56 m to 96 m and got a farm each with ground behind it.
  halfExtent: { x: 140, z: 210 },
  teamSize: 4,
  bases: FLATS_BASES.bases,
  props: [
    // Collision lives on invisible masks at 90% of each prop's own box,
    // not on the props themselves — see `clipMasked` in `clip-masks.ts`.
    ...clipMasked([
    ...FLATS_BASES.props,
    ...FLATS_HEDGE_WEST,
    ...FLATS_HEDGE_EAST,
    ...FLATS_FIELD,
    // The working farm, and the same farm burnt and turned about the origin.
    ...FLATS_STEADING,
    ...FLATS_CLOSES,
    ...burntLane(FLATS_STEADING),
    ...burntLane(FLATS_CLOSES),
    // The orchard's felled half: standing dead trees on the burnt farm where the
    // working farm has fruit, and stumps where the rest went for fuel. So the
    // burnt orchard conceals about half as well as the living one — same ground,
    // same route, less cover, which is the map's asymmetry in one district.
    ...burntLane(FLATS_ORCHARD_FELLED, { tree_orchard: 'stump_cut' }),
    ...FLATS_ORCHARD_FELLED,
      ]),
  ],
};

// ─── 4. Kestrel Depot ────────────────────────────────────────────────────────

/**
 * A walled fuel depot in the middle, and a long way to it.
 *
 * **The map that is about one place.** The depot is a 44 m walled square on the
 * origin with a gate in each of its four sides and four silos inside making a
 * cross of 13 m avenues. Whoever is in it has hard cover facing every
 * direction, a short reload cycle behind a wall, and shots into all four
 * approaches. It is the strongest ground in any of these five maps.
 *
 * **And it cannot be turtled**, because it has four gates and no back. Three
 * tanks hold at most three of them, and the fourth is a free entry into the
 * middle of an enemy already committed outwards. That is the trade: the depot is
 * worth taking and cannot be kept quietly.
 *
 * **The approach is diagonal and the gates are not.** Bases sit at the
 * north-west and south-east corners, so a tank driving straight at the depot
 * arrives at a *corner* of it and has to go round to a gate — under fire from
 * whoever is already inside. Going round the outside instead is a much longer
 * drive through the two open quadrants, which is the flank this map offers and
 * the reason it is not simply a race.
 *
 * **The last forty metres to a base are deliberately bare.** Nothing is placed
 * within 42 m of a base centre, so an attack on a base is a crossing rather than
 * a creep, and a team that has lost the depot still has somewhere it can hold.
 *
 * ## It is a depot now, and the depot is why the middle is worth having
 *
 * The compound is unchanged — four walls, four gates, four silos, a cross of
 * 13 m avenues — because that shape *is* the map. What changed is that it now
 * has the plant a fuel depot would have, and the plant is arranged the way one
 * would be rather than sprinkled:
 *
 * - **The control building stands off the corner of the compound**, out of the
 *   gate line, where a depot office stands.
 * - **The tank farm is beside a gate, not across it.** Two horizontal tanks
 *   about ten metres out from the south gate and offset to one side of it, so
 *   the gate mouth stays open and you come in and go *round* them. That is
 *   where the fight for a gate actually happens.
 * - **The workshop, the warehouse and the anti-tank furniture** sit out at
 *   30–56 m, off the gate axes and off the diagonal a base drives out along.
 *   Both are learned constraints rather than taste: a silo battery stood across
 *   the west gate's approach for one revision and a pipe rack flanked it, and
 *   between them they took the worst bot on this map from 8.05 m/s to 3.12 and
 *   its pinned share from 11% to 41%. Both were removed.
 * - **Jersey barriers and a sandbag emplacement inside and beside the walls.**
 *   They stop rounds and not tanks, so they are cover for whoever holds the
 *   compound and can never narrow an avenue or trap a tank against a silo.
 * - **A container yard in the south-west quadrant**, on the long way round —
 *   two rows of stacks, the one piece of hard cover on a flank whose whole cost
 *   is that it is unsupported.
 *
 * **The last forty-two metres to a base are still bare**, and `maps.test.ts`
 * asserts it: an attack on a base is a crossing rather than a creep. Every
 * placement below clears both base centres by that margin, which is why the
 * north-west quadrant looks thinner than the south-west one — it is not, the
 * mirror fills it, and the empty ring is the base's.
 *
 * **Authored as a half-map turned half a revolution — point symmetry, because
 * the bases are diagonally opposite — and now flat literals.** As on ridge, a
 * BAKE replaced the composition with its output and the editor owns the layout
 * from here. The fairness ledger, not the rotation, is what keeps the two
 * halves comparable.
 */
export const MAP_DEPOT: MapDefinition = {
  id: 'depot',
  name: 'Kestrel Depot',
  blurb: 'A walled depot on the origin with four gates. The middle is the match.',
  halfExtent: { x: 100, z: 100 },
  // Four a side, like every map the resize enlarges — see `MAP_TOWN.teamSize`.
  teamSize: 4,
  bases: {
      blue: {
      team: 'blue',
      x: 73,
      z: -73,
      facing: -Math.PI / 4,
      spawns: [
        { x: 75, z: -75.5 },
        { x: 83.5, z: -85 },
        { x: 82, z: -55.5 },
        { x: 55, z: -83 },
        { x: 65, z: -64.5 },
        { x: 68, z: -93.5 },
        { x: 92.5, z: -70.5 },
      ],
    },
      red: {
      team: 'red',
      x: -73,
      z: 73,
      facing: (3 * Math.PI) / 4,
      spawns: [
        { x: -64.5, z: 91 },
        { x: -76.5, z: 76.5 },
        { x: -90, z: 64.5 },
        { x: -79, z: 93 },
        { x: -92.5, z: 78.5 },
        { x: -61.5, z: 76 },
        { x: -77.5, z: 59 },
      ],
    },
    },
  props: [
    { propId: 'hesco_barrier', x: -83, z: 42, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -86.5, z: 45.5, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -90, z: 49, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -93.5, z: 52.5, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -97, z: 56, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -39.5, z: 82, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -43, z: 85.5, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -46.5, z: 89, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -50, z: 92.5, yaw: (-3 * Math.PI) / 4 },
    { propId: 'hesco_barrier', x: -53.5, z: 96, yaw: (-3 * Math.PI) / 4 },
    { propId: 'dragon_teeth', x: -48, z: 71, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: -51, z: 67.5, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: -67.5, z: 51, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: -71, z: 47.5, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'gate_pylon', x: -54, z: 64.5, yaw: (3 * Math.PI) / 4 },
    { propId: 'gate_pylon', x: -64.5, z: 54, yaw: (3 * Math.PI) / 4 },
    { propId: 'command_post', x: -91, z: 92, yaw: (3 * Math.PI) / 4 },
    { propId: 'sandbag_wall', x: -49.5, z: 73, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: -51.5, z: 70.5, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: -70.5, z: 51.5, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: -73, z: 49, yaw: (3 * Math.PI) / 4, blocksShells: false },
    { propId: 'wall_concrete', x: -19.56, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: -14, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: -8.44, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: 8.44, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: 14, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: 19.56, z: 22, yaw: 0 },
    { propId: 'wall_concrete', x: -19.56, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: -14, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: -8.44, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: 8.44, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: 14, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: 19.56, z: -22, yaw: 0 },
    { propId: 'wall_concrete', x: 22, z: 19.56, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: 22, z: 14, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: 22, z: 8.44, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: 22, z: -8.44, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: 22, z: -14, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: 22, z: -19.56, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: 19.56, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: 14, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: 8.44, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: -8.44, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: -14, yaw: Math.PI / 2 },
    { propId: 'wall_concrete', x: -22, z: -19.56, yaw: Math.PI / 2 },
    { propId: 'fuel_silo', x: -12, z: 12, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_silo', x: 12, z: 12, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_silo', x: -12, z: -12, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_silo', x: 12.5, z: -12, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'depot_control', x: -26, z: 34, yaw: 0 },
    { propId: 'warehouse_brick', x: -56, z: 12, yaw: 0.35 },
    { propId: 'workshop_garage', x: -46, z: -8, yaw: 0 },
    { propId: 'fuel_tank_horizontal', x: -8, z: -34, yaw: Math.PI / 2 },
    { propId: 'fuel_tank_horizontal', x: -16, z: -32, yaw: Math.PI / 2 },
    { propId: 'sandbag_pit', x: -14, z: 26, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: -18, z: -46, yaw: 0.3, blocksShells: false },
    { propId: 'hedgehog_antitank', x: -24, z: -44, yaw: -0.2, blocksShells: false },
    { propId: 'berm_earth', x: -34, z: -40, yaw: 0.4, blocksTanks: false, blocksShells: false },
    { propId: 'pylon_concrete', x: -74, z: 14, yaw: Math.PI / 2, blocksShells: false },
    { propId: 'crater_small', x: -46, z: -30, yaw: 0 },
    { propId: 'container_stack', x: -62.5, z: -33, yaw: 0 },
    { propId: 'container_stack', x: -56, z: -33, yaw: 0 },
    { propId: 'container_stack', x: -50, z: -33, yaw: 0 },
    { propId: 'container_stack', x: -61, z: -43.5, yaw: 0 },
    { propId: 'container_stack', x: -55, z: -43.5, yaw: 0 },
    { propId: 'container_stack', x: -48.5, z: -43.5, yaw: 0 },
    { propId: 'spoil_heap', x: -40, z: -50, yaw: 0.4 },
    { propId: 'ruin_warehouse', x: -26, z: -68, yaw: 0.5 },
    { propId: 'guard_tower', x: -30, z: -30, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'bunker_concrete', x: -66, z: 0, yaw: Math.PI / 2 },
    { propId: 'bunker_concrete', x: 0, z: -66, yaw: 0 },
    { propId: 'water_tower', x: -56, z: -56, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_silo', x: -72, z: -26, yaw: 0 },
    { propId: 'tree_poplar', x: -78, z: 6, yaw: 0, blocksShells: false },
    { propId: 'tree_poplar', x: -78, z: -6, yaw: 0, blocksShells: false },
    { propId: 'depot_control', x: 26, z: -34, yaw: Math.PI },
    { propId: 'warehouse_brick', x: 56, z: -12, yaw: -2.792 },
    { propId: 'workshop_garage', x: 46, z: 8, yaw: Math.PI },
    { propId: 'fuel_tank_horizontal', x: 8, z: 34, yaw: -Math.PI / 2 },
    { propId: 'fuel_tank_horizontal', x: 14.5, z: 31.5, yaw: -Math.PI / 2 },
    { propId: 'sandbag_pit', x: 14, z: -26, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: 18, z: 46, yaw: -2.842, blocksShells: false },
    { propId: 'hedgehog_antitank', x: 24, z: 44, yaw: 2.942, blocksShells: false },
    { propId: 'berm_earth', x: 34, z: 40, yaw: -2.742, blocksTanks: false, blocksShells: false },
    { propId: 'pylon_concrete', x: 74, z: -14, yaw: -Math.PI / 2, blocksShells: false },
    { propId: 'crater_small', x: 46, z: 30, yaw: Math.PI },
    { propId: 'container_stack', x: 62, z: 34, yaw: Math.PI },
    { propId: 'container_stack', x: 55.9, z: 34, yaw: Math.PI },
    { propId: 'container_stack', x: 49.8, z: 34, yaw: Math.PI },
    { propId: 'container_stack', x: 61, z: 43.5, yaw: Math.PI },
    { propId: 'container_stack', x: 55, z: 43.5, yaw: Math.PI },
    { propId: 'container_stack', x: 49, z: 43.5, yaw: Math.PI },
    { propId: 'spoil_heap', x: 40, z: 50, yaw: -2.742 },
    { propId: 'ruin_warehouse', x: 26, z: 68, yaw: -2.642 },
    { propId: 'guard_tower', x: 30, z: 30, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'bunker_concrete', x: 66, z: 0, yaw: -Math.PI / 2 },
    { propId: 'bunker_concrete', x: 0, z: 66, yaw: Math.PI },
    { propId: 'water_tower', x: 56, z: 56, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'fuel_silo', x: 72, z: 26, yaw: Math.PI },
    { propId: 'tree_poplar', x: 78, z: -6, yaw: Math.PI, blocksShells: false },
    { propId: 'tree_poplar', x: 78, z: 6, yaw: Math.PI, blocksShells: false },
    { propId: 'hesco_barrier', x: 83, z: -42, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 86.5, z: -45.5, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 90, z: -49, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 93.5, z: -52.5, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 97, z: -56, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 39.5, z: -82, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 43, z: -85.5, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 46.5, z: -89, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 50, z: -92.5, yaw: Math.PI / 4 },
    { propId: 'hesco_barrier', x: 53.5, z: -96, yaw: Math.PI / 4 },
    { propId: 'dragon_teeth', x: 48, z: -71, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: 51, z: -67.5, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: 67.5, z: -51, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'dragon_teeth', x: 71, z: -47.5, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'gate_pylon', x: 54, z: -64.5, yaw: -Math.PI / 4 },
    { propId: 'gate_pylon', x: 64.5, z: -54, yaw: -Math.PI / 4 },
    { propId: 'command_post', x: 91, z: -92, yaw: -Math.PI / 4 },
    { propId: 'sandbag_wall', x: 49.5, z: -73, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: 51.5, z: -70.5, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: 70.5, z: -51.5, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'sandbag_wall', x: 73, z: -49, yaw: -Math.PI / 4, blocksShells: false },
    { propId: 'wreck_truck', x: 28.5, z: -39.5, yaw: 0.262 },
    { propId: 'wreck_truck', x: -27.5, z: 39.5, yaw: -2.88 },
    { propId: 'tree_birch_clump', x: 33.5, z: -37.5, yaw: 0 },
    { propId: 'tree_birch_clump', x: -33.5, z: 37.5, yaw: Math.PI, blocksShells: false },
    { propId: 'loading_ramp', x: 53, z: 8, yaw: Math.PI / 2, blocksTanks: true },
    { propId: 'loading_ramp', x: -53, z: -8, yaw: -Math.PI / 2, blocksTanks: true },
    { propId: 'oil_drums', x: -54, z: -12, yaw: 0.262, blocksShells: false },
    { propId: 'oil_drums', x: 54, z: 12, yaw: -2.88, blocksShells: false },
    { propId: 'clip_wall', x: 55, z: 43.5, yaw: 0 },
    { propId: 'clip_wall', x: 56, z: 34, yaw: 0 },
    { propId: 'clip_wall', x: -54.5, z: -43.5, yaw: 0 },
    { propId: 'clip_wall', x: -56, z: -33, yaw: 0 },
    { propId: 'barrier_jersey', x: -20, z: 6, yaw: 0 },
    { propId: 'barrier_jersey', x: -16.5, z: 6, yaw: 0 },
    { propId: 'barrier_jersey', x: 20.5, z: -6.5, yaw: 0 },
    { propId: 'barrier_jersey', x: 17, z: -6.5, yaw: 0 },
    { propId: 'clip_wall', x: 11, z: -11, yaw: 0.873, clipSize: { halfX: 3.25, halfZ: 3.2, height: 3 } },
    { propId: 'clip_wall', x: 10.5, z: -11, yaw: 1.349, clipSize: { halfX: 3.25, halfZ: 2.7, height: 3 } },
    { propId: 'clip_wall', x: 14.5, z: -15, yaw: 0.978, clipSize: { halfX: 1, halfZ: 3.25, height: 3 } },
    { propId: 'clip_wall', x: 11, z: 12.5, yaw: 0.873, clipSize: { halfX: 3.25, halfZ: 3.2, height: 3 } },
    { propId: 'clip_wall', x: -11, z: 10.5, yaw: 1.349, clipSize: { halfX: 3.25, halfZ: 2.7, height: 3 } },
    { propId: 'clip_wall', x: -14, z: -9.5, yaw: 0.978, clipSize: { halfX: 1, halfZ: 3.25, height: 3 } },
    { propId: 'clip_wall', x: -11.5, z: 11, yaw: 0.873, clipSize: { halfX: 3.25, halfZ: 3.2, height: 3 } },
    { propId: 'clip_wall', x: 10, z: 12.5, yaw: 1.349, clipSize: { halfX: 3.25, halfZ: 2.7, height: 3 } },
    { propId: 'clip_wall', x: -14, z: 15, yaw: 0.978, clipSize: { halfX: 1, halfZ: 3.25, height: 3 } },
    { propId: 'clip_wall', x: -11, z: -12.5, yaw: 0.873, clipSize: { halfX: 3.25, halfZ: 3.2, height: 3 } },
    { propId: 'clip_wall', x: -11.5, z: -13, yaw: 1.349, clipSize: { halfX: 3.25, halfZ: 2.7, height: 3 } },
    { propId: 'clip_wall', x: 14.5, z: 9, yaw: 0.978, clipSize: { halfX: 1, halfZ: 3.25, height: 3 } },
    { propId: 'clip_wall_move', x: -14, z: 26, yaw: 0.695, clipSize: { halfX: 2.25, halfZ: 1.5, height: 3 } },
    { propId: 'clip_wall_move', x: 14, z: -26, yaw: 0.695, clipSize: { halfX: 2.25, halfZ: 1.5, height: 3 } },
    { propId: 'clip_wall', x: -30.5, z: -29, yaw: 0.782, clipSize: { halfX: 2.7, halfZ: 2.2, height: 3 } },
    { propId: 'clip_wall', x: -28, z: -32, yaw: 0.784, clipSize: { halfX: 1.25, halfZ: 1, height: 3 } },
    { propId: 'clip_wall', x: 31, z: 29.5, yaw: 0.782, clipSize: { halfX: 2.7, halfZ: 2.2, height: 3 } },
    { propId: 'clip_wall', x: 28, z: 32, yaw: 0.784, clipSize: { halfX: 1.25, halfZ: 1, height: 3 } },
    { propId: 'clip_wall', x: 34, z: 40, yaw: 0.384, clipSize: { halfX: 2.75, halfZ: 1.45, height: 3 } },
    { propId: 'clip_wall', x: -34, z: -40, yaw: 0.384, clipSize: { halfX: 2.75, halfZ: 1.45, height: 3 } },
    { propId: 'clip_wall', x: -55.9, z: -55.9, yaw: 0, clipSize: { halfX: 1.45, halfZ: 1.45, height: 3 } },
    { propId: 'clip_wall', x: 56, z: 56, yaw: 0, clipSize: { halfX: 1.45, halfZ: 1.45, height: 3 } },
  ],
  decals: [
    { kind: 'rect', x: -27.5, z: 39.5, yaw: -2.88, scaleX: 3, scaleZ: 1.5 },
    { kind: 'rect', x: 28.5, z: -39.5, yaw: 0.262, scaleX: 3.7, scaleZ: 1.7 },
  ],
};

// ─── 5. The Cauldron ─────────────────────────────────────────────────────────

export const MAP_CAULDRON: MapDefinition = {
  id: 'cauldron',
  name: 'The Cauldron',
  blurb: 'Four quadrants, one crossing in the middle. Everything meets there.',
  halfExtent: { x: 100, z: 100 },
  // Four a side, like every map the resize enlarges — see `MAP_TOWN.teamSize`.
  teamSize: 4,
  bases: {
      blue: {
      team: 'blue',
      x: 0,
      z: -78,
      facing: 0,
      spawns: [
        { x: -16, z: -80 },
        { x: 0, z: -80 },
        { x: 16, z: -80 },
        { x: -8, z: -73 },
        { x: 8, z: -73 },
        { x: -25, z: -86 },
        { x: 25, z: -86 },
      ],
    },
      red: {
      team: 'red',
      x: 0,
      z: 78,
      facing: Math.PI,
      spawns: [
        { x: 16, z: 80 },
        { x: 0, z: 80 },
        { x: -16, z: 80 },
        { x: 8, z: 73 },
        { x: -8, z: 73 },
        { x: 25, z: 86 },
        { x: -25, z: 86 },
      ],
    },
    },
  props: [
    // A U-shaped spawn wall: one movement-only mask per side (below, near the
    // other masks), not one per barrier — the barrier itself keeps blocking
    // shells, hence `blocksShells: true` here. See `wallOf`/`wallMask` on
    // `bastion()`, which this fort is the same shape as.
    { propId: 'hesco_barrier', x: 27.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 22.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 17.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 12.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 7.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 2.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -2.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -7.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -12.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -17.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -22.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -27.5, z: 91, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 68, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 73, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 78, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 83, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 88, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 68, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 73, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 78, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 83, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 88, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'dragon_teeth', x: 16.4, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 11.6, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -11.6, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -16.4, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 7.6, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -7.6, z: 67, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 0, z: 87, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 16.72, z: 69.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: 69.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: 69.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -16.72, z: 69.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    // Same fort shape, mirrored — see the note on the other spawn's back wall.
    { propId: 'hesco_barrier', x: -27.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -22.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -17.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -12.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -7.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -2.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 2.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 7.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 12.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 17.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 22.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 27.5, z: -91, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -68, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -73, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -78, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -83, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -88, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -68, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -73, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -78, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -83, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -88, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'dragon_teeth', x: -16.4, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -11.6, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 11.6, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 16.4, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -7.6, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 7.6, z: -67, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 0, z: -87, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -16.72, z: -69.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: -69.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: -69.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 16.72, z: -69.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 12.73, z: 12.73, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 16.66, z: 16.66, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 20.59, z: 20.59, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 24.52, z: 24.52, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 28.45, z: 28.45, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 32.39, z: 32.39, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 42.43, z: 42.43, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 46.36, z: 46.36, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 50.29, z: 50.29, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 54.22, z: 54.22, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 58.15, z: 58.15, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 62.08, z: 62.08, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 66.02, z: 66.02, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -12.73, z: 12.73, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -16.66, z: 16.66, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -20.59, z: 20.59, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -24.52, z: 24.52, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -28.45, z: 28.45, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -32.39, z: 32.39, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -42.43, z: 42.43, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -46.36, z: 46.36, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -50.29, z: 50.29, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -54.22, z: 54.22, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -58.15, z: 58.15, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -62.08, z: 62.08, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -66.02, z: 66.02, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -12.73, z: -12.73, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -16.66, z: -16.66, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -20.59, z: -20.59, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -24.52, z: -24.52, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -28.45, z: -28.45, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -32.39, z: -32.39, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -42.43, z: -42.43, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -46.36, z: -46.36, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -50.29, z: -50.29, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -54.22, z: -54.22, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -58.15, z: -58.15, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -62.08, z: -62.08, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: -66.02, z: -66.02, yaw: (3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 12.73, z: -12.73, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 16.66, z: -16.66, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 20.59, z: -20.59, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 24.52, z: -24.52, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 28.45, z: -28.45, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 32.39, z: -32.39, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 42.43, z: -42.43, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 46.36, z: -46.36, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 50.29, z: -50.29, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 54.22, z: -54.22, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 58.15, z: -58.15, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 62.08, z: -62.08, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wall_concrete', x: 66.02, z: -66.02, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: 30, z: 46, yaw: -0.5, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 22, z: 36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 26.8, z: 36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 31.6, z: 36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 36.4, z: 36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: 36, z: 62, yaw: 0.4, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: 40, z: 48, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_bus', x: 34, z: 20, yaw: 1.2, blocksTanks: false, blocksShells: false },
    { propId: 'crater_small', x: 6, z: 12, yaw: 0.5 },
    { propId: 'milestone', x: 12, z: 6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: -30, z: 46, yaw: -2.642, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -22, z: 36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -26.8, z: 36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -31.6, z: 36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -36.4, z: 36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: -36, z: 62, yaw: 2.742, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: -40, z: 48, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_bus', x: -34, z: 20, yaw: 1.942, blocksTanks: false, blocksShells: false },
    { propId: 'crater_small', x: -6, z: 12, yaw: 2.642 },
    { propId: 'milestone', x: -12, z: 6, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'civic_hall', x: 0, z: 26, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'billboard', x: 0, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: 30, z: -46, yaw: 0.5, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 22, z: -36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 26.8, z: -36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 31.6, z: -36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: 36.4, z: -36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: 36, z: -62, yaw: -0.4, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: 40, z: -48, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_bus', x: 34, z: -20, yaw: -1.2, blocksTanks: false, blocksShells: false },
    { propId: 'crater_small', x: 6, z: -12, yaw: -0.5 },
    { propId: 'milestone', x: 12, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: -30, z: -46, yaw: 2.642, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -22, z: -36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -26.8, z: -36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -31.6, z: -36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_drystone', x: -36.4, z: -36, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: -36, z: -62, yaw: -2.742, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: -40, z: -48, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_bus', x: -34, z: -20, yaw: -1.942, blocksTanks: false, blocksShells: false },
    { propId: 'crater_small', x: -6, z: -12, yaw: -2.642 },
    { propId: 'milestone', x: -12, z: -6, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'civic_hall', x: 0, z: -26, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'billboard', x: 0, z: -40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 60, z: 14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 65.2, z: 14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 70.4, z: 14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 75.6, z: 14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: 88, z: 30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: 50, z: 22, yaw: 0.2, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: 36, z: 12, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 46, z: 10, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 60, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 65.2, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 70.4, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: 75.6, z: -14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: 88, z: -30, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: 50, z: -22, yaw: -0.2, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: 36, z: -12, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 46, z: -10, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: 56, z: 0, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'guard_tower', x: 86, z: 0, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -60, z: 14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -65.2, z: 14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -70.4, z: 14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -75.6, z: 14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: -88, z: 30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: -50, z: 22, yaw: 2.942, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: -36, z: 12, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -46, z: 10, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -60, z: -14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -65.2, z: -14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -70.4, z: -14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'wall_brick_garden', x: -75.6, z: -14, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'terrace_row', x: -88, z: -30, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'rubble_bank', x: -50, z: -22, yaw: -2.942, blocksTanks: false, blocksShells: false },
    { propId: 'tree_poplar', x: -36, z: -12, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -46, z: -10, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -56, z: 0, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'guard_tower', x: -86, z: 0, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    // One mask per side, not one per barrier — full span on the long axis
    // (halfX = 2.5 × count), MASK_SCALE only on thickness and height. See
    // `wallOf`/`wallMask` on `bastion()`, which this fort is the same shape as.
    { propId: 'clip_wall_move', x: 0, z: 91, yaw: Math.PI, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -30, z: 78, yaw: -Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 30, z: 78, yaw: -Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 16.4, z: 67, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 11.6, z: 67, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -11.6, z: 67, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -16.4, z: 67, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: 7.6, z: 67, yaw: Math.PI, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: -7.6, z: 67, yaw: Math.PI, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 0, z: 87, yaw: Math.PI, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: 16.72, z: 69.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 13.28, z: 69.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -13.28, z: 69.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -16.72, z: 69.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    // Same fort shape, mirrored — see the note on the other spawn's masks.
    { propId: 'clip_wall_move', x: 0, z: -91, yaw: 0, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 30, z: -78, yaw: Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -30, z: -78, yaw: Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -16.4, z: -67, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -11.6, z: -67, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 11.6, z: -67, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 16.4, z: -67, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: -7.6, z: -67, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 7.6, z: -67, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 0, z: -87, yaw: 0, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: -16.72, z: -69.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -13.28, z: -69.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 13.28, z: -69.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 16.72, z: -69.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall', x: 12.73, z: 12.73, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 16.66, z: 16.66, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 20.59, z: 20.59, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 24.52, z: 24.52, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 28.45, z: 28.45, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 32.39, z: 32.39, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 42.43, z: 42.43, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 46.36, z: 46.36, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 50.29, z: 50.29, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 54.22, z: 54.22, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 58.15, z: 58.15, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 62.08, z: 62.08, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 66.02, z: 66.02, yaw: -Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -12.73, z: 12.73, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -16.66, z: 16.66, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -20.59, z: 20.59, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -24.52, z: 24.52, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -28.45, z: 28.45, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -32.39, z: 32.39, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -42.43, z: 42.43, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -46.36, z: 46.36, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -50.29, z: 50.29, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -54.22, z: 54.22, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -58.15, z: 58.15, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -62.08, z: 62.08, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -66.02, z: 66.02, yaw: (-3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -12.73, z: -12.73, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -16.66, z: -16.66, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -20.59, z: -20.59, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -24.52, z: -24.52, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -28.45, z: -28.45, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -32.39, z: -32.39, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -42.43, z: -42.43, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -46.36, z: -46.36, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -50.29, z: -50.29, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -54.22, z: -54.22, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -58.15, z: -58.15, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -62.08, z: -62.08, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: -66.02, z: -66.02, yaw: (3 * Math.PI) / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 12.73, z: -12.73, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 16.66, z: -16.66, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 20.59, z: -20.59, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 24.52, z: -24.52, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 28.45, z: -28.45, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 32.39, z: -32.39, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 42.43, z: -42.43, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 46.36, z: -46.36, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 50.29, z: -50.29, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 54.22, z: -54.22, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 58.15, z: -58.15, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 62.08, z: -62.08, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 66.02, z: -66.02, yaw: Math.PI / 4, clipSize: { halfX: 2.5, halfZ: 0.22, height: 2.34 } },
    { propId: 'clip_wall', x: 30, z: 46, yaw: -0.5, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall', x: 22, z: 36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 26.8, z: 36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 31.6, z: 36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 36.4, z: 36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall_move', x: 36, z: 62, yaw: 0.4, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall', x: 34, z: 20, yaw: 1.2, clipSize: { halfX: 3.02, halfZ: 1.26, height: 2.35 } },
    { propId: 'clip_wall_move', x: 12, z: 6, yaw: 0, clipSize: { halfX: 0.99, halfZ: 0.98, height: 1.98 } },
    { propId: 'clip_wall', x: -30, z: 46, yaw: -2.642, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall', x: -22, z: 36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -26.8, z: 36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -31.6, z: 36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -36.4, z: 36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall_move', x: -36, z: 62, yaw: 2.742, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall', x: -34, z: 20, yaw: 1.942, clipSize: { halfX: 3.02, halfZ: 1.26, height: 2.35 } },
    { propId: 'clip_wall_move', x: -12, z: 6, yaw: Math.PI, clipSize: { halfX: 0.99, halfZ: 0.98, height: 1.98 } },
    { propId: 'clip_wall', x: 0, z: 26, yaw: 0, clipSize: { halfX: 6.53, halfZ: 4.95, height: 12.93 } },
    { propId: 'clip_wall', x: 0, z: 40, yaw: 0, clipSize: { halfX: 2.7, halfZ: 0.41, height: 5.49 } },
    { propId: 'clip_wall', x: 30, z: -46, yaw: 0.5, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall', x: 22, z: -36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 26.8, z: -36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 31.6, z: -36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: 36.4, z: -36, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall_move', x: 36, z: -62, yaw: -0.4, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall_move', x: 40, z: -48, yaw: 0, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall', x: 34, z: -20, yaw: -1.2, clipSize: { halfX: 3.02, halfZ: 1.26, height: 2.35 } },
    { propId: 'clip_wall_move', x: 12, z: -6, yaw: 0, clipSize: { halfX: 0.99, halfZ: 0.98, height: 1.98 } },
    { propId: 'clip_wall', x: -30, z: -46, yaw: 2.642, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall', x: -22, z: -36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -26.8, z: -36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -31.6, z: -36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall', x: -36.4, z: -36, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.77, height: 1.8 } },
    { propId: 'clip_wall_move', x: -36, z: -62, yaw: -2.742, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall', x: -34, z: -20, yaw: -1.942, clipSize: { halfX: 3.02, halfZ: 1.26, height: 2.35 } },
    { propId: 'clip_wall_move', x: -12, z: -6, yaw: Math.PI, clipSize: { halfX: 0.99, halfZ: 0.98, height: 1.98 } },
    { propId: 'clip_wall', x: 0, z: -26, yaw: 0, clipSize: { halfX: 6.53, halfZ: 4.95, height: 12.93 } },
    { propId: 'clip_wall', x: 0, z: -40, yaw: 0, clipSize: { halfX: 2.7, halfZ: 0.41, height: 5.49 } },
    { propId: 'clip_wall', x: 60, z: 14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 65.2, z: 14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 70.4, z: 14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 75.6, z: 14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 88, z: 30, yaw: Math.PI / 2, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall_move', x: 50, z: 22, yaw: 0.2, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall_move', x: 36, z: 12, yaw: 0, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall', x: 46, z: 10, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 60, z: -14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 65.2, z: -14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 70.4, z: -14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 75.6, z: -14, yaw: 0, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: 88, z: -30, yaw: -Math.PI / 2, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall_move', x: 50, z: -22, yaw: -0.2, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall_move', x: 36, z: -12, yaw: 0, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall', x: 46, z: -10, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 56, z: 0, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -60, z: 14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -65.2, z: 14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -70.4, z: 14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -75.6, z: 14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -88, z: 30, yaw: Math.PI / 2, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall_move', x: -50, z: 22, yaw: 2.942, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall', x: -46, z: 10, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -60, z: -14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -65.2, z: -14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -70.4, z: -14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -75.6, z: -14, yaw: Math.PI, clipSize: { halfX: 2.34, halfZ: 0.41, height: 2.44 } },
    { propId: 'clip_wall', x: -88, z: -30, yaw: -Math.PI / 2, clipSize: { halfX: 6.8, halfZ: 3.6, height: 11.12 } },
    { propId: 'clip_wall_move', x: -50, z: -22, yaw: -2.942, clipSize: { halfX: 2.7, halfZ: 1.26, height: 1.43 } },
    { propId: 'clip_wall', x: -46, z: -10, yaw: Math.PI, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -56, z: 0, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -86, z: 1, yaw: Math.PI / 2, clipSize: { halfX: 3, halfZ: 2.75, height: 9 } },
    { propId: 'clip_wall_move', x: -86, z: -3, yaw: 0, clipSize: { halfX: 1, halfZ: 1, height: 3 } },
    { propId: 'clip_wall', x: 86, z: 1, yaw: Math.PI / 2, clipSize: { halfX: 3, halfZ: 2.75, height: 9 } },
    { propId: 'clip_wall_move', x: 86, z: -3, yaw: Math.PI, clipSize: { halfX: 1, halfZ: 1, height: 3 } },
    { propId: 'clip_wall_move', x: 40, z: 48, yaw: 0, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall_move', x: -40, z: 48, yaw: Math.PI, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall_move', x: -36, z: 12, yaw: Math.PI, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall_move', x: -40, z: -48, yaw: Math.PI, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
    { propId: 'clip_wall_move', x: -36, z: -12, yaw: Math.PI, clipSize: { halfX: 1.2, halfZ: 1.2, height: 13.5 } },
  ],
};

// ─── 6. Karsten Basin ────────────────────────────────────────────────────────

const BASIN_HALF = 220;
const BASIN_BASES = bases(
  { x: 0, z: 170, facing: SOUTH },
  { x: 0, z: -170, facing: NORTH },
  citadel,
);

/**
 * Fifteen a side, on a front too long for any one of them to hold.
 *
 * **The map that is about not being able to be everywhere.** It is 440 m square
 * — 4.8 times the area of the other six — because thirty tanks on a 200 m square
 * have 6.7 m of frontage each, which is not a battle, it is a scrum. At this
 * size each tank has about 6,450 m², the same room a tank has in a 3 v 3 on
 * Ironback Ridge, and base to base is 340 m, or 24 seconds at full speed.
 *
 * **It is cut into five north–south routes, and that is the whole design.** A
 * fifteen-a-side fight on open ground is one blob of thirty tanks in the middle;
 * what makes it a battle is several fights happening at once, none of which any
 * one player can see the whole of. Reading across:
 *
 *   - **The west works** (|x| > 114) — three staggered rows of warehouses per
 *     half with 10 m alleys between them, so nothing out here sees further than
 *     about 25 m. Slag Foundry's fight, on the flank of a much bigger one.
 *   - **The west spoil ridge** (x = -110) — a rock spine running almost the
 *     whole length of the map, with exactly one 24 m pass per half and a 14.5 m
 *     lane squeezing past behind each base. Crossing it is a decision.
 *   - **The west approach** (-106 < x < -50) — broken ground of bunkers, ruins
 *     and rubble between a base and the works, with a hedge line across it.
 *   - ...and the same two, mirrored, to the east.
 *   - **The works, in the middle** — a 100 m walled compound on the origin with
 *     a gate in each side.
 *
 * Five routes and about three tanks a side in each is what the roster buys: a
 * team that sends everybody down one wins that one and loses the other four.
 *
 * **The hedge lines are what stop the approaches being 340 m alleys.** They run
 * east–west at z = ±100 with three openings each, and `hedgerow` stops a shell
 * without stopping a tank — so the line breaks the sight line from a base gate
 * to the middle without being a wall anybody has to queue at. That is the whole
 * reason they are hedges and not concrete: at this scale a real wall across the
 * approach would turn thirty tanks into two files.
 *
 * **The middle is worth having and cannot be kept.** Four gates, four fuel
 * silos and four warehouses inside making a cross of avenues, and it is where
 * the crates fall — 154 m of scatter around the origin, with 80 m around each
 * citadel refused. Fifteen tanks can hold three of its four gates and the fourth
 * is then a free entry into the middle of an enemy already committed outwards,
 * which is Kestrel Depot's bargain at five times the roster.
 *
 * **The rear lanes are deliberate, not an oversight.** Each spoil ridge stops
 * 14.5 m short of the arena wall behind each base, so a flank can be rotated
 * without crossing the middle — a long, unsupported drive right past your own
 * back wall, which is exactly the cost that makes it a choice.
 *
 * Generated for the north-west quadrant and reflected twice, so no side and no
 * flank has a metre more cover than any other.
 */
const BASIN_NORTH_WEST: PlacedProp[] = [
  // ── The west spoil ridge, north arm ──
  // Footprint z = 84.5 .. 205.5, so there is a 24 m pass below it and a 14.5 m
  // lane above it, behind the base.
  ...run('rock_outcrop', -110, 200, EAST, 11),
  // A pine belt in the lee of the ridge. A mature pine blocks five metres of
  // ground, so this is a second obstacle line rather than scenery — which is
  // why it is a line of four with sixteen metres between them and not a wood.
  { propId: 'tree_pine', x: -102, z: 190, yaw: 0 },
  { propId: 'tree_pine', x: -102, z: 174, yaw: 0 },
  { propId: 'tree_pine', x: -102, z: 158, yaw: 0 },
  { propId: 'tree_pine', x: -102, z: 142, yaw: 0 },

  // ── The west works ──
  // Three rows, staggered so no alley runs the length of the map. Blocks are
  // 18–20 m wide with 10 m or more between them; the outermost leaves a lane
  // against the arena wall. Warehouses and industrial units alternate so the
  // works reads as a plant rather than as one building printed nine times, and
  // the substitution is deliberately width-neutral: `industrial_unit` is 1.6 m
  // narrower than `warehouse_brick`, so every alley is that much wider than it
  // was and none is narrower.
  { propId: 'warehouse_brick', x: -135, z: 40, yaw: 0 },
  { propId: 'industrial_unit', x: -165, z: 40, yaw: 0 },
  { propId: 'warehouse_brick', x: -195, z: 40, yaw: 0 },
  { propId: 'industrial_unit', x: -145, z: 100, yaw: 0 },
  { propId: 'warehouse_brick', x: -175, z: 100, yaw: 0 },
  { propId: 'industrial_unit', x: -205, z: 100, yaw: 0 },
  { propId: 'warehouse_brick', x: -140, z: 160, yaw: 0 },
  { propId: 'industrial_unit', x: -170, z: 160, yaw: 0 },
  { propId: 'warehouse_brick', x: -200, z: 160, yaw: 0 },
  // Cross-pieces standing in the alleys, so the works is corners rather than a
  // grid you can shoot down. The boiler house is the tallest thing in the game
  // and is here to be seen over the rows from the basin floor.
  { propId: 'terrace_row', x: -150, z: 70, yaw: EAST },
  { propId: 'boiler_house', x: -190, z: 70, yaw: 0 },
  { propId: 'terrace_row', x: -160, z: 130, yaw: EAST },
  { propId: 'factory_hall', x: -200, z: 130, yaw: EAST },
  { propId: 'ruin_roofless', x: -125, z: 70, yaw: 0.3 },
  { propId: 'ruin_roofless', x: -127, z: 130, yaw: 0.4 },
  { propId: 'chapel_stone', x: -186, z: 194, yaw: 0.2 },
  { propId: 'ruin_warehouse', x: -152, z: 194, yaw: -0.3 },

  // ── The west approach ──
  // The hedge line, z = 100. Three openings: 19 m on the centre line (written
  // once, in `BASIN_CENTRE`'s mirror partner below) and 10.5 m at |x| = 51.5..62.
  // Hedges stop a shell and not a tank, so this breaks the sight line without
  // being a queue. The inner half is the overgrown hedge, which is the same
  // section at the same spacing — so the openings are untouched and the line
  // stops reading as one endlessly repeated bush.
  ...span('hedgerow', -83, 100, 0, 7),
  ...span('hedgerow_wild', -30.5, 100, 0, 7),
  // Hard cover, scattered rather than laid out — the basin floor.
  { propId: 'bunker_concrete', x: -30, z: 70, yaw: 0.3 },
  { propId: 'bunker_concrete', x: -80, z: 70, yaw: -0.3 },
  { propId: 'rock_outcrop', x: -20, z: 58, yaw: -0.35 },
  { propId: 'rock_outcrop', x: -72, z: 62, yaw: 0.35 },
  { propId: 'spoil_heap', x: -62, z: 122, yaw: -0.4 },
  { propId: 'tree_birch_clump', x: -60, z: 90, yaw: 0.3 },
  { propId: 'tree_fallen', x: -40, z: 88, yaw: 0.6 },
  { propId: 'rubble_pile', x: -72, z: 146, yaw: 0.5 },
  { propId: 'ruin_roofless', x: -45, z: 132, yaw: -0.2 },
  // At (-95, 132) this ruin sat across the farmyard wall's west door and — with
  // the pines solid at the trunk — resealed the yard the shortened wall run
  // below had just opened. Eight metres east it is the same scattered cover
  // with the channel between pine belt and wall end left clear.
  { propId: 'ruin_roofless', x: -88, z: 128, yaw: 0.2 },
  // A farmstead on the basin floor, west of the citadel's flank — the ground a
  // rotation between the works and the middle actually drives through. It sits
  // beyond x = -70 because the citadel itself is 122 m across and reaches
  // x = ±61; a farm inside that is a farm inside somebody's fort.
  { propId: 'farmhouse_long', x: -80, z: 152, yaw: 0 },
  { propId: 'barn_wooden', x: -93, z: 164, yaw: EAST },
  // Four sections, not six: with the pines solid at the trunk, the west end of
  // the full run met the pine at (-102, 142) and closed the farmyard's west
  // door — the flood fill measured the whole yard as a 60-cell pocket. One
  // section shorter was still not enough: 7 m of clear ground minus the tank
  // radius inflated off both sides leaves a ~1 m standable band, which the
  // flood fill's 2 m grid steps straight over. Two sections shorter gives the
  // door ~12 m of clear ground — a real gap, not a grid-width one.
  ...run('wall_drystone', -86.4, 136, 0, 4),
  { propId: 'tree_oak', x: -72, z: 132, yaw: 0.4 },
  // Overlooking the pass through the ridge.
  { propId: 'guard_tower', x: -95, z: 90, yaw: 0 },
  { propId: 'water_tower', x: -88, z: 40, yaw: 0 },
];

/**
 * On the west centre line, so it belongs to neither half and is written once.
 *
 * The ridge's inner span. It is centred on z = 0 and is therefore symmetric
 * about it already; running it through `mirrorZ` would stack a second copy of
 * every section on top of the first.
 */
const BASIN_WEST_MIDDLE: PlacedProp[] = [
  ...span('rock_outcrop', -110, 0, EAST, 10),
  { propId: 'fuel_silo', x: -160, z: 0, yaw: 0 },
  { propId: 'silo_battery', x: -186, z: 0, yaw: 0 },
];

const BASIN_WEST: PlacedProp[] = [
  ...BASIN_NORTH_WEST,
  ...mirrorZ(BASIN_NORTH_WEST),
  ...BASIN_WEST_MIDDLE,
];

/**
 * The works in the middle, and the two ruins on the centre lane.
 *
 * Written once and symmetric about both axes by construction, because
 * everything in it straddles or sits on x = 0 — mirroring it would stack it on
 * itself.
 */
const BASIN_CENTRE: PlacedProp[] = [
  // The compound: a 100 m square with a 22 m gate in each side. Each side is two
  // seven-section runs, and the corners close because the runs overlap there.
  ...span('wall_concrete', -30.5, 50, 0, 7),
  ...span('wall_concrete', 30.5, 50, 0, 7),
  ...span('wall_concrete', -30.5, -50, 0, 7),
  ...span('wall_concrete', 30.5, -50, 0, 7),
  ...span('wall_concrete', 50, 30.5, EAST, 7),
  ...span('wall_concrete', 50, -30.5, EAST, 7),
  ...span('wall_concrete', -50, 30.5, EAST, 7),
  ...span('wall_concrete', -50, -30.5, EAST, 7),

  // Four silos, leaving a cross of 33 m avenues meeting on the origin — which is
  // where `pickDropSpot` falls back to, and is kept clear on every map for
  // exactly that reason.
  { propId: 'fuel_silo', x: -24, z: 24, yaw: 0 },
  { propId: 'fuel_silo', x: 24, z: 24, yaw: 0 },
  { propId: 'fuel_silo', x: -24, z: -24, yaw: 0 },
  { propId: 'fuel_silo', x: 24, z: -24, yaw: 0 },
  // One warehouse inside each gate, so a gate is covered without being blocked:
  // you come in and go round it, which is where the fight for the works happens.
  { propId: 'warehouse_brick', x: -34, z: 0, yaw: EAST },
  { propId: 'warehouse_brick', x: 34, z: 0, yaw: EAST },
  { propId: 'warehouse_brick', x: 0, z: 34, yaw: 0 },
  { propId: 'warehouse_brick', x: 0, z: -34, yaw: 0 },

  // The centre lane runs from one citadel's gate to the works' north gate. These
  // are the only thing in 98 m of it, and they are why it is a lane rather than
  // a firing range.
  { propId: 'ruin_roofless', x: 0, z: 120, yaw: 0.25 },
  { propId: 'ruin_roofless', x: 0, z: -120, yaw: -0.25 },
];

/**
 * The plant inside the works, one quadrant of it.
 *
 * Reflected twice rather than written four times, for the reason everything
 * else in this file is: four hand-written corners are four chances to be eight
 * metres out on one of them.
 *
 * **One prop per corner, and the corners are the only place it goes.** Cargo
 * stacks in front of the gate warehouses were tried and measured: they turned
 * each 27.6 m gate into a chicane and pushed the worst bot on this map from
 * 3.45 m/s to under 3. The four gates and the four avenues are what make
 * fifteen a side a battle instead of a queue, and nothing goes in either.
 */
const BASIN_WORKS_NORTH_EAST: PlacedProp[] = [
  { propId: 'silo_battery', x: 40, z: 40, yaw: NORTH_WEST },
];

const BASIN_WORKS_NORTH: PlacedProp[] = [
  ...BASIN_WORKS_NORTH_EAST,
  ...mirrorX(BASIN_WORKS_NORTH_EAST),
];

export const MAP_BASIN: MapDefinition = {
  id: 'basin',
  name: 'Karsten Basin',
  blurb: 'Fifteen a side across five routes. Nobody can hold all of them.',
  halfExtent: { x: BASIN_HALF, z: BASIN_HALF },
  teamSize: 15,
  bases: BASIN_BASES.bases,
  props: [
    // Collision lives on invisible masks at 90% of each prop's own box,
    // not on the props themselves — see `clipMasked` in `clip-masks.ts`.
    ...clipMasked([
    ...BASIN_BASES.props,
    ...BASIN_WEST,
    ...mirrorX(BASIN_WEST),
    ...BASIN_CENTRE,
    ...BASIN_WORKS_NORTH,
    ...mirrorZ(BASIN_WORKS_NORTH),
      ]),
  ],
};

// ─── The registry ────────────────────────────────────────────────────────────

/**
 * Every map, by id.
 *
 * Ordered the way the picker shows them: the five three-a-side team maps, then
 * the fifteen-a-side one, then the two that predate bases.
 */
/**
 * A gunnery range: bare ground, three targets, and nothing shooting back.
 *
 * Built for the question *"did that shot actually register?"*, which is exactly
 * the question every other map is bad at answering — on a dressed map a round
 * that does nothing might have hit a hedge, and the tracer flies on either way
 * because it knows nothing about props. That ambiguity is not hypothetical: it
 * hid a real bug where a tank standing inside a canopy was unhittable.
 *
 * So this map removes every variable but the shot. **No props at all**, so
 * nothing can eat a round; `gunneryRange`, so nothing shoots back and no
 * team-mate wanders into the line; and no base, so `pickSpawn` uses the open
 * ring and the targets scatter rather than huddling in a compound.
 *
 * 200 m square is deliberate against the enlarged maps: the longest gun reaches
 * 260 m, so every target is inside every weapon's range from anywhere on the
 * map, and a miss is a miss rather than a round falling short.
 */
const MAP_RANGE: MapDefinition = {
  id: 'range',
  name: 'Gunnery Range',
  blurb: 'Bare ground and three tanks that do not shoot back. For testing a gun, not for a fight.',
  halfExtent: { x: 100, z: 100 },
  // Three targets, and — because a range only fills the side without humans —
  // three is what you face rather than three split across both sides.
  teamSize: 3,
  gunneryRange: true,
  props: [],
};

// ─── Mission battlefields ────────────────────────────────────────────────────
//
// Three maps built for the three PvE missions. They carry their full mission
// layout — the route, the gates, the core's post and the uplink — and they were
// registered **bare**, which was a deliberate order of work rather than an
// unfinished one: the layout is the contract every mission's logic and every
// test is written against, and dressing it is a separate pass per map. A mission
// that plays correctly on bare ground and then gets buildings is a mission whose
// failures are the buildings' fault, which is the only way to tell a layout bug
// from a navigation one. `convoy` has had that pass; `relay` and `redoubt` have
// not.
//
// All three are **unpickable**. A mission's map is implied by its mode and
// forced server-side, so offering them beside Ironback Ridge would let a player
// choose a convoy road and then fight a deathmatch down it.

/**
 * The convoy road, district by district.
 *
 * Five of them, south to north, and each is a different *kind* of fight rather
 * than the same one with different buildings — which is the only reason a 420 m
 * drive is worth five ambushes instead of one.
 *
 * The one rule every district obeys: **nothing that blocks a tank comes within
 * 13 m of the route's centre line.** The contract is a 14 m clear channel (a
 * 3 m circle standing at the sample and at ±7 m perpendicular), 13 m is that
 * plus the circle's own radius, and `maps.test.ts` samples every leg every 2 m
 * and fails for a metre of it. Everything that dresses the road itself is
 * therefore a prop that does *not* block a tank — hedgerows, haystacks, wrecks,
 * craters, sandbags, poles — of which the catalogue has plenty, and which stop
 * shells while letting the convoy through. That is not a workaround; it is what
 * makes the road read as a road with cover on it rather than as a corridor
 * between two rows of boxes.
 */

/**
 * 1 — The marshalling yard. Where the run starts, and the only safe ground.
 *
 * Working and readable: a wire at the back, a depot compound to the east, a
 * steading to the west, and a line of poles marking the road north. The blue
 * spawn slots sit on the pad between them, which is what makes a player's first
 * sight of the mission the convoy twenty metres in front of them.
 */
const CONVOY_YARD: PlacedProp[] = [
  // The wire across the back of the yard. Hesco is solid, so it stands behind
  // the spawn slots rather than beside them.
  ...span('hesco_barrier', 0, -202, 0, 9),
  // The depot compound east of the pad.
  { propId: 'warehouse_brick', x: 50, z: -192, yaw: 0 },
  { propId: 'depot_control', x: 46, z: -168, yaw: 0 },
  { propId: 'cargo_container', x: 34, z: -198, yaw: 0 },
  { propId: 'cargo_container', x: 34, z: -190, yaw: 0 },
  { propId: 'cargo_container', x: 38, z: -180, yaw: Math.PI / 2 },
  { propId: 'fuel_silo', x: 66, z: -176, yaw: 0 },
  // The steading west of the pad.
  { propId: 'farmhouse_long', x: -52, z: -190, yaw: Math.PI / 2 },
  { propId: 'barn_wooden', x: -42, z: -168, yaw: 0 },
  { propId: 'warehouse_brick', x: -62, z: -152, yaw: Math.PI / 2 },
  // The road itself, dressed entirely with things a tank drives through or over.
  // Telegraph lines, placed pole by pole rather than with `span()`.
  //
  // `span` steps by the prop's own `halfX`, which is the right rule for a *wall
  // section* and the wrong one for a pole: the footprint was tightened to the
  // timber when poles became solid (see `utility_pole`), and that turned a run
  // of four into four poles two metres apart — a clump rather than a line. A
  // pole line is poles every twenty-odd metres, which is a placement decision
  // and not a footprint one.
  { propId: 'utility_pole', x: 13, z: -176, yaw: 0 },
  { propId: 'utility_pole', x: 13, z: -152, yaw: 0 },
  { propId: 'utility_pole', x: 13, z: -128, yaw: 0 },
  { propId: 'utility_pole', x: -42, z: -134, yaw: 0 },
  { propId: 'utility_pole', x: -46, z: -110, yaw: 0 },
  { propId: 'utility_pole', x: -48, z: -86, yaw: 0 },
  { propId: 'lorry_loaded', x: -20, z: -178, yaw: 0.4 },
  { propId: 'lorry_loaded', x: 15, z: -191, yaw: -0.3 },
  { propId: 'lorry_loaded', x: 21, z: -178, yaw: 1.4 },
  { propId: 'oil_drums', x: 8, z: -197, yaw: 0 },
  { propId: 'oil_drums', x: 11, z: -194, yaw: 0.7 },
  { propId: 'oil_drums', x: -7, z: -198, yaw: 0 },
  { propId: 'wreck_truck', x: -24, z: -184, yaw: 2.1 },
  ...run('fence_chainlink', 22, -206, 0, 7),
  ...run('fence_chainlink', -40, -206, 0, 7),
  ...run('fence_chainlink', 68, -196, -Math.PI / 2, 6),
  { propId: 'tree_oak', x: 26, z: -158, yaw: 0 },
  { propId: 'tree_oak', x: -30, z: -196, yaw: 0 },
];

/**
 * 2 — The hedged lanes. Farmland either side of a road you can be shot across.
 *
 * The hedgerows are the district: `blocksShells` without `blocksTanks`, so the
 * route can be fired *into* but not through, and a player can burst out of a
 * field the convoy cannot be flanked through without being seen. The steading
 * west of W1 is the only hard cover for eighty metres, and the first ambush
 * comes out of the ground behind it.
 */
const CONVOY_LANES: PlacedProp[] = [
  // The steading. Solid, and comfortably clear of both legs it overlooks.
  { propId: 'farmhouse_long', x: -70, z: -122, yaw: Math.PI / 2 },
  { propId: 'barn_wooden', x: -58, z: -140, yaw: 0 },
  { propId: 'house_shelled', x: -76, z: -102, yaw: 0 },
  { propId: 'barn_wooden', x: -60, z: -84, yaw: Math.PI / 2 },
  ...span('wall_drystone', -66, -112, 0, 7),
  ...span('wall_drystone', -80, -130, Math.PI / 2, 6),
  // East of the road: a second steading, so the lanes are not one-sided.
  { propId: 'farmhouse_long', x: 48, z: -106, yaw: 0 },
  { propId: 'barn_wooden', x: 44, z: -128, yaw: 0 },
  { propId: 'house_shelled', x: 30, z: -84, yaw: 0 },
  ...span('wall_drystone', 44, -116, 0, 6),
  // Field boundaries. None of these stops a tank; all of them stop a round.
  ...span('hedgerow', 24, -150, 0, 12),
  ...span('hedgerow', 30, -118, 0, 10),
  ...span('hedgerow_wild', 18, -66, 0, 11),
  ...span('hedgerow', -52, -158, 0, 12),
  ...span('hedgerow_wild', -56, -70, 0, 12),
  ...span('hedgerow', -26, -58, 0, 9),
  ...span('hedgerow_wild', -96, -142, Math.PI / 2, 8),
  ...span('hedgerow', 68, -110, Math.PI / 2, 8),
  // Cover **beside** the road. These were on it — the comment here read
  // "drive-through cover on the road itself" — which is what a haystack was
  // before it became five metres of solid barn.
  { propId: 'haystack', x: -34, z: -146, yaw: 0 },
  { propId: 'haystack', x: 6, z: -154, yaw: 0 },
  { propId: 'haystack', x: -44, z: -122, yaw: 0 },
  { propId: 'haystack', x: -6, z: -96, yaw: 0 },
  { propId: 'haystack', x: 4, z: -62, yaw: 0 },
  { propId: 'tree_oak', x: -6, z: -128, yaw: 0 },
  { propId: 'tree_oak', x: 12, z: -92, yaw: 0 },
  { propId: 'tree_oak', x: -46, z: -100, yaw: 0 },
  { propId: 'tree_oak', x: 26, z: -70, yaw: 0 },
  { propId: 'tree_oak', x: -70, z: -60, yaw: 0 },
  { propId: 'wreck_truck', x: -48, z: -130, yaw: 0.9 },
];

/**
 * 3 — The ford. The waist of the map, and the set piece.
 *
 * Earth banks funnel the road to its narrowest point at W2 and the two flanking
 * gates fire into it from both sides at once. The banks are `berm_earth` and
 * `rubble_bank` — a tank drives over them, a shell does not — so the funnel is
 * a *sightline*, not a wall, which is the difference between a hard leg and a
 * leg that fails the corridor contract. The bridge stands beside the crossing as
 * a landmark rather than on it: `bridge_stone` is solid, and a solid thing on
 * the route is a building in the middle of the road.
 */
const CONVOY_FORD: PlacedProp[] = [
  { propId: 'bridge_stone', x: -38, z: -24, yaw: Math.PI / 2 },
  { propId: 'bridge_stone', x: -38, z: -14, yaw: Math.PI / 2 },
  { propId: 'house_shelled', x: 26, z: -34, yaw: 0.3 },
  { propId: 'barn_wooden', x: -58, z: -36, yaw: 0 },
  { propId: 'ruin_roofless', x: 34, z: -6, yaw: 0.6 },
  { propId: 'ruin_roofless', x: -52, z: 6, yaw: 2.2 },
  { propId: 'cargo_container', x: 62, z: 8, yaw: 0 },
  { propId: 'cargo_container', x: 62, z: 16, yaw: 0 },
  // The funnel, **widened to stay a funnel**.
  //
  // The banks used to be drive-over, which is what let this district's comment
  // call the funnel "a *sightline*, not a wall — which is the difference between
  // a hard leg and a leg that fails the corridor contract". Solid, they were the
  // wall: four of these six runs put a section within thirteen metres of the
  // route, and the west bank at z = -46 reached x = -16 against a route passing
  // at x = -16.9.
  //
  // Every run is the same length and the same shape; they are further out. The
  // sightline is unchanged — a berm still stops the round — and the gap between
  // the two banks is now something a convoy can be funnelled *through* rather
  // than into.
  ...span('berm_earth', -43, -46, 0, 5),
  ...span('berm_earth', 16, -40, 0, 5),
  ...span('berm_earth', -30, 4, 0, 5),
  ...span('berm_earth', 40, 8, 0, 4),
  ...span('rubble_bank', -32, -22, Math.PI / 2, 5),
  ...span('rubble_bank', 22, -18, Math.PI / 2, 5),
  { propId: 'wreck_truck', x: -20, z: -8, yaw: 1.9 },
  { propId: 'wreck_bus', x: 32, z: 16, yaw: 0.5 },
  { propId: 'haystack', x: -40, z: 18, yaw: 0 },
  { propId: 'haystack', x: 46, z: -22, yaw: 0 },
  ...span('hedgerow_wild', -74, -20, 0, 9),
  ...span('hedgerow', 74, -34, 0, 9),
];

/**
 * 4 — The shelled village. The hardest leg, and deliberately so.
 *
 * The route bends east through it and the sightlines onto the convoy are the
 * shortest on the map, so a player who has spent two legs fighting at range has
 * to change how they fight. The street holds its corridor with the ruins standing
 * back from it; the wrecks and rubble banks *in* the street are cover the convoy
 * drives straight through.
 */
const CONVOY_VILLAGE: PlacedProp[] = [
  // West side of the street.
  { propId: 'house_shelled', x: -12, z: 18, yaw: 0 },
  { propId: 'terrace_shelled', x: -22, z: 42, yaw: 0.3 },
  { propId: 'ruin_roofless', x: 2, z: 40, yaw: 0 },
  { propId: 'house_shelled', x: 2, z: 62, yaw: 0.5 },
  { propId: 'terrace_shelled', x: -16, z: 80, yaw: 0 },
  { propId: 'ruin_roofless', x: -30, z: 58, yaw: 0 },
  { propId: 'house_shelled', x: -14, z: 112, yaw: 0.2 },
  { propId: 'warehouse_brick', x: -40, z: 96, yaw: Math.PI / 2 },
  // East side of the street.
  { propId: 'terrace_shelled', x: 46, z: 16, yaw: 0 },
  { propId: 'warehouse_brick', x: 68, z: 44, yaw: 0 },
  { propId: 'house_shelled', x: 60, z: 70, yaw: 0.4 },
  { propId: 'ruin_roofless', x: 46, z: 90, yaw: 0 },
  { propId: 'terrace_shelled', x: 40, z: 112, yaw: 0 },
  { propId: 'house_shelled', x: 64, z: 96, yaw: 0 },
  { propId: 'barn_wooden', x: 80, z: 70, yaw: 0 },
  { propId: 'house_shelled', x: -44, z: 32, yaw: 0 },
  // The street's **verges**. This read "everything here is drive-through", and
  // the wrecks and rubble banks were literally in the carriageway; a shelled
  // village whose wrecks are shoved to the kerb is if anything the more likely
  // picture. The street itself is now the fourteen metres the contract asks for.
  { propId: 'wreck_bus', x: -4, z: 22, yaw: 1.2 },
  { propId: 'wreck_truck', x: 16, z: 44, yaw: 0.4 },
  { propId: 'wreck_truck', x: 16, z: 74, yaw: 2.4 },
  { propId: 'wreck_bus', x: 14, z: 58, yaw: 0.9 },
  { propId: 'wreck_truck', x: 6, z: 96, yaw: 1.7 },
  ...span('rubble_bank', -3, 31, 0.9, 4),
  ...span('rubble_bank', 6, 44, 0.6, 4),
  ...span('rubble_bank', -4, 92, 0.2, 4),
  ...span('rubble_bank', 55, 34, 0, 3),
  ...span('berm_earth', 3, 84, 0, 3),
  { propId: 'haystack', x: 58, z: 56, yaw: 0 },
  { propId: 'tree_oak', x: -30, z: 116, yaw: 0 },
  { propId: 'tree_oak', x: 76, z: 20, yaw: 0 },
  ...span('hedgerow_wild', -70, 40, 0, 10),
  ...span('hedgerow', 96, 60, 0, 10),
];

/**
 * 5 — The depot approach. It opens back up, and that is the point.
 *
 * The mirror of the hedged lanes: after a hundred metres of short sightlines the
 * last leg is open ground with a wire down each side, so the final counterattack
 * has to cross it and long guns finally have somewhere to work. The depot at the
 * end is the terminus — the buildings stand well back from W5, so arriving
 * inside ten metres of it is a matter of driving there rather than of threading
 * a gap.
 */
const CONVOY_DEPOT: PlacedProp[] = [
  { propId: 'warehouse_brick', x: -28, z: 192, yaw: 0 },
  { propId: 'depot_control', x: 0, z: 198, yaw: 0 },
  { propId: 'fuel_silo', x: 30, z: 190, yaw: 0 },
  { propId: 'cargo_container', x: -46, z: 196, yaw: Math.PI / 2 },
  { propId: 'cargo_container', x: -46, z: 188, yaw: Math.PI / 2 },
  { propId: 'cargo_container', x: 46, z: 174, yaw: 0 },
  { propId: 'guard_tower', x: -18, z: 148, yaw: 0 },
  { propId: 'guard_tower', x: 27, z: 152, yaw: 0 },
  { propId: 'barn_wooden', x: 54, z: 196, yaw: 0 },
  // The wire down both sides of the approach, and across the back of the depot.
  ...run('fence_chainlink', -44, 152, -Math.PI / 2, 8),
  ...run('fence_chainlink', 52, 152, -Math.PI / 2, 8),
  ...span('fence_chainlink', 0, 206, 0, 20),
  // Open ground, loosely dressed. The wrecks and the two oaks are solid but
  // stand off the approach lanes; the rest is drive-through or drive-over.
  { propId: 'wreck_truck', x: -14, z: 132, yaw: 1.1 },
  { propId: 'wreck_bus', x: 22, z: 138, yaw: 2.2 },
  { propId: 'haystack', x: -34, z: 160, yaw: 0 },
  { propId: 'haystack', x: 40, z: 140, yaw: 0 },
  ...span('berm_earth', -27, 176, 0, 4),
  ...span('berm_earth', 34, 168, 0, 5),
  ...span('hedgerow', -74, 122, 0, 10),
  ...span('hedgerow_wild', 76, 124, 0, 10),
  ...span('hedgerow', -100, 186, Math.PI / 2, 6),
  { propId: 'utility_pole', x: -12, z: 124, yaw: 0 },
  { propId: 'utility_pole', x: -14, z: 148, yaw: 0 },
  { propId: 'utility_pole', x: -16, z: 172, yaw: 0 },
  { propId: 'tree_oak', x: -60, z: 140, yaw: 0 },
  { propId: 'tree_oak', x: 66, z: 172, yaw: 0 },
];

/**
 * Where a human starts, and where the counterattack forms.
 *
 * `bases` is a record over **both** sides, and an escort wants only one — so
 * this is the one place the mission maps depart from what a `MapBase` usually
 * means, and it is worth stating rather than leaving to be discovered.
 *
 * **Blue's is real and load-bearing.** Without it `pickSpawn` falls back to the
 * arena's ring, which on a 420 m map can put a joining player three hundred
 * metres up the road from the convoy they are supposed to be walking beside.
 * The slots are the marshalling yard's pad, behind the start point and in front
 * of the wire.
 *
 * **Red's names ground rather than seats one.** A mission's enemies are dealt at
 * gates with an explicit point and are never respawned, so `pickSpawn` is never
 * asked for red on this map at all. It is put on the staging ground behind G5 —
 * where the fifth ambush actually forms — so that it is a true statement about
 * the layout, and so `pickDropSpot`'s keep-out will not drop a crate into it.
 *
 * There is deliberately **no fort on either**. The design's marshalling yard was
 * a `bastion()` straddling the start pad, and the code refuses it: a bastion's
 * gate is 12 m and the route corridor contract is 14 m, so the convoy's first
 * act would have been to fail the contract on its own start point.
 */
const CONVOY_BASES: Record<TeamId, MapBase> = {
  blue: {
    team: 'blue',
    x: 0,
    z: -192,
    facing: NORTH,
    spawns: [
      { x: -16, z: -196 },
      { x: 0, z: -196 },
      { x: 16, z: -196 },
      { x: -8, z: -188 },
      { x: 8, z: -188 },
      { x: -26, z: -190 },
      { x: 26, z: -190 },
    ],
    keepOut: 30,
  },
  red: {
    team: 'red',
    x: -100,
    z: 150,
    facing: EAST,
    spawns: [
      { x: -100, z: 150 },
      { x: -100, z: 138 },
      { x: -100, z: 162 },
      { x: -100, z: 126 },
      { x: -100, z: 174 },
      { x: -112, z: 144 },
      { x: -112, z: 156 },
    ],
    keepOut: 30,
  },
};

/**
 * The road north, for a convoy that cannot fight.
 *
 * Long axis is the route and that is the whole shape of the map: 280 m across
 * by 420 m of driving, six waypoints, and five gates the ambushes come out of.
 * The rectangle is the proving ground's, which is the one size measured to cost
 * the prediction floor nothing.
 *
 * **The corridor between consecutive waypoints is the contract.** The convoy
 * is driven by local avoidance, so every leg is straight by design and no leg
 * asks the driver to solve a corner — see `MissionLayout.route`.
 *
 * It is asymmetric on purpose, like every PvE map: the fairness ledger the team
 * maps are held to has nothing to say about a road with one end. What replaces
 * it is the corridor contract, the gate pockets and the drivability sweep, all
 * of which `maps.test.ts` measures.
 */
const MAP_CONVOY: MapDefinition = {
  id: 'convoy',
  name: 'Convoy Run',
  blurb: 'Walk a supply tank the length of the valley. Five ambushes between it and the depot.',
  halfExtent: { x: 140, z: 210 },
  teamSize: 4,
  mission: {
    kind: 'escort',
    route: [
      { x: 0, z: -180 },
      { x: -34, z: -110 },
      { x: -10, z: -20 },
      { x: 42, z: 55 },
      { x: 8, z: 130 },
      { x: 0, z: 178 },
    ],
    // One gate per ambush, each well off the road so an ambush arrives from a
    // flank rather than materialising in front of the convoy.
    gates: [
      { x: -95, z: -105, yaw: Math.PI / 2 },
      { x: -90, z: -10, yaw: Math.PI / 2 },
      { x: 85, z: 20, yaw: -Math.PI / 2 },
      { x: 110, z: 70, yaw: -Math.PI / 2 },
      { x: -100, z: 150, yaw: Math.PI / 2 },
    ],
  },
  bases: CONVOY_BASES,
  props: [
    // Collision lives on invisible masks at 90% of each prop's own box,
    // not on the props themselves — see `clipMasked` in `clip-masks.ts`.
    ...clipMasked([
    ...CONVOY_YARD,
    ...CONVOY_LANES,
    ...CONVOY_FORD,
    ...CONVOY_VILLAGE,
    ...CONVOY_DEPOT,
      ]),
  ],
};

/**
 * The relay, lane by lane.
 *
 * Three routes from the uplink to the fort and each is a different *kind* of run,
 * which is the only reason a 120 m carry is worth dressing at all: the west works
 * are short sightlines and hard corners, the centre green is fast and lethal, and
 * the east closes are cover you can drive through. A raider chooses on the way in
 * and again — under pressure, carrying — on the way out, and the two choices are
 * rarely the same.
 *
 * **The rule every lane obeys: a lane holds a 14 m navigable channel.** A 3 m
 * circle stands legally on the centre line and at ±7 m across it, which is 20 m
 * of physical clear ground — `TANK_RADIUS` is 3 and the navigator inflates every
 * obstacle by a further 3, so 20 m of ground is 14 m a bot can actually use, and
 * 10 m is the working floor. `maps.test.ts` walks all three lanes every 2 m and
 * fails for a metre of it.
 *
 * That is why almost everything *on* a lane is a prop that does not block a tank
 * — hedgerows, orchard, haystacks, jersey barriers, berms, craters, spoil. Those
 * stop rounds and let a hull through, so a lane reads as a lane with cover on it
 * rather than as a corridor between two rows of boxes. Everything solid stands
 * back off the centre line by 10 m plus its own half-extent.
 */

/**
 * Design notes for `relay`'s districts, kept after the map was baked to
 * flat prop literals (see `clip-masks.ts`'s note on `props: [` anchoring the
 * map editor's BAKE) — the geometry these describe is unchanged, only no
 * longer built from the named constants (`RELAY_FORT`, `RELAY_UPLINK`, …)
 * this prose originally sat on. `relay`'s own clip masks are also 12%
 * smaller than `MASK_SCALE`'s standard 90% (baked in, not a live parameter
 * any more), to allow a little more bleed into this map's objects.
 *
 * ── The fort, reused whole ──────────────────────────────────────────────
 *
 * The fort, reused whole.
 *
 * `bastion` is the standard field fortification five PvP maps already share, and
 * its **three entrances** are exactly what a raid target needs: a 12 m gate on the
 * centre line and a 10.3 m gap at each front corner, so the garrison cannot hold
 * it with three tanks standing on three doors. Its own anti-turtle property is the
 * property being borrowed.
 *
 * The core stands at (0, 58) — inside, one metre behind the middle sandbag line,
 * which stops rounds and lets a hull over. So the last two metres of a raid are a
 * drive over cover rather than a squeeze through a gap, and a defender holding the
 * line is hull-down between the raider and the thing they came for.
 *
 * The gate is 12 m, which is **below the 14 m the lanes are held to**, and that is
 * deliberate rather than an oversight: a fort with a 20 m gate is a courtyard. The
 * lane contract stops 7 m short of the front line and the last stretch is tested
 * separately, as a walk from the gate mouth to the core.
 *
 * ── The uplink ──────────────────────────────────────────────────────────
 *
 * The uplink: half a compound, open to the north.
 *
 * A hesco back wall, two sandbag wings and a command post — and the wings are
 * **sandbags rather than hesco on purpose**. They stop rounds and not hulls, so
 * the uplink is somewhere to fight from and can never be somewhere to be
 * besieged in: a carrier coming home under fire drives over the wing wherever
 * they meet it rather than round to the mouth. Measured, the mouth between the
 * wings' inner faces is 32.6 m.
 *
 * Nothing solid stands inside the capture ring — 12 m about (0, −64) — which is
 * the one clearance the mission's own arithmetic depends on: a carrier's centre
 * inside that radius is a capture, so a building in it would be a capture that
 * cannot be driven to.
 *
 * ── West lane — the works ───────────────────────────────────────────────
 *
 * West lane — the works. The sneak route.
 *
 * Two rows of industrial blocks with a dog-legged alley between them: short
 * sightlines, a corner to break contact round, and the only route on the map a
 * carrier can use without being seen from the fort. The price is that it is the
 * longest way home and the alley has exactly one width — there is nowhere to go
 * sideways if the garrison guesses right.
 *
 * The alley's centre line runs (−62, −58) → (−62, 10) → (−44, 44); the dog-leg is
 * the shoulder at (−74, 40) closing the straight continuation, so the last thirty
 * metres swing east and come out on the fort's own approach.
 *
 * ── Centre — the green ──────────────────────────────────────────────────
 *
 * Centre — the green. The race route.
 *
 * Open ground straight down the axis of the map: the fastest way in, the fastest
 * way out, and the one route the fort can shoot the whole length of. The memorial
 * and the two bunkers are the only hard cover on it and they are deliberately
 * *offset* — a raider running the green is choosing speed over cover and the map
 * should not quietly give them both.
 *
 * The memorial stands at (14, 2) rather than the design's (6, 0), and it had to:
 * six metres off the axis puts it 4.5 m from the centre lane's own centre line, so
 * the lane's 14 m channel and the memorial's stated position contradict each
 * other. Fourteen metres out it is cover a raider can choose to use.
 *
 * ── East lane — the closes ──────────────────────────────────────────────
 *
 * East lane — the closes. The fighting-retreat route home.
 *
 * A hedgerow field system with an orchard block in the middle of it. Nothing here
 * stops a tank and almost everything stops a round, so a carrier who takes the
 * closes can break line of sight every twenty metres without ever losing speed —
 * which is exactly the route a raider wants when three defenders are converging.
 * The gaps in the hedge lines are the fast diagonals; the closes are slower than
 * the green and far harder to shoot down.
 *
 * ── Bases ───────────────────────────────────────────────────────────────
 *
 * Where a raider starts, and where the garrison comes back.
 *
 * `bases` is a record over both sides and a raid wants the two halves to mean
 * different things — the same departure `convoy` makes, worth restating because
 * the reason here is sharper.
 *
 * **Blue's is real and load-bearing.** Without it `pickSpawn` falls back to the
 * arena's 75 m ring, and on a 200 m square that ring passes straight through the
 * fort's front line — a joining raider could arrive inside the compound they are
 * meant to be raiding, twelve metres from the core. The slots are the uplink's own
 * ground, behind the ring and in front of the back wall.
 *
 * **Red's is the fort's own**, and unlike the convoy's it seats real tanks: the
 * mission deals its garrison at these slots and deals it *back* at them every
 * twelve seconds, walking the list with a counter that only climbs so two
 * respawns come back at different doors.
 *
 * Seven slots for a side of four either way, which is the bastion's own five
 * thirds. The design asks for six at the uplink; six is 1.5, and with exactly as
 * many slots as tanks a revive during a lull finds none free and puts two tanks in
 * one place.
 */

/**
 * A raid on a fort that holds something you want.
 *
 * One-sided capture the flag: the core sits in the enemy fort to the north and
 * has to reach the uplink to the south, three times. There is no second core,
 * because a symmetric flag game against AI would need the AI to *attack* — a
 * second brain, twice the work, and a worse mission than a raid.
 *
 * 200 m square keeps the carry to about 120 m, which is long enough to be a
 * run and short enough that losing the core is not the end of the attempt.
 *
 * It is asymmetric on purpose, like every PvE map: the fairness ledger the team
 * maps are held to has nothing to say about a fort with one attacker. What
 * replaces it is the lane contract, the capture ring's clearance and the
 * drivability sweep, all of which `maps.test.ts` measures.
 */
const MAP_RELAY: MapDefinition = {
  id: 'relay',
  name: 'Relay Raid',
  blurb: 'Steal the signal core out of their fort and carry it home. Three times.',
  halfExtent: { x: 100, z: 100 },
  teamSize: 4,
  mission: {
    kind: 'ctf',
    flagPost: { x: 0, z: 58 },
    captureRing: { x: 0, z: -64 },
    // Where defenders come back. The fort's own ground, so a respawn puts them
    // between the raiders and the thing they are guarding rather than behind.
    gates: [{ x: 0, z: 62, yaw: Math.PI }],
  },
  bases: {
      blue: {
      team: 'blue',
      x: 0,
      z: -70,
      facing: 0,
      spawns: [
        { x: 0, z: -74 },
        { x: -13, z: -73 },
        { x: 13, z: -73 },
        { x: -8, z: -83 },
        { x: 8, z: -83 },
        { x: -24, z: -78 },
        { x: 24, z: -78 },
      ],
      keepOut: 34,
    },
      red: {
      team: 'red',
      x: 0,
      z: 62,
      facing: Math.PI,
      spawns: [
        { x: 16, z: 64 },
        { x: 0, z: 64 },
        { x: -16, z: 64 },
        { x: 8, z: 57 },
        { x: -8, z: 57 },
        { x: 25, z: 70 },
        { x: -25, z: 70 },
      ],
    },
    },
  props: [
    { propId: 'hesco_barrier', x: 27.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 22.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 17.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 12.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 7.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 2.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -2.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -7.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -12.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -17.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -22.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -27.5, z: 75, yaw: Math.PI, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 52, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 57, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 62, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 67, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: 72, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 52, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 57, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 62, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 67, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: 72, yaw: -Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'clip_wall_move', x: 0, z: 75, yaw: Math.PI, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -30, z: 62, yaw: -Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 30, z: 62, yaw: -Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'dragon_teeth', x: 16.4, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 11.6, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -11.6, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -16.4, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 7.6, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -7.6, z: 51, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 0, z: 71, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 16.72, z: 53.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: 53.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: 53.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -16.72, z: 53.5, yaw: Math.PI, blocksTanks: false, blocksShells: false },
    { propId: 'hesco_barrier', x: -25, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -20, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -15, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -10, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -5, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 0, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 5, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 10, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 15, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 20, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 25, z: -88, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'sandbag_wall', x: -17, z: -67.4, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: -70.84, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: -74.28, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: -77.72, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: -81.16, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: -84.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -67.4, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -70.84, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -74.28, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -77.72, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -81.16, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: -84.6, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 0, z: -83, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -28, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -25, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -22, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -19, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -16, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: 16, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: 19, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: 22, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: 25, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: 28, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -44, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -38, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -32, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 32, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 38, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 44, z: -62, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -32, z: -83, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -30, z: -80, yaw: 0.7, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: 31, z: -84, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -30, z: -72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 30, z: -72, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -30, z: -50, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 30, z: -50, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -44, z: -74, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 45.5, z: -80, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'haystack', x: -52, z: -66, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'haystack', x: 54.5, z: -68.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -84, z: -46, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'barn_wooden', x: -84, z: -14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'industrial_unit', x: -84, z: 18, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -74, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'industrial_unit', x: -40, z: -44, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -42, z: -14, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'warehouse_brick', x: -38, z: 12, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'industrial_unit', x: -24, z: 28, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -75, z: -62, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -75, z: -56, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'cargo_container', x: -77, z: -30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'cargo_container', x: -77, z: -25, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -75, z: 2, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -75, z: 8, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'cargo_container', x: -47, z: -62, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'cargo_container', x: -47, z: -57, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'cargo_container', x: -47, z: -30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -47, z: 2, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'container_stack', x: -47, z: 8, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -77, z: -44, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -74, z: -44, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -80, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -77, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -74, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -83, z: 30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -80, z: 30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barrier_jersey', x: -77, z: 30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -70, z: -70, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -68, z: -68, yaw: 0.5, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -74, z: -16, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -50, z: -8, yaw: 1.1, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -32, z: 44, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -78, z: -40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -88, z: 8, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: -76, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: -70, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: -64, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: -49, z: 66, yaw: 0 },
    { propId: 'hedgerow', x: -43, z: 66, yaw: 0 },
    { propId: 'utility_pole', x: -76, z: -76, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -54, z: -76, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -90, z: 34, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -90, z: 52, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'war_memorial', x: 13.5, z: 1, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'bunker_concrete', x: -26, z: 8, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'bunker_concrete', x: 26, z: 9.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -24, z: -30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -18, z: -30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 18, z: -2, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 24, z: -2, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -24, z: 20, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -18, z: 20, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 18, z: 24, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 24, z: 24, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -20.44, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -17, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.56, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.56, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 17, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 20.44, z: 40, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -16.5, z: -45.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: -31, z: -2, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'haystack', x: 20.5, z: 9.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: -22, z: -6, yaw: 0 },
    { propId: 'haystack', x: 23.5, z: 30, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -16, z: 0, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'oil_drums', x: -14, z: 26, yaw: 0.9, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -20, z: -34, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -20, z: -12, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: -20, z: 10, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'tree_orchard', x: 30, z: -34, yaw: 0 },
    { propId: 'tree_orchard', x: -32, z: -32, yaw: 0 },
    { propId: 'farmhouse_long', x: 84, z: -30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'barn_wooden', x: 82, z: 8, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'barn_wooden', x: 74, z: 36, yaw: Math.PI / 2, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: 48, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 54, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 60, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 66, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 72, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 25, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 31, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 37, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 43, z: -46, yaw: 0 },
    { propId: 'hedgerow', x: 47, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 53, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 59, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 65, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 71, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 77, z: -26, yaw: 0 },
    { propId: 'hedgerow', x: 28, z: -8, yaw: 0 },
    { propId: 'hedgerow', x: 34, z: -8, yaw: 0 },
    { propId: 'hedgerow', x: 40, z: -8, yaw: 0 },
    { propId: 'hedgerow', x: 46, z: -8, yaw: 0 },
    { propId: 'hedgerow', x: 52, z: -8, yaw: 0 },
    { propId: 'hedgerow', x: 61, z: -6, yaw: 0 },
    { propId: 'hedgerow', x: 67, z: -6, yaw: 0 },
    { propId: 'hedgerow', x: 73, z: -6, yaw: 0 },
    { propId: 'hedgerow', x: 79, z: -6, yaw: 0 },
    { propId: 'hedgerow', x: 43, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 49, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 55, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 61, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 67, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 73, z: 14, yaw: 0 },
    { propId: 'hedgerow', x: 27, z: 22, yaw: 0 },
    { propId: 'hedgerow', x: 33, z: 22, yaw: 0 },
    { propId: 'hedgerow', x: 39, z: 22, yaw: 0 },
    { propId: 'hedgerow', x: 45, z: 22, yaw: 0 },
    { propId: 'hedgerow', x: 52, z: 34, yaw: 0 },
    { propId: 'hedgerow', x: 58, z: 34, yaw: 0 },
    { propId: 'hedgerow', x: 64, z: 34, yaw: 0 },
    { propId: 'hedgerow', x: 70, z: 34, yaw: 0 },
    { propId: 'hedgerow', x: 76, z: 34, yaw: 0 },
    { propId: 'hedgerow', x: 32, z: 46, yaw: 0 },
    { propId: 'hedgerow', x: 38, z: 46, yaw: 0 },
    { propId: 'hedgerow', x: 44, z: 46, yaw: 0 },
    { propId: 'hedgerow', x: 50, z: 46, yaw: 0 },
    { propId: 'hedgerow', x: 56, z: 46, yaw: 0 },
    { propId: 'tree_orchard', x: 46, z: -18, yaw: 0 },
    { propId: 'tree_orchard', x: 54, z: -18, yaw: 0 },
    { propId: 'tree_orchard', x: 62, z: -18, yaw: 0 },
    { propId: 'tree_orchard', x: 46, z: -10, yaw: 0 },
    { propId: 'tree_orchard', x: 54, z: -10, yaw: 0 },
    { propId: 'tree_orchard', x: 62, z: -10, yaw: 0 },
    { propId: 'tree_orchard', x: 50, z: -2, yaw: 0 },
    { propId: 'tree_orchard', x: 58, z: -2, yaw: 0 },
    { propId: 'haystack', x: 38, z: -34, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: 66, z: -36, yaw: 0 },
    { propId: 'haystack', x: 36, z: 6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: 70, z: 22, yaw: 0 },
    { propId: 'haystack', x: 62, z: 46, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 74, z: -56, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 30, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'spoil_heap', x: 60, z: 52, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgerow', x: 46, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: 52, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: 58, z: 74, yaw: 0 },
    { propId: 'hedgerow', x: 73, z: 66, yaw: 0 },
    { propId: 'hedgerow', x: 79, z: 66, yaw: 0 },
    { propId: 'utility_pole', x: 88, z: 34, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 88, z: 52, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 70, z: -76, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'utility_pole', x: 45.5, z: -80, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'haystack', x: 86, z: 86.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'haystack', x: 62, z: 86, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall_move', x: 16.4, z: 51, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 11.6, z: 51, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -11.6, z: 51, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -16.4, z: 51, yaw: Math.PI, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: 7.6, z: 51, yaw: Math.PI, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: -7.6, z: 51, yaw: Math.PI, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 0, z: 71, yaw: Math.PI, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: 16.72, z: 53.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 13.28, z: 53.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -13.28, z: 53.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -16.72, z: 53.5, yaw: Math.PI, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -25, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -20, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -15, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -10, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -5, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 0, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 5, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 10, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 15, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 20, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 25, z: -88, yaw: 0, clipSize: { halfX: 2.25, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -17, z: -67.4, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: -70.84, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: -74.28, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: -77.72, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: -81.16, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: -84.6, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -67.4, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -70.84, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -74.28, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -77.72, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -81.16, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: -84.6, yaw: Math.PI / 2, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall', x: 0, z: -83, yaw: 0, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: -28, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -25, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -22, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -19, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -16, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: 16, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: 19, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: 22, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: 25, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: 28, z: -66, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall', x: -44, z: -62, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: -38, z: -62, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: -32, z: -62, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 32, z: -62, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 38, z: -62, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 44, z: -62, yaw: 0, clipSize: { halfX: 2.75, halfZ: 1.75, height: 2.81 } },
    { propId: 'clip_wall_move', x: -32, z: -83, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -30, z: -80, yaw: 0.7, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: 31, z: -84, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall', x: -30, z: -72, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 30, z: -72, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -30, z: -50, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 30, z: -50, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -44, z: -74, yaw: 0, clipSize: { halfX: 2.9, halfZ: 2.88, height: 2.27 } },
    { propId: 'clip_wall', x: 45.5, z: -80, yaw: 0, clipSize: { halfX: 2.25, halfZ: 2.25, height: 2.27 } },
    { propId: 'clip_wall', x: -52, z: -66, yaw: 0, clipSize: { halfX: 1.75, halfZ: 1.75, height: 3.89 } },
    { propId: 'clip_wall', x: 54.5, z: -68.5, yaw: 0.015, clipSize: { halfX: 1.25, halfZ: 1, height: 3.89 } },
    { propId: 'clip_wall', x: -84, z: -46.5, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -84, z: -14, yaw: Math.PI / 2, clipSize: { halfX: 4.95, halfZ: 4.21, height: 8.1 } },
    { propId: 'clip_wall', x: -84, z: 18, yaw: Math.PI / 2, clipSize: { halfX: 8.29, halfZ: 6.3, height: 10.13 } },
    { propId: 'clip_wall', x: -74, z: 40, yaw: 0, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -40, z: -44, yaw: Math.PI / 2, clipSize: { halfX: 8.29, halfZ: 6.3, height: 10.13 } },
    { propId: 'clip_wall', x: -42, z: -14, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -38, z: 12, yaw: Math.PI / 2, clipSize: { halfX: 9, halfZ: 5.78, height: 7.32 } },
    { propId: 'clip_wall', x: -24, z: 28, yaw: Math.PI / 2, clipSize: { halfX: 8.29, halfZ: 6.3, height: 10.13 } },
    { propId: 'clip_wall', x: -75, z: -62, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -75, z: -56, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -77, z: -30, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.57, height: 3.3 } },
    { propId: 'clip_wall', x: -77, z: -25, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.57, height: 3.3 } },
    { propId: 'clip_wall', x: -75, z: 2, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -75, z: 8, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -47, z: -62, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.57, height: 3.3 } },
    { propId: 'clip_wall', x: -47, z: -57, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.57, height: 3.3 } },
    { propId: 'clip_wall', x: -47, z: -30, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.57, height: 3.3 } },
    { propId: 'clip_wall', x: -47, z: 2, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall', x: -47, z: 8, yaw: Math.PI / 2, clipSize: { halfX: 2.75, halfZ: 1.34, height: 5.02 } },
    { propId: 'clip_wall_move', x: -77, z: -44, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -74, z: -44, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -80, z: -6, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -77, z: -6, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -74, z: -6, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -83, z: 30, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -80, z: 30, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -77, z: 30, yaw: 0, clipSize: { halfX: 1.35, halfZ: 0.64, height: 1.75 } },
    { propId: 'clip_wall_move', x: -70, z: -70, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -68, z: -68, yaw: 0.5, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -74, z: -16, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -50, z: -8, yaw: 1.1, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -32, z: 44, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall', x: -78, z: -40, yaw: 0, clipSize: { halfX: 2.9, halfZ: 2.88, height: 2.27 } },
    { propId: 'clip_wall', x: -88, z: 8, yaw: 0, clipSize: { halfX: 2.9, halfZ: 2.88, height: 2.27 } },
    { propId: 'clip_wall', x: -76, z: -76, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -54, z: -76, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -90, z: 34, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -90, z: 52, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 13.5, z: 1, yaw: 0, clipSize: { halfX: 1, halfZ: 1, height: 3.77 } },
    { propId: 'clip_wall', x: -26, z: 8, yaw: 0, clipSize: { halfX: 2.82, halfZ: 2.73, height: 3.15 } },
    { propId: 'clip_wall', x: 26, z: 9.5, yaw: 0, clipSize: { halfX: 2.82, halfZ: 2.73, height: 3.15 } },
    { propId: 'clip_wall', x: -24, z: -30, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: -18, z: -30, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 18, z: -2, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 24, z: -2, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: -24, z: 20, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: -18, z: 20, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 18, z: 24, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 24, z: 24, yaw: 0, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall_move', x: -20.44, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -17, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -13.56, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 13.56, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 17, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 20.44, z: 40, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall', x: -31, z: -2, yaw: 0, clipSize: { halfX: 1.5, halfZ: 1.75, height: 2.27 } },
    { propId: 'clip_wall', x: 20.5, z: 10, yaw: 0, clipSize: { halfX: 1.75, halfZ: 1.75, height: 3.89 } },
    { propId: 'clip_wall', x: 23.5, z: 29.5, yaw: 0, clipSize: { halfX: 1.5, halfZ: 1.75, height: 3.89 } },
    { propId: 'clip_wall_move', x: -16, z: 0, yaw: 0, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall_move', x: -14, z: 26, yaw: 0.9, clipSize: { halfX: 1.01, halfZ: 0.9, height: 1.12 } },
    { propId: 'clip_wall', x: -20, z: -34, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -20, z: -12, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: -20, z: 10, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 84, z: -30, yaw: Math.PI / 2, clipSize: { halfX: 6.3, halfZ: 2.74, height: 7.28 } },
    { propId: 'clip_wall', x: 82, z: 8, yaw: 0, clipSize: { halfX: 4.95, halfZ: 4.21, height: 8.1 } },
    { propId: 'clip_wall', x: 74, z: 36, yaw: Math.PI / 2, clipSize: { halfX: 4.95, halfZ: 4.21, height: 8.1 } },
    { propId: 'clip_wall', x: 38, z: -34, yaw: 0, clipSize: { halfX: 1.5, halfZ: 1.75, height: 3.89 } },
    { propId: 'clip_wall', x: 36, z: 6.5, yaw: 0, clipSize: { halfX: 1.75, halfZ: 1.5, height: 3.89 } },
    { propId: 'clip_wall', x: 62, z: 46, yaw: 0, clipSize: { halfX: 1.75, halfZ: 1.5, height: 3.89 } },
    { propId: 'clip_wall', x: 74, z: -56, yaw: 0, clipSize: { halfX: 2.25, halfZ: 2.25, height: 2.27 } },
    { propId: 'clip_wall', x: 30, z: -6, yaw: 0, clipSize: { halfX: 2.9, halfZ: 2.88, height: 2.27 } },
    { propId: 'clip_wall', x: 60, z: 52, yaw: 0, clipSize: { halfX: 2.25, halfZ: 2.25, height: 2.27 } },
    { propId: 'clip_wall', x: 88, z: 34, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 88, z: 52, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 70, z: -76, yaw: 0, clipSize: { halfX: 0.32, halfZ: 0.32, height: 7.2 } },
    { propId: 'clip_wall', x: 86, z: 86.5, yaw: 0, clipSize: { halfX: 2, halfZ: 1.5, height: 3.89 } },
    { propId: 'clip_wall', x: 62, z: 86, yaw: 0, clipSize: { halfX: 1.75, halfZ: 1.75, height: 3.89 } },
    { propId: 'clip_wall', x: -16.5, z: -45.5, yaw: 0, clipSize: { halfX: 2.25, halfZ: 2.25, height: 2.27 } },
  ],
};

/**
 * A fortress to hold, and three ways onto its ground.
 *
 * Eight waves out of three gates at north, east and west; the south is the
 * players' back and stays quiet, so the fortress has a front. The gates are far
 * enough apart that a wave of more than three arrives from at least two
 * bearings, which is what stops the mission being one firing line.
 *
 * **The arena is only a little bigger than the fortress it exists for** —
 * 110 × 170 m around an 84 × 60 m position, cut down from the 200 m square it
 * was registered on. The mode's subject is the position, and on the square
 * most of the map was ground nobody had a reason to stand on; now the flanks
 * are a 14 m lane between the rampart and the arena wall, wide enough for a
 * wave to work round to a side door and nothing else, and the length is spent
 * where it pays — the north approach, so a wave is under the defenders' guns
 * for its whole drive in. The flank gates moved inside the new bound with the
 * walls (±48, not ±92); the mission's gate table and the map's size are one
 * statement and moved in one edit.
 */
const MAP_REDOUBT: MapDefinition = {
  id: 'redoubt',
  name: 'Last Stand',
  blurb: 'Hold the redoubt against eight waves. They come from three sides.',
  halfExtent: { x: 55, z: 85 },
  teamSize: 4,
  mission: {
    kind: 'wave-defence',
    gates: [
      { x: 0, z: 77, yaw: Math.PI },
      { x: 48, z: 30, yaw: -Math.PI / 2 },
      { x: -48, z: 30, yaw: Math.PI / 2 },
    ],
  },
  bases: {
      blue: {
        team: 'blue',
        x: 0,
        z: -40,
        facing: 0,
        spawns: [
          { x: -16, z: -42 },
          { x: 0, z: -42 },
          { x: 16, z: -42 },
          { x: -8, z: -35 },
          { x: 8, z: -35 },
          { x: -25, z: -48 },
          { x: 25, z: -48 },
        ],
        keepOut: 55,
      },
      red: {
        team: 'red',
        x: 0,
        z: 79,
        facing: Math.PI,
        spawns: [
          { x: 0, z: 79 },
          { x: -12, z: 79 },
          { x: 12, z: 79 },
          { x: -24, z: 79 },
          { x: 24, z: 79 },
          { x: -6, z: 74 },
          { x: 6, z: 74 },
        ],
        keepOut: 30,
      },
    },
  props: [
    // The blue base's own fort — the same bastion() shape as the PvP maps.
    // One movement-only mask per side (below), not one per barrier.
    { propId: 'hesco_barrier', x: -27.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -22.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -17.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -12.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -7.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -2.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 2.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 7.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 12.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 17.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 22.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 27.5, z: -53, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -35, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -40, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -45, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -50, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -30, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -35, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -40, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -45, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -50, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'dragon_teeth', x: -16.4, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: -11.6, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 11.6, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'dragon_teeth', x: 16.4, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: -7.6, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 7.6, z: -29, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'command_post', x: 0, z: -49, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -16.72, z: -31.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -13.28, z: -31.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 13.28, z: -31.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 16.72, z: -31.5, yaw: 0, blocksTanks: false, blocksShells: false },
    // The redoubt's own second line — five walls (a back wall, two side wings,
    // and a gated inner wall in two halves), each getting one movement-only
    // mask below rather than one per barrier.
    { propId: 'hesco_barrier', x: -35, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -30, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -25, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -20, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -15, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -10, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -5, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 0, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 5, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 10, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 15, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 20, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 25, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 30, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 35, z: -64, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -22.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -27.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -32.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -37.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -42.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -47.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -52.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -57.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -40, z: -62.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -22.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -27.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -32.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -37.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -42.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -47.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -52.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -57.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 40, z: -62.5, yaw: Math.PI / 2, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -39.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -34.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -29.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -24.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -19.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: -14.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 14.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 19.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 24.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 29.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 34.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'hesco_barrier', x: 39.5, z: -6, yaw: 0, blocksTanks: false, blocksShells: true },
    { propId: 'gate_pylon', x: -9.9, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'gate_pylon', x: 9.9, z: -6, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -6.88, z: -18, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: -3.44, z: -18, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 0, z: -18, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 3.44, z: -18, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'sandbag_wall', x: 6.88, z: -18, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: -17, z: -16, yaw: Math.PI / 4, blocksTanks: false, blocksShells: false },
    { propId: 'berm_earth', x: 17, z: -16, yaw: -Math.PI / 4, blocksTanks: false, blocksShells: false },
    // The fort's own three masks — see `wallOf`/`wallMask` on `bastion()`.
    { propId: 'clip_wall_move', x: 0, z: -53, yaw: 0, clipSize: { halfX: 30, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 30, z: -40, yaw: Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -30, z: -40, yaw: Math.PI / 2, clipSize: { halfX: 12.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -16.4, z: -29, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: -11.6, z: -29, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 11.6, z: -29, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall_move', x: 16.4, z: -29, yaw: 0, clipSize: { halfX: 2.16, halfZ: 0.59, height: 0.95 } },
    { propId: 'clip_wall', x: -7.6, z: -29, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 7.6, z: -29, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 0, z: -49, yaw: 0, clipSize: { halfX: 3.78, halfZ: 3.06, height: 5.04 } },
    { propId: 'clip_wall_move', x: -16.72, z: -31.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -13.28, z: -31.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 13.28, z: -31.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 16.72, z: -31.5, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    // The second line's five walls — one movement-only mask each, not one per
    // barrier: the back wall, the two side wings, and the gated inner wall's
    // two halves.
    { propId: 'clip_wall_move', x: 0, z: -64, yaw: 0, clipSize: { halfX: 37.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -40, z: -42.5, yaw: Math.PI / 2, clipSize: { halfX: 22.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 40, z: -42.5, yaw: Math.PI / 2, clipSize: { halfX: 22.5, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: -27, z: -6, yaw: 0, clipSize: { halfX: 15, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall_move', x: 27, z: -6, yaw: 0, clipSize: { halfX: 15, halfZ: 0.95, height: 2.03 } },
    { propId: 'clip_wall', x: -9.9, z: -6, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall', x: 9.9, z: -6, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.44, height: 4.73 } },
    { propId: 'clip_wall_move', x: -6.88, z: -18, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: -3.44, z: -18, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 0, z: -18, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 3.44, z: -18, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall_move', x: 6.88, z: -18, yaw: 0, clipSize: { halfX: 1.55, halfZ: 0.63, height: 1.08 } },
    { propId: 'clip_wall', x: -17, z: -16, yaw: Math.PI / 4, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'clip_wall', x: 17, z: -16, yaw: -Math.PI / 4, clipSize: { halfX: 2.7, halfZ: 1.85, height: 2.81 } },
    { propId: 'tree_oak_ancient', x: 38, z: 18.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'tree_oak_ancient', x: 10.5, z: 50, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'tree_oak_ancient', x: -23, z: 36, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: 10, z: 14, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: -3.5, z: 14.5, yaw: -0.524, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: -10, z: 14.5, yaw: 0, blocksTanks: false, blocksShells: false },
    { propId: 'hedgehog_antitank', x: 6.5, z: 10, yaw: 0.524, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_car', x: 25, z: 9.5, yaw: (-3 * Math.PI) / 4, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_car', x: -51.5, z: 7, yaw: 2.094, blocksTanks: false, blocksShells: false },
    { propId: 'military_truck', x: 22, z: 12.5, yaw: 2.88, blocksTanks: false, blocksShells: false },
    { propId: 'wreck_bus', x: -38.5, z: 12.5, yaw: -0.262, blocksTanks: false, blocksShells: false },
    { propId: 'clip_wall_move', x: 25, z: 9.5, yaw: Math.PI / 4, clipSize: { halfX: 1.5, halfZ: 0.7, height: 2 } },
    { propId: 'clip_wall_move', x: -51.5, z: 7, yaw: 2.138, clipSize: { halfX: 1.5, halfZ: 0.7, height: 2 } },
    { propId: 'crater_small', x: -9.5, z: 21, yaw: 0 },
    { propId: 'crater_small', x: 25.5, z: 35, yaw: 0 },
    { propId: 'crater_small', x: 27.5, z: 42.5, yaw: 0 },
    { propId: 'crater_small', x: -31.5, z: 49.5, yaw: 0 },
    { propId: 'crater_small', x: -40.5, z: 62, yaw: 0 },
      // Added in the editor after this map was baked, so `clipMasked` no
    // longer ran over them — masked by hand to match their neighbours.
    { propId: 'clip_wall', x: 38, z: 18.5, yaw: 0, clipSize: { halfX: 5.02, halfZ: 5.04, height: 13.5 } },
    { propId: 'clip_wall', x: 10.5, z: 50, yaw: 0, clipSize: { halfX: 5.02, halfZ: 5.04, height: 13.5 } },
    { propId: 'clip_wall', x: -23, z: 36, yaw: 0, clipSize: { halfX: 5.02, halfZ: 5.04, height: 13.5 } },
    { propId: 'clip_wall_move', x: 10, z: 14, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.31, height: 3.59 } },
    { propId: 'clip_wall_move', x: -3.5, z: 14.5, yaw: -0.524, clipSize: { halfX: 1.44, halfZ: 1.31, height: 3.59 } },
    { propId: 'clip_wall_move', x: -10, z: 14.5, yaw: 0, clipSize: { halfX: 1.44, halfZ: 1.31, height: 3.59 } },
    { propId: 'clip_wall_move', x: 6.5, z: 10, yaw: 0.524, clipSize: { halfX: 1.44, halfZ: 1.31, height: 3.59 } },
    { propId: 'clip_wall', x: 22, z: 12.5, yaw: 2.88, clipSize: { halfX: 1.09, halfZ: 2.49, height: 2.56 } },
    { propId: 'clip_wall', x: -38.5, z: 12.5, yaw: -0.262, clipSize: { halfX: 3.02, halfZ: 1.26, height: 2.35 } },
],
  decals: [
    { kind: 'tracks_light', x: -37.5, z: 12.7, yaw: 1.297, scaleX: 1.7, scaleZ: 4.2 },
    { kind: 'tracks_light', x: 21.58, z: 13.94, yaw: 2.898, scaleX: 1.5, scaleZ: 4.2, strength: 0.7 },
    { kind: 'tracks_light', x: 19.7, z: 21.31, yaw: 2.898, scaleX: 1.5, scaleZ: 4.2, strength: 0.3 },
    { kind: 'rect', x: 22, z: 12.5, yaw: 2.88, scaleX: 1.82, scaleZ: 4.16 },
    { kind: 'rect', x: 25, z: 9.5, yaw: (-3 * Math.PI) / 4, scaleX: 1.82, scaleZ: 1.05 },
    { kind: 'rect', x: -38.5, z: 12.5, yaw: -0.262, scaleX: 4.5, scaleZ: 2.1 },
    { kind: 'rect', x: -51.5, z: 7, yaw: 2.094, scaleX: 2, scaleZ: 1 },
    { kind: 'rect', x: 25.5, z: 35, yaw: 0, scaleX: 2.7, scaleZ: 2.7, strength: 0.5 },
    { kind: 'rect', x: 27.5, z: 42.5, yaw: 0, scaleX: 3, scaleZ: 3, strength: 0.4 },
  ],
};

/**
 * Which battlefield each mission is fought on.
 *
 * The server forces this from the mode and ignores any map a client asks for,
 * so there is no request that produces an escort's rules on a deathmatch map.
 * Derived on both sides from one table rather than paired by convention twice.
 */
export const MISSION_MAP_IDS: Readonly<Record<MissionKind, string>> = {
  // The tutorial's ground was already resolved by id and already unpickable, so
  // it joined this table by becoming a mission rather than by being moved.
  // `TUTORIAL_MAP_ID` is the same string and is kept as the name the gateway and
  // the tests read it by.
  tutorial: MAP_DRILL.id,
  escort: MAP_CONVOY.id,
  ctf: MAP_RELAY.id,
  'wave-defence': MAP_REDOUBT.id,
};

export const MAPS: Record<string, MapDefinition> = {
  range: MAP_RANGE,
  [MAP_RIDGE.id]: MAP_RIDGE,
  [MAP_FOUNDRY.id]: MAP_FOUNDRY,
  [MAP_FLATS.id]: MAP_FLATS,
  [MAP_DEPOT.id]: MAP_DEPOT,
  [MAP_CAULDRON.id]: MAP_CAULDRON,
  [MAP_BASIN.id]: MAP_BASIN,
  [MAP_TOWN.id]: MAP_TOWN,
  [MAP_EMPTY.id]: MAP_EMPTY,
  // Last, and not in `MAP_SUMMARIES` at all — they are in the registry because
  // both sides have to be able to resolve them from an id, not because anybody
  // picks them.
  [MAP_DRILL.id]: MAP_DRILL,
  [MAP_CONVOY.id]: MAP_CONVOY,
  [MAP_RELAY.id]: MAP_RELAY,
  [MAP_REDOUBT.id]: MAP_REDOUBT,
};

/**
 * The map a deploy lands on when nobody has chosen one.
 *
 * A team map rather than Ashfield, because bases are the point: a player who
 * presses Deploy without touching the picker should get the game this arena is
 * for. `QUICK_MATCH_ARENA_ID` is derived from it, so changing this line changes
 * which room the bare Deploy button opens — which is exactly what it should do.
 */
export const DEFAULT_MAP_ID = MAP_RIDGE.id;

/** Looks a map up by id, falling back to the empty one. */
export function mapById(id: string | null | undefined): MapDefinition {
  return (id && MAPS[id]) || MAP_EMPTY;
}

/**
 * The default map's bodies, built once.
 *
 * Kept for the handful of callers that legitimately have no arena to ask —
 * unit tests, the divergence probe — and **not** for anything in a match. An
 * arena resolves its own bodies from its own map at construction; a module
 * constant read inside a tick is how the client ends up predicting against a
 * different building than the server, which is silent, per-tick, and only
 * visible as a permanent reconciliation error near scenery.
 */
export const DEFAULT_MAP_BODIES: readonly PropBody[] = buildBodies(mapById(DEFAULT_MAP_ID));

/** What the map picker shows. Order is the order of `MAPS`. */
export interface MapSummary {
  id: string;
  name: string;
  blurb: string;
  /** Whether the map has fortified team bases, for the picker's badge. */
  teamBases: boolean;
  /**
   * Tanks per side, so the picker can say "15 v 15" rather than leaving the
   * player to find out on arrival.
   *
   * Resolved here rather than left optional: the summary is a display record,
   * and a client that had to know the default to render a badge would be a
   * second place the default lives.
   */
  teamSize: number;
  /**
   * The playable rectangle's half-extent, per axis, in metres. What "big map"
   * means — and a pair, because the day a map is 140 × 210 a summary carrying
   * one number would have to choose which half of that to lie about.
   */
  halfExtent: ArenaHalfExtent;
}

/**
 * Maps that are not offered in the picker.
 *
 * The exclusion is a display decision rather than a claim that a map is
 * unfinished: every one of these is a working battlefield that `mapById`
 * resolves and an arena can be built on.
 *
 * **Every one of them is a mission map now**, the drill yard included, and the
 * reason is the same for all four: a mission's map is implied by its mode and
 * forced server-side, so a picker offering the convoy road would be offering a
 * road with no convoy on it, and one offering the drill yard would be offering
 * a lesson with none of its drills. The mode chooses the map, not the other way
 * round. `TUTORIAL_MAP_ID` is still named separately here because it was
 * excluded for that display reason before it had the structural one, and losing
 * the name would lose the test that reads it.
 *
 * A list rather than a flag on `MapDefinition`, because a flag would be a
 * property of the *map* and this is a property of the *picker*.
 */
const UNPICKABLE_MAP_IDS: readonly string[] = [
  TUTORIAL_MAP_ID,
  ...Object.values(MISSION_MAP_IDS),
];

/** Maps a player may choose to fight on, in picker order. */
export const PICKABLE_MAPS: readonly MapDefinition[] = Object.values(MAPS).filter(
  (m) => !UNPICKABLE_MAP_IDS.includes(m.id),
);

export const MAP_SUMMARIES: readonly MapSummary[] = PICKABLE_MAPS.map((m) => ({
  id: m.id,
  name: m.name,
  blurb: m.blurb,
  teamBases: m.bases !== undefined,
  teamSize: teamSizeFor(m),
  halfExtent: m.halfExtent,
}));

/** The base a team starts from, or `undefined` on a map that has none. */
export function baseFor(map: MapDefinition, team: TeamId): MapBase | undefined {
  return map.bases?.[team];
}
